import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    Search,
    Users,
    UserPlus,
    ShieldCheck,
    Mail,
    Trash2,
    BadgeCheck,
    Clock3,
    X,
    Edit2,
    Phone,
    MapPin,
    FileText,
    Loader2,
    FileCheck2,
    AlertCircle,
    CalendarDays,
    History,
    LayoutDashboard,
    UserRound
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";
import SalarySlipModal from "../../components/admin/SalarySlipModal";
import { useMeta } from "../../context/MetaContext";
import {
    MONTH_NAMES,
    WEEKDAYS_SHORT,
    toDateKey,
    computeMonthAttendanceStats,
    computeAutoLeaveCompensation,
    countLeavesInMonth,
    buildPresentDateSet,
    normalizeLeaveType,
    getCalendarDayPresentation,
    CALENDAR_COLORS,
    CALENDAR_LEGEND_ITEMS,
    ATTENDANCE_SUMMARY_STYLES,
} from "../../utils/calendarAttendance";
import {
    createIndiaDateTimeIso,
    getIndiaTimeInputValue,
} from "../../utils/attendanceTime";

const TIME_12_HOURS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));
const TIME_MINUTES = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"));

const getAttendanceSaveErrorMessage = (error, action = "save attendance") => {
    if (!error?.response || error.code === "ERR_NETWORK" || error.message === "Network Error") {
        return "Network connection problem. Please check your internet and try again.";
    }

    const status = error.response.status;
    const serverMessage = String(error.response?.data?.message || "").trim();
    const looksTechnical = /\b(referenceerror|typeerror|syntaxerror|error:|undefined|not defined|stack|\/controllers\/|\/utils\/)\b/i.test(serverMessage);
    const isSafeValidationMessage = [400, 409, 422].includes(status)
        && serverMessage
        && serverMessage.length <= 180
        && !looksTechnical;

    if (isSafeValidationMessage) return serverMessage;
    if (status === 401) return "Your session has expired. Please sign in again.";
    if (status === 403) return "You do not have permission to update attendance.";
    if (status === 408 || status === 504) return "The request took too long. Please try again.";
    if (status === 429) return "Too many requests. Please wait a moment and try again.";
    if (status >= 500) return `We could not ${action} right now. Please try again later.`;
    return `Unable to ${action}. Please check the details and try again.`;
};

const toTwelveHourParts = (value = "") => {
    if (!value) return { hour: "", minute: "", period: "AM" };
    const [hourValue = "", minuteValue = ""] = String(value || "").split(":");
    const hour24 = Number(hourValue);
    const minute = /^\d{2}$/.test(minuteValue) ? minuteValue : "00";
    const period = hour24 >= 12 ? "PM" : "AM";
    const hour12 = hour24 % 12 || 12;

    return {
        hour: Number.isNaN(hour24) ? "" : String(hour12).padStart(2, "0"),
        minute,
        period,
    };
};

const fromTwelveHourParts = ({ hour, minute, period }) => {
    const hourNumber = Number(hour);
    if (!hour || Number.isNaN(hourNumber)) return "";
    const hour24 = period === "PM" ? (hourNumber % 12) + 12 : hourNumber % 12;
    return `${String(hour24).padStart(2, "0")}:${minute || "00"}`;
};

const AdminTwelveHourTimeInput = ({ value, onChange }) => {
    const parts = toTwelveHourParts(value);
    const updatePart = (key, nextValue) => {
        onChange(fromTwelveHourParts({ ...parts, [key]: nextValue }));
    };

    const selectClass = "h-11 min-w-0 border-0 bg-transparent px-2 text-sm font-bold text-neutral-800 outline-none dark:text-neutral-100";

    return (
        <div className="grid w-max max-w-full grid-cols-[50px_16px_50px_64px] items-center overflow-hidden rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] focus-within:border-[var(--accent-primary)]">
            <select value={parts.hour} onChange={(event) => updatePart("hour", event.target.value)} className={selectClass} aria-label="Hour">
                <option value="">HH</option>
                {TIME_12_HOURS.map((hour) => <option key={hour} value={hour}>{hour}</option>)}
            </select>
            <span className="grid h-11 place-items-center border-l border-neutral-200 text-sm font-black text-neutral-500 dark:border-neutral-800">:</span>
            <select value={parts.minute} onChange={(event) => updatePart("minute", event.target.value)} className={selectClass} aria-label="Minute">
                <option value="">MM</option>
                {TIME_MINUTES.map((minute) => <option key={minute} value={minute}>{minute}</option>)}
            </select>
            <select value={parts.period} onChange={(event) => updatePart("period", event.target.value)} className={`${selectClass} border-l border-neutral-200 dark:border-neutral-800`} aria-label="AM or PM">
                <option value="AM">AM</option>
                <option value="PM">PM</option>
            </select>
        </div>
    );
};

const EmployeeLeaveCalendar = ({ employee, leaves = [], attendance = [], holidays = [], onAttendanceSaved }) => {
    const today = new Date();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [attendanceModal, setAttendanceModal] = useState({
        open: false,
        saving: false,
        record: null,
        error: "",
        form: {
            date: "",
            status: "present",
            compensationType: "casual",
            mode: "office",
            checkIn: "09:30",
            breakStart: "",
            breakEnd: "",
            checkOut: "",
        },
    });

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayIndex = new Date(year, month, 1).getDay();
    const prevMonthDays = new Date(year, month, 0).getDate();
    
    const { presentCount, notInCount, weekOffCount, holidayCount, dayMeta } =
        computeMonthAttendanceStats(year, month, {
            leaves,
            attendance,
            holidays,
            today,
            attendanceStartDate: employee?.attendanceStartDate || employee?.joiningDate,
        });
    const autoCompensation = computeAutoLeaveCompensation({ employee, dayMeta });

    const {
        casualCount,
        sickCount,
        lopCount,
        compensatoryCount,
        totalLeaveDays,
    } = countLeavesInMonth(leaves, year, month);
    const earnedCompLeaves = (attendance || []).filter((record) => {
        if (!record?.date || !record?.checkIn) return false;
        const [dateYear, dateMonth, dateDay] = record.date.split("-").map(Number);
        const dateObj = new Date(dateYear, dateMonth - 1, dateDay);
        return dateObj.getFullYear() === year && dateObj.getMonth() === month && dateObj.getDay() === 0;
    }).length;
    const availableCompBalance = Number(employee?.compensatoryLeave || 0);

    const handlePrevMonth = () => {
        setCurrentDate(new Date(year, month - 1, 1));
    };
    
    const handleNextMonth = () => {
        setCurrentDate(new Date(year, month + 1, 1));
    };

    const days = [];
    
    // Previous month fill days
    for (let i = firstDayIndex - 1; i >= 0; i--) {
        days.push({
            day: prevMonthDays - i,
            isCurrentMonth: false,
            key: `prev-${i}`
        });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
        const isToday = i === today.getDate() && month === today.getMonth() && year === today.getFullYear();
        const meta = dayMeta[i] || {};

        days.push({
            day: i,
            isCurrentMonth: true,
            isToday,
            isSunday: meta.isSunday,
            isWeekOff: meta.isWeekOff,
            isHoliday: meta.isHoliday,
            holidayName: meta.holidayName,
            leave: meta.leave,
            isPresent: meta.isPresent,
            isNotIn: meta.isNotIn,
            isCompensated: meta.isCompensated,
            autoLeaveType: meta.isCompensated
                ? (meta.compensationType === "medical" ? "sick" : meta.compensationType)
                : autoCompensation.allocationByDate?.[meta.dateKey],
            key: `curr-${i}`
        });
    }
    
    // Next month fill days
    const totalSlots = 42;
    const remainingSlots = totalSlots - days.length;
    for (let i = 1; i <= remainingSlots; i++) {
        days.push({
            day: i,
            isCurrentMonth: false,
            key: `next-${i}`
        });
    }

    const autoCalculationStyles = {
        carryForwardCasual: {
            label: "Carry Forward (CL)",
            wrap: "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-100 dark:border-emerald-900/30",
            badge: "bg-emerald-600",
        },
        carryForwardMedical: {
            label: "Carry Forward (ML)",
            wrap: "bg-teal-50 dark:bg-teal-950/20 border-teal-100 dark:border-teal-900/30",
            badge: "bg-teal-600",
        },
        casual: {
            label: "Casual Leave",
            wrap: "bg-[var(--accent-light)] dark:bg-[var(--accent-subtle)] border-[var(--border-color)] dark:border-[var(--border-color)]/30",
            badge: "bg-[var(--accent-primary)]",
        },
        sick: {
            label: "Medical Leave",
            wrap: "bg-indigo-50 dark:bg-indigo-950/20 border-indigo-100 dark:border-indigo-900/30",
            badge: "bg-[var(--accent-primary)]",
        },
        compensatory: {
            label: "Comp Off",
            wrap: "bg-[var(--accent-light)] dark:bg-[var(--accent-subtle)] border-[var(--border-color)] dark:border-[var(--border-color)]",
            badge: "bg-[var(--accent-primary)]",
        },
        lop: {
            label: "LOP",
            wrap: "bg-red-50 dark:bg-red-950/20 border-red-100 dark:border-red-900/30",
            badge: "bg-red-600",
        },
    };
    const autoCalculationDetails = Object.entries(autoCompensation.allocationByDate || {})
        .sort(([firstDate], [secondDate]) => firstDate.localeCompare(secondDate))
        .map(([dateKey, allocation]) => ({
            dateKey,
            ...(autoCalculationStyles[allocation] || autoCalculationStyles.lop),
        }));
    const formatAutoCalculationDate = (dateKey) => {
        const [dateYear, dateMonth, dateDay] = String(dateKey || "").split("-").map(Number);
        if (![dateYear, dateMonth, dateDay].every(Number.isFinite)) return dateKey;
        return new Date(dateYear, dateMonth - 1, dateDay).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };
    const findAttendanceForDate = (dateKey) =>
        (attendance || []).find((record) => record?.date === dateKey);

    const formatTimeForInput = (value) => {
        return getIndiaTimeInputValue(value);
    };

    const combineDateAndTime = (dateKey, timeValue) => {
        if (!dateKey || !timeValue) return "";
        const [dateYear, dateMonth, dateDay] = dateKey.split("-").map(Number);
        const [hours, minutes] = timeValue.split(":").map(Number);
        if (![dateYear, dateMonth, dateDay, hours, minutes].every(Number.isFinite)) return "";
        return createIndiaDateTimeIso(dateKey, hours, minutes, 0, 0) || "";
    };

    const openAttendanceEditor = (day) => {
        if (!day.isCurrentMonth || !employee?._id) return;
        const dateKey = toDateKey(year, month, day.day);
        const record = findAttendanceForDate(dateKey);
        setAttendanceModal({
            open: true,
            saving: false,
            record: record || null,
            error: "",
            form: {
                date: dateKey,
                status: record?.status || (record?.checkIn ? "present" : "absent"),
                compensationType: record?.compensationType || "casual",
                mode: record?.mode || "office",
                checkIn: formatTimeForInput(record?.checkIn) || "09:30",
                breakStart: formatTimeForInput(record?.breakStart),
                breakEnd: formatTimeForInput(record?.breakEnd),
                checkOut: formatTimeForInput(record?.checkOut),
            },
        });
    };

    const closeAttendanceEditor = () => {
        setAttendanceModal((current) => ({ ...current, open: false, error: "" }));
    };

    const updateAttendanceForm = (field, value) => {
        const firstAvailableCompensation = Number(employee?.casualLeave || 0) >= 1
            ? "casual"
            : Number(employee?.medicalLeave || 0) >= 1
                ? "medical"
                : "compensatory";
        setAttendanceModal((current) => ({
            ...current,
            form: field === "status" && (value === "absent" || value === "compensated")
                ? {
                    ...current.form,
                    status: value,
                    compensationType: value === "compensated" ? firstAvailableCompensation : current.form.compensationType,
                    checkIn: "",
                    breakStart: "",
                    breakEnd: "",
                    checkOut: "",
                }
                : { ...current.form, [field]: value },
            error: "",
        }));
    };

    const saveAttendanceFromCalendar = async (event) => {
        event.preventDefault();
        if (!employee?._id) return;

        setAttendanceModal((current) => ({ ...current, saving: true, error: "" }));
        const { record, form } = attendanceModal;
        const isAbsent = form.status === "absent";
        if (form.status === "compensated") {
            try {
                const res = await api.post("/api/admin/attendance/compensate", {
                    employeeId: employee._id,
                    date: form.date,
                    compensationType: form.compensationType,
                });
                if (res.data?.attendance && onAttendanceSaved) {
                    onAttendanceSaved(res.data.attendance, res.data.employee);
                }
                closeAttendanceEditor();
            } catch (err) {
                setAttendanceModal((current) => ({
                    ...current,
                    saving: false,
                    error: getAttendanceSaveErrorMessage(err, "compensate attendance"),
                }));
            }
            return;
        }
        const payload = {
            date: form.date,
            status: form.status,
            mode: form.mode,
            checkIn: isAbsent ? "" : combineDateAndTime(form.date, form.checkIn),
            breakStart: isAbsent ? "" : combineDateAndTime(form.date, form.breakStart),
            breakEnd: isAbsent ? "" : combineDateAndTime(form.date, form.breakEnd),
            checkOut: isAbsent ? "" : combineDateAndTime(form.date, form.checkOut),
        };

        try {
            let savedRecord = null;
            if (record?._id) {
                const res = await api.patch(`/api/admin/attendance/${record._id}`, payload);
                savedRecord = res.data;
            } else {
                const res = await api.post("/api/admin/attendance/manual", {
                    ...payload,
                    employeeIds: [employee._id],
                });
                savedRecord = Array.isArray(res.data?.records) ? res.data.records[0] : null;
            }

            if (savedRecord && onAttendanceSaved) {
                onAttendanceSaved(savedRecord);
            }
            closeAttendanceEditor();
        } catch (err) {
            setAttendanceModal((current) => ({
                ...current,
                saving: false,
                error: getAttendanceSaveErrorMessage(err),
            }));
        }
    };

    return (
        <div className="w-full bg-neutral-50/20 dark:bg-neutral-900/20 p-5 rounded-2xl border border-neutral-100 dark:border-neutral-800 space-y-5">
            {/* Header controls */}
            <div className="flex items-center justify-between">
                <span className="font-bold text-base text-neutral-800 dark:text-neutral-100">
                    {MONTH_NAMES[month]} {year}
                </span>
                <div className="flex gap-1.5">
                    <button onClick={handlePrevMonth} className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all text-xs font-bold border border-neutral-200 dark:border-neutral-700 bg-[var(--card-bg)] dark:bg-neutral-900 cursor-pointer">&lt;</button>
                    <button onClick={handleNextMonth} className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all text-xs font-bold border border-neutral-200 dark:border-neutral-700 bg-[var(--card-bg)] dark:bg-neutral-900 cursor-pointer">&gt;</button>
                </div>
            </div>

            {/* Weekdays */}
            <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold text-neutral-400 dark:text-neutral-500">
                {WEEKDAYS_SHORT.map(d => (
                    <div key={d}>{d}</div>
                ))}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7 gap-1 text-center text-xs">
                {days.map(item => {
                    const { cellStyle, titleText } = getCalendarDayPresentation(item);
                    const todayAttendance = item.isToday && !item.isWeekOff && !item.isHoliday
                        ? (item.isCompensated ? "compensated" : item.isPresent ? "present" : "absent")
                        : "";

                    return (
                        <button
                            type="button"
                            key={item.key} 
                            onClick={() => openAttendanceEditor(item)}
                            className={`relative py-2 rounded-lg font-semibold flex items-center justify-center transition-all ${cellStyle} ${item.isCurrentMonth ? "cursor-pointer hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]/40" : "cursor-default"}`}
                            title={todayAttendance ? `${titleText} • ${todayAttendance === "present" ? "Present" : todayAttendance === "compensated" ? "Compensated" : "Absent (no check-in)"}` : titleText}
                        >
                            {item.day}
                            {todayAttendance && (
                                <span
                                    className={`absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full text-[8px] font-black text-white shadow-sm ${
                                        todayAttendance === "present" ? "bg-emerald-600" : todayAttendance === "compensated" ? "bg-[var(--accent-primary)]" : "bg-red-500"
                                    }`}
                                    aria-label={todayAttendance === "present" ? "Present today" : todayAttendance === "compensated" ? "Compensated today" : "Absent today"}
                                >
                                    {todayAttendance === "present" ? "P" : todayAttendance === "compensated" ? "C" : "A"}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* LEGEND SECTION */}
            <div className="border-t border-neutral-100 dark:border-neutral-800 pt-4">
                <h4 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase mb-2">Legend</h4>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px]">
                    {CALENDAR_LEGEND_ITEMS.map(({ key, label }) => (
                        <div key={key} className="flex items-center gap-1.5">
                            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${CALENDAR_COLORS[key].dot}`} />
                            <span className="text-neutral-600 dark:text-neutral-400 font-medium">{label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* ATTENDANCE SUMMARY */}
            <div className="border-t border-neutral-100 dark:border-neutral-800 pt-4">
                <h4 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase mb-2">
                    Attendance Summary ({MONTH_NAMES[month]} {year})
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                    <div className={`flex items-center justify-between p-2 rounded-xl border ${ATTENDANCE_SUMMARY_STYLES.present.wrap}`}>
                        <span className={`font-semibold ${ATTENDANCE_SUMMARY_STYLES.present.text}`}>Present</span>
                        <span className={`px-2 py-0.5 rounded-md text-white font-bold ${ATTENDANCE_SUMMARY_STYLES.present.badge}`}>{presentCount}d</span>
                    </div>
                    <div className={`flex items-center justify-between p-2 rounded-xl border ${ATTENDANCE_SUMMARY_STYLES.notIn.wrap}`}>
                        <span className={`font-semibold ${ATTENDANCE_SUMMARY_STYLES.notIn.text}`}>Absent</span>
                        <span className={`px-2 py-0.5 rounded-md text-white font-bold ${ATTENDANCE_SUMMARY_STYLES.notIn.badge}`}>{notInCount}d</span>
                    </div>
                    <div className={`flex items-center justify-between p-2 rounded-xl border ${ATTENDANCE_SUMMARY_STYLES.weekOff.wrap}`}>
                        <span className={`font-semibold ${ATTENDANCE_SUMMARY_STYLES.weekOff.text}`}>Week Off</span>
                        <span className={`px-2 py-0.5 rounded-md text-white font-bold ${ATTENDANCE_SUMMARY_STYLES.weekOff.badge}`}>{weekOffCount}d</span>
                    </div>
                    <div className={`flex items-center justify-between p-2 rounded-xl border ${ATTENDANCE_SUMMARY_STYLES.holiday.wrap}`}>
                        <span className={`font-semibold ${ATTENDANCE_SUMMARY_STYLES.holiday.text}`}>Holidays</span>
                        <span className={`px-2 py-0.5 rounded-md text-white font-bold ${ATTENDANCE_SUMMARY_STYLES.holiday.badge}`}>{holidayCount}d</span>
                    </div>
                </div>
            </div>

            {/* MONTHLY LEAVE TRACKER COUNTER */}
            <div className="border-t border-neutral-100 dark:border-neutral-800 pt-4">
                <h4 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase mb-2">Leave & Absence Summary (This Month)</h4>
                {totalLeaveDays === 0 && notInCount === 0 && earnedCompLeaves === 0 ? (
                    <p className="text-xs italic text-neutral-400 dark:text-neutral-500">No leaves or absences this month.</p>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
                        {autoCompensation.counts.carryForward > 0 && (
                            <div className="flex items-center justify-between p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30">
                                <span className="font-semibold text-emerald-700 dark:text-emerald-400">Carry Forward</span>
                                <span className="px-2 py-0.5 rounded-md bg-emerald-600 text-white font-bold">{autoCompensation.counts.carryForward}d</span>
                            </div>
                        )}
                        {autoCompensation.counts.casual > 0 && (
                            <div className="flex items-center justify-between p-2 rounded-xl bg-[var(--accent-light)] dark:bg-[var(--accent-subtle)] border border-[var(--border-color)] dark:border-[var(--border-color)]/30">
                                <span className="font-semibold text-[var(--heading-secondary)] dark:text-[var(--accent-primary)]">Auto Casual</span>
                                <span className="px-2 py-0.5 rounded-md bg-[var(--accent-primary)] text-white font-bold">{autoCompensation.counts.casual}d</span>
                            </div>
                        )}
                        {autoCompensation.counts.medical > 0 && (
                            <div className="flex items-center justify-between p-2 rounded-xl bg-[var(--accent-light)] dark:bg-[var(--accent-subtle)] border border-[var(--border-color)] dark:border-[var(--border-color)]">
                                <span className="font-semibold text-indigo-700 dark:text-indigo-400">Auto Medical</span>
                                <span className="px-2 py-0.5 rounded-md bg-[var(--accent-primary)] text-white font-bold">{autoCompensation.counts.medical}d</span>
                            </div>
                        )}
                        {autoCompensation.counts.compensatory > 0 && (
                            <div className="flex items-center justify-between p-2 rounded-xl bg-[var(--accent-light)] dark:bg-[var(--accent-subtle)] border border-[var(--border-color)] dark:border-[var(--border-color)]">
                                <span className="font-semibold text-purple-700 dark:text-[var(--accent-primary)]">Auto Comp Off</span>
                                <span className="px-2 py-0.5 rounded-md bg-[var(--accent-primary)] text-white font-bold">{autoCompensation.counts.compensatory}d</span>
                            </div>
                        )}
                        {earnedCompLeaves > 0 && (
                            <div className="flex items-center justify-between p-2 rounded-xl bg-[var(--accent-light)] dark:bg-[var(--accent-subtle)] border border-[var(--border-color)] dark:border-[var(--border-color)]">
                                <span className="font-semibold text-purple-700 dark:text-[var(--accent-primary)]">Comp Off Balance</span>
                                <span className="px-2 py-0.5 rounded-md bg-[var(--accent-primary)] text-white font-bold">{availableCompBalance}d</span>
                            </div>
                        )}
                        {autoCompensation.payableAbsentCount > 0 && (
                            <div className="flex items-center justify-between p-2 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30">
                                <span className="font-semibold text-red-700 dark:text-red-400">LOP</span>
                                <span className="px-2 py-0.5 rounded-md bg-red-600 text-white font-bold">{autoCompensation.payableAbsentCount}d</span>
                            </div>
                        )}
                        {casualCount > 0 && (
                            <div className="flex items-center justify-between p-2 rounded-xl bg-[var(--accent-light)] dark:bg-[var(--accent-subtle)] border border-[var(--border-color)] dark:border-[var(--border-color)]/30">
                                <span className="font-semibold text-[var(--heading-secondary)] dark:text-[var(--accent-primary)]">Casual Leave</span>
                                <span className="px-2 py-0.5 rounded-md bg-[var(--accent-primary)] text-white font-bold">{casualCount}d</span>
                            </div>
                        )}
                        {sickCount > 0 && (
                            <div className="flex items-center justify-between p-2 rounded-xl bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900 border border-neutral-800 dark:border-neutral-200">
                                <span className="font-semibold">Medical Leave</span>
                                <span className="px-2 py-0.5 rounded-md bg-[var(--card-bg)] text-black dark:bg-black dark:text-white font-bold">{sickCount}d</span>
                            </div>
                        )}
                        {lopCount > 0 && (
                            <div className="flex items-center justify-between p-2 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30">
                                <span className="font-semibold text-red-700 dark:text-red-400">Loss of Pay</span>
                                <span className="px-2 py-0.5 rounded-md bg-red-600 text-white font-bold">{lopCount}d</span>
                            </div>
                        )}
                        {compensatoryCount > 0 && (
                            <div className="flex items-center justify-between p-2 rounded-xl bg-[var(--accent-light)] dark:bg-[var(--accent-subtle)] border border-[var(--border-color)] dark:border-[var(--border-color)]">
                                <span className="font-semibold text-purple-700 dark:text-[var(--accent-primary)]">Comp Off</span>
                                <span className="px-2 py-0.5 rounded-md bg-[var(--accent-primary)] text-white font-bold">{compensatoryCount}d</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {autoCalculationDetails.length > 0 && (
                <div className="border-t border-neutral-100 dark:border-neutral-800 pt-4">
                    <div className="mb-2">
                        <h4 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase">
                            Auto Calculation Details
                        </h4>
                        <p className="mt-1 text-[10px] text-neutral-400 dark:text-neutral-500">
                            Order: Carry Forward, Casual, Medical, Comp Off, then LOP.
                        </p>
                    </div>
                    <div className="space-y-2 text-[11px]">
                        {autoCalculationDetails.map(({ dateKey, label, wrap, badge }) => (
                            <div
                                key={dateKey}
                                className={`flex items-center justify-between gap-3 rounded-xl border p-2 ${wrap}`}
                            >
                                <div className="min-w-0">
                                    <p className="font-bold text-neutral-800 dark:text-neutral-100">
                                        {formatAutoCalculationDate(dateKey)}
                                    </p>
                                    <p className="text-[10px] font-semibold text-neutral-400 dark:text-neutral-500">
                                        No check-in recorded
                                    </p>
                                </div>
                                <span className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold text-white ${badge}`}>
                                    {label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {attendanceModal.open && (
                <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
                    <button
                        type="button"
                        aria-label="Close attendance editor"
                        onClick={closeAttendanceEditor}
                        className="absolute inset-0 bg-black/55 backdrop-blur-sm"
                    />
                    <form
                        onSubmit={saveAttendanceFromCalendar}
                        className="relative z-10 w-full max-w-lg rounded-2xl border border-neutral-200 bg-[var(--card-bg)] p-6 shadow-2xl dark:border-neutral-800 dark:bg-neutral-950"
                    >
                        <button
                            type="button"
                            onClick={closeAttendanceEditor}
                            className="absolute right-4 top-4 rounded-full p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-900 dark:hover:text-neutral-200"
                            title="Close"
                        >
                            <X size={18} />
                        </button>

                        <div className="mb-5 pr-10">
                            <p className="text-xs font-black uppercase tracking-wider text-[var(--accent-primary)]">Admin attendance edit</p>
                            <h3 className="mt-1 text-xl font-black text-neutral-900 dark:text-neutral-50">
                                {attendanceModal.record?._id ? "Edit attendance" : "Add attendance"}
                            </h3>
                            <p className="mt-1 text-sm font-medium text-neutral-500">
                                {employee?.name} - {attendanceModal.form.date}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <label className="flex flex-col gap-1.5">
                                <span className="text-xs font-bold uppercase text-neutral-500">Date</span>
                                <input
                                    type="date"
                                    value={attendanceModal.form.date}
                                    onChange={(event) => updateAttendanceForm("date", event.target.value)}
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-semibold outline-none focus:border-[var(--accent-primary)]"
                                    required
                                />
                            </label>
                            <label className="flex flex-col gap-1.5">
                                <span className="text-xs font-bold uppercase text-neutral-500">Status</span>
                                <select
                                    value={attendanceModal.form.status}
                                    onChange={(event) => updateAttendanceForm("status", event.target.value)}
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-semibold outline-none focus:border-[var(--accent-primary)]"
                                >
                                    <option value="present">Present</option>
                                    <option value="absent">Absent</option>
                                    <option value="compensated">Compensate</option>
                                </select>
                            </label>
                            {attendanceModal.form.status === "compensated" && (
                                <div className="sm:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/30 dark:bg-emerald-500/10">
                                    <p className="text-xs font-black uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Available paid leave</p>
                                    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                                        <div className="rounded-lg bg-[var(--card-bg)] p-2 dark:bg-neutral-900"><span className="block text-[10px] font-bold text-neutral-400">Casual</span><strong>{formatLeaveValue(employee?.casualLeave)}</strong></div>
                                        <div className="rounded-lg bg-[var(--card-bg)] p-2 dark:bg-neutral-900"><span className="block text-[10px] font-bold text-neutral-400">Medical</span><strong>{formatLeaveValue(employee?.medicalLeave)}</strong></div>
                                        <div className="rounded-lg bg-[var(--card-bg)] p-2 dark:bg-neutral-900"><span className="block text-[10px] font-bold text-neutral-400">Comp Off</span><strong>{formatLeaveValue(employee?.compensatoryLeave)}</strong></div>
                                    </div>
                                    <label className="mt-3 flex flex-col gap-1.5">
                                        <span className="text-xs font-bold text-neutral-600 dark:text-neutral-300">Use balance from</span>
                                        <select
                                            value={attendanceModal.form.compensationType}
                                            onChange={(event) => updateAttendanceForm("compensationType", event.target.value)}
                                            className="h-11 rounded-lg border border-emerald-200 bg-[var(--card-bg)] px-3 text-sm font-semibold outline-none dark:border-emerald-500/30 dark:bg-neutral-900"
                                        >
                                            <option value="casual" disabled={Number(employee?.casualLeave || 0) < 1}>Casual Leave ({formatLeaveValue(employee?.casualLeave)} available)</option>
                                            <option value="medical" disabled={Number(employee?.medicalLeave || 0) < 1}>Medical Leave ({formatLeaveValue(employee?.medicalLeave)} available)</option>
                                            <option value="compensatory" disabled={Number(employee?.compensatoryLeave || 0) < 1}>Comp Off ({formatLeaveValue(employee?.compensatoryLeave)} available)</option>
                                        </select>
                                    </label>
                                    <p className="mt-2 text-[11px] font-semibold text-emerald-700/80 dark:text-emerald-300/80">Confirming will deduct 1 paid-leave day and mark this date as compensated.</p>
                                </div>
                            )}
                            {attendanceModal.form.status !== "compensated" && (
                            <>
                            <label className="flex flex-col gap-1.5">
                                <span className="text-xs font-bold uppercase text-neutral-500">Mode</span>
                                <select
                                    value={attendanceModal.form.mode}
                                    onChange={(event) => updateAttendanceForm("mode", event.target.value)}
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-semibold outline-none focus:border-[var(--accent-primary)]"
                                >
                                    <option value="office">Office</option>
                                    <option value="wfh">WFH</option>
                                </select>
                            </label>
                            <label className="flex flex-col gap-1.5">
                                <span className="text-xs font-bold uppercase text-neutral-500">Check In</span>
                                <AdminTwelveHourTimeInput
                                    value={attendanceModal.form.checkIn}
                                    onChange={(value) => updateAttendanceForm("checkIn", value)}
                                />
                            </label>
                            </>
                            )}
                            <label className={`flex flex-col gap-1.5 ${attendanceModal.form.status === "compensated" ? "hidden" : ""}`}>
                                <span className="text-xs font-bold uppercase text-neutral-500">Lunch Start</span>
                                <AdminTwelveHourTimeInput
                                    value={attendanceModal.form.breakStart}
                                    onChange={(value) => updateAttendanceForm("breakStart", value)}
                                />
                            </label>
                            <label className={`flex flex-col gap-1.5 ${attendanceModal.form.status === "compensated" ? "hidden" : ""}`}>
                                <span className="text-xs font-bold uppercase text-neutral-500">Lunch End</span>
                                <AdminTwelveHourTimeInput
                                    value={attendanceModal.form.breakEnd}
                                    onChange={(value) => updateAttendanceForm("breakEnd", value)}
                                />
                            </label>
                            <label className={`flex flex-col gap-1.5 ${attendanceModal.form.status === "compensated" ? "hidden" : ""}`}>
                                <span className="text-xs font-bold uppercase text-neutral-500">Check Out</span>
                                <AdminTwelveHourTimeInput
                                    value={attendanceModal.form.checkOut}
                                    onChange={(value) => updateAttendanceForm("checkOut", value)}
                                />
                            </label>
                        </div>

                        {attendanceModal.error && (
                            <p className="mt-4 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300">
                                {attendanceModal.error}
                            </p>
                        )}

                        <div className="mt-6 flex justify-end gap-3 border-t border-neutral-100 pt-4 dark:border-neutral-800">
                            <button
                                type="button"
                                onClick={closeAttendanceEditor}
                                className="rounded-lg bg-neutral-100 px-5 py-2.5 text-sm font-bold text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-900 dark:text-neutral-300"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={attendanceModal.saving || (
                                    attendanceModal.form.status === "compensated"
                                    && Number(employee?.casualLeave || 0) < 1
                                    && Number(employee?.medicalLeave || 0) < 1
                                    && Number(employee?.compensatoryLeave || 0) < 1
                                )}
                                className="inline-flex items-center gap-2 rounded-lg bg-[var(--accent-primary)] px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-[var(--accent-primary)]/20 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {attendanceModal.saving && <Loader2 size={16} className="animate-spin" />}
                                {attendanceModal.form.status === "compensated" ? "Confirm Compensation" : "Save Attendance"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

const stripSalutation = (name) => {
    if (!name) return "";
    return name.replace(/^(mr|ms|mrs|miss)\.?\s+/i, "");
};

const formatRoleLabel = (role) => {
    const value = String(role || "").trim();
    if (!value) return "";
    return value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const formatLeaveValue = (value) => {
    const number = Number(value || 0);
    return Number.isInteger(number) ? String(number) : number.toFixed(1).replace(/\.0$/, "");
};

const effectiveAdminCarryForward = (employee, carryForwardKey) => (
    Math.max(0, Number(employee?.[carryForwardKey] || 0))
);

const getMonthlyLeaveAllocation = (employee, monthlyKey, balanceKey) => {
    const monthlyValue = employee?.[monthlyKey];
    if (monthlyValue !== undefined && monthlyValue !== null && (employee?.leavePolicyManualOverride || Number(monthlyValue) > 0)) {
        return Math.max(0, Number(monthlyValue || 0));
    }
    return Math.max(0, Number(employee?.[balanceKey] || 0));
};

const LeaveDashboardCard = ({ label, value, subLabel, tone = "text-neutral-800 dark:text-neutral-100" }) => (
    <div className="p-4 rounded-xl border border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/30 text-center">
        <h4 className="text-xs text-neutral-400 dark:text-neutral-500 font-semibold mb-1">{label}</h4>
        <p className={`text-base font-bold ${tone}`}>{value}</p>
        {subLabel && <p className="mt-1 text-[10px] font-semibold text-neutral-400 dark:text-neutral-500">{subLabel}</p>}
    </div>
);

const getDateOnly = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
};

const getLeaveDatesInRange = (leave, rangeStart, rangeEnd) => {
    const start = getDateOnly(leave?.startDate);
    const end = getDateOnly(leave?.endDate);
    if (!start || !end || end < rangeStart || start > rangeEnd) return [];

    const cursor = new Date(Math.max(start.getTime(), rangeStart.getTime()));
    const last = new Date(Math.min(end.getTime(), rangeEnd.getTime()));
    const dates = [];
    while (cursor <= last) {
        dates.push({
            date: new Date(cursor),
            key: toDateKey(cursor.getFullYear(), cursor.getMonth(), cursor.getDate()),
        });
        cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
};

const repeatLeaveUnit = (unit, count) => (
    Array.from({ length: Math.max(0, Math.round(Number(count || 0))) }, () => unit)
);

const getFallbackLeaveUnit = (leaveType) => {
    const normalizedType = normalizeLeaveType(leaveType);
    if (normalizedType === "casual") return "casual";
    if (normalizedType === "sick" || normalizedType === "emergency") return "medical";
    if (normalizedType === "compensatory") return "compensatory";
    if (normalizedType === "lop") return "lop";
    return null;
};

const getLeaveAllocationUnits = (leave) => {
    const allocation = leave.leaveAllocation || {};
    const units = [
        ...repeatLeaveUnit("carryForwardCasual", allocation.carriedForwardCasualLeave),
        ...repeatLeaveUnit("carryForwardMedical", allocation.carriedForwardMedicalLeave),
        ...repeatLeaveUnit("casual", allocation.casualLeave),
        ...repeatLeaveUnit("medical", allocation.medicalLeave),
        ...repeatLeaveUnit("compensatory", allocation.compensatoryLeave),
        ...repeatLeaveUnit("lop", allocation.lossOfPay),
    ];

    if (units.length > 0) {
        return {
            hasStoredAllocation: true,
            units,
            fallbackUnit: getFallbackLeaveUnit(leave.leaveType),
        };
    }

    return {
        hasStoredAllocation: false,
        units: [],
        fallbackUnit: getFallbackLeaveUnit(leave.leaveType),
    };
};

const applyLeaveUnitToSummary = (summary, unit) => {
    if (unit === "carryForwardCasual") {
        summary.casual.carryForwardUsed += 1;
        summary.casual.carryForwardLeft = Math.max(0, summary.casual.carryForwardLeft - 1);
    } else if (unit === "carryForwardMedical") {
        summary.medical.carryForwardUsed += 1;
        summary.medical.carryForwardLeft = Math.max(0, summary.medical.carryForwardLeft - 1);
    } else if (unit === "casual") {
        summary.casual.normalUsed += 1;
    } else if (unit === "medical") {
        summary.medical.normalUsed += 1;
    } else if (unit === "compensatory") {
        summary.compensatory.normalUsed += 1;
    } else if (unit === "lop") {
        summary.lop.normalUsed += 1;
    }
};

const getPreviousMonthCarryForwardCaps = (employee, leaves = [], attendance = [], holidays = [], referenceDate = new Date()) => {
    const now = getDateOnly(referenceDate) || new Date();
    const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const previousMonthStart = new Date(previousMonthDate.getFullYear(), previousMonthDate.getMonth(), 1);
    const previousMonthEnd = new Date(previousMonthDate.getFullYear(), previousMonthDate.getMonth() + 1, 0);
    const previousMonthEndAsToday = new Date(now.getFullYear(), now.getMonth(), 1);
    const presentSet = buildPresentDateSet(attendance);
    const used = { casual: 0, medical: 0 };

    leaves
        .filter((leave) => String(leave?.status || "").toLowerCase() === "approved")
        .forEach((leave) => {
            const allocation = getLeaveAllocationUnits(leave);
            const leaveStart = getDateOnly(leave.startDate);
            if (!leaveStart) return;
            getLeaveDatesInRange(leave, leaveStart, previousMonthEnd).forEach(({ date, key }, index) => {
                if (date < previousMonthStart || date > previousMonthEnd) return;
                if (presentSet.has(key)) return;
                const unit = allocation.hasStoredAllocation
                    ? allocation.units[index] || allocation.fallbackUnit
                    : allocation.fallbackUnit;
                if (unit === "carryForwardCasual" || unit === "casual") used.casual += 1;
                if (unit === "carryForwardMedical" || unit === "medical") used.medical += 1;
            });
        });

    const monthlyCasual = getMonthlyLeaveAllocation(employee, "monthlyCasualLeave", "casualLeave");
    const monthlyMedical = getMonthlyLeaveAllocation(employee, "monthlyMedicalLeave", "medicalLeave");
    const monthStats = computeMonthAttendanceStats(previousMonthDate.getFullYear(), previousMonthDate.getMonth(), {
        leaves,
        attendance,
        holidays,
        today: previousMonthEndAsToday,
        attendanceStartDate: employee?.attendanceStartDate || employee?.joiningDate,
    });
    const autoBalances = {
        casual: Math.max(0, monthlyCasual - used.casual),
        medical: Math.max(0, monthlyMedical - used.medical),
    };

    Object.values(monthStats.dayMeta || {})
        .filter((meta) => meta?.isNotIn)
        .sort((a, b) => String(a.dateKey).localeCompare(String(b.dateKey)))
        .forEach(() => {
            if (autoBalances.casual > 0) {
                autoBalances.casual -= 1;
                used.casual += 1;
            } else if (autoBalances.medical > 0) {
                autoBalances.medical -= 1;
                used.medical += 1;
            }
        });

    return {
        casual: Math.max(0, monthlyCasual - used.casual),
        medical: Math.max(0, monthlyMedical - used.medical),
    };
};

const applyAutoCompensatedUsage = (summary, autoCompensation) => {
    Object.values(autoCompensation?.allocationByDate || {}).forEach((allocation) => {
        applyLeaveUnitToSummary(summary, allocation === "sick" ? "medical" : allocation);
    });
};

const getLeaveUsageSummary = (employee, leaves = [], attendance = [], holidays = [], referenceDate = new Date()) => {
    const now = getDateOnly(referenceDate) || new Date();
    const casualAllocated = getMonthlyLeaveAllocation(employee, "monthlyCasualLeave", "casualLeave");
    const medicalAllocated = getMonthlyLeaveAllocation(employee, "monthlyMedicalLeave", "medicalLeave");
    const carryForwardCaps = {
        casual: Number(employee?.carriedForwardCasualLeave || 0),
        medical: Number(employee?.carriedForwardMedicalLeave || 0),
    };
    const summary = {
        casual: {
            label: "Casual Leave",
            short: "CL",
            allocated: casualAllocated,
            carryForwardLeft: effectiveAdminCarryForward(employee, "carriedForwardCasualLeave", "casualLeave", carryForwardCaps.casual),
            carryForwardUsed: 0,
            normalUsed: 0,
            used: 0,
            left: casualAllocated,
        },
        medical: {
            label: "Medical Leave",
            short: "ML",
            allocated: medicalAllocated,
            carryForwardLeft: effectiveAdminCarryForward(employee, "carriedForwardMedicalLeave", "medicalLeave", carryForwardCaps.medical),
            carryForwardUsed: 0,
            normalUsed: 0,
            used: 0,
            left: medicalAllocated,
        },
        compensatory: {
            label: "Comp Off",
            short: "CO",
            allocated: Number(employee?.compensatoryLeave || 0),
            carryForwardLeft: 0,
            carryForwardUsed: 0,
            normalUsed: 0,
            used: 0,
            left: Number(employee?.compensatoryLeave || 0),
        },
        lop: {
            label: "Loss of Pay",
            short: "LOP",
            allocated: Number(employee?.lossOfPay || 0),
            carryForwardLeft: 0,
            carryForwardUsed: 0,
            normalUsed: 0,
            used: Number(employee?.lossOfPay || 0),
            left: 0,
        },
    };

    Object.values(summary).forEach((item) => {
        item.used = item.carryForwardUsed + item.normalUsed;
        if (item.short !== "LOP") {
            item.left = Math.max(0, item.allocated + item.carryForwardLeft - item.normalUsed);
        }
    });

    return summary;
};

const LeaveUsageDetail = ({ employee }) => {
    const casual = Math.max(0, Number(employee?.casualLeave || 0));
    const medical = Math.max(0, Number(employee?.medicalLeave || 0));
    const compensatory = Math.max(0, Number(employee?.compensatoryLeave || 0));
    const carryForwardCasual = effectiveAdminCarryForward(employee, "carriedForwardCasualLeave");
    const carryForwardMedical = effectiveAdminCarryForward(employee, "carriedForwardMedicalLeave");
    const carryForwardTotal = carryForwardCasual + carryForwardMedical;
    const totalAvailable = casual + medical + compensatory + carryForwardTotal;
    const rows = [
        { label: "Casual", value: casual, tone: "text-[var(--accent-primary)]" },
        { label: "Medical", value: medical, tone: "text-indigo-600" },
        { label: "Comp Off", value: compensatory, tone: "text-[var(--accent-primary)]" },
        { label: "Carry Forward", value: carryForwardTotal, tone: "text-emerald-600" },
    ];

    return (
        <div className="rounded-xl border border-neutral-200 bg-neutral-50/70 p-4 dark:border-neutral-800 dark:bg-neutral-900/30">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-neutral-400">Available leave balance</p>
                    <div className="mt-1 flex items-end gap-2">
                        <strong className="text-3xl font-black text-emerald-600">{formatLeaveValue(totalAvailable)}</strong>
                        <span className="pb-1 text-xs font-bold text-neutral-500">days total</span>
                    </div>
                </div>
                <span className="w-fit rounded-full bg-[var(--accent-soft)] px-3 py-1 text-[10px] font-black uppercase tracking-wide text-[var(--heading-secondary)] dark:bg-[var(--accent-soft)] dark:text-[var(--heading-secondary)]">
                    Admin controlled
                </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {rows.map((row) => (
                    <div key={row.label} className="rounded-lg border border-neutral-200 bg-[var(--card-bg)] px-3 py-2.5 dark:border-neutral-800 dark:bg-neutral-950/40">
                        <span className="block text-[10px] font-bold uppercase tracking-wide text-neutral-400">{row.label}</span>
                        <strong className={`mt-1 block text-lg ${row.tone}`}>{formatLeaveValue(row.value)}</strong>
                    </div>
                ))}
            </div>
            <p className="mt-3 text-[10px] font-semibold text-neutral-400">
                Carry Forward: {formatLeaveValue(carryForwardCasual)} casual + {formatLeaveValue(carryForwardMedical)} medical. LOP recorded: {formatLeaveValue(employee?.lossOfPay)} day(s).
            </p>
        </div>
    );
};

const EmployeeLeaveBalanceSummary = ({ employee, leaves = [], attendance = [], holidays = [] }) => {
    const [cycleOffset, setCycleOffset] = useState(0);

    useEffect(() => {
        document.title = "Employees | Savrion Technologies";
        setCycleOffset(0);
    }, [employee?._id]);

    const today = new Date();
    const currentCycleStart = today.getDate() >= 25
        ? new Date(today.getFullYear(), today.getMonth(), 25)
        : new Date(today.getFullYear(), today.getMonth() - 1, 25);
    const cycleStart = new Date(
        currentCycleStart.getFullYear(),
        currentCycleStart.getMonth() + cycleOffset,
        25,
    );
    const cycleEnd = new Date(
        cycleStart.getFullYear(),
        cycleStart.getMonth() + 1,
        24,
    );
    cycleStart.setHours(0, 0, 0, 0);
    cycleEnd.setHours(23, 59, 59, 999);

    const formatCycleDate = (date) => date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
    const formatLeaveDate = (date) => date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
    const leaveTypeLabels = {
        casual: "Casual Leave",
        sick: "Medical Leave",
        emergency: "Emergency Leave",
        compensatory: "Comp Off",
        lop: "Loss of Pay",
        other: "Other Leave",
    };
    const approvedLeavesInCycle = leaves
        .filter((leave) => String(leave?.status || "").toLowerCase() === "approved")
        .map((leave) => ({
            leave,
            dates: getLeaveDatesInRange(leave, cycleStart, cycleEnd)
                .map(({ date }) => date),
        }))
        .filter((item) => item.dates.length > 0)
        .sort((a, b) => a.dates[0] - b.dates[0]);
    const totalLeaveDays = approvedLeavesInCycle.reduce(
        (total, item) => total + item.dates.length,
        0,
    );
    const appliedLeaveDateKeys = new Set(
        leaves.flatMap((leave) => getLeaveDatesInRange(leave, cycleStart, cycleEnd)
            .map(({ date }) => toDateKey(date.getFullYear(), date.getMonth(), date.getDate()))),
    );
    const employeeId = String(employee?._id || "");
    const employeeAttendance = attendance.filter((record) => {
        const recordEmployeeId = String(record?.employee?._id || record?.employee || "");
        return !recordEmployeeId || !employeeId || recordEmployeeId === employeeId;
    });
    const attendanceCalculationOptions = {
        leaves,
        attendance: employeeAttendance,
        holidays,
        today,
        attendanceStartDate: employee?.attendanceStartDate || employee?.joiningDate,
    };
    const firstMonthStats = computeMonthAttendanceStats(
        cycleStart.getFullYear(),
        cycleStart.getMonth(),
        attendanceCalculationOptions,
    );
    const secondMonthStats = computeMonthAttendanceStats(
        cycleEnd.getFullYear(),
        cycleEnd.getMonth(),
        attendanceCalculationOptions,
    );
    const absenceWithoutApplication = [];
    for (
        let date = new Date(cycleStart);
        date <= cycleEnd;
        date = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)
    ) {
        const stats = date.getMonth() === cycleStart.getMonth()
            && date.getFullYear() === cycleStart.getFullYear()
            ? firstMonthStats
            : secondMonthStats;
        const dateKey = toDateKey(date.getFullYear(), date.getMonth(), date.getDate());
        if (stats.dayMeta?.[date.getDate()]?.isNotIn && !appliedLeaveDateKeys.has(dateKey)) {
            absenceWithoutApplication.push(new Date(date));
        }
    }

    return (
        <section className="mb-5 rounded-xl border border-neutral-200 bg-neutral-50/70 p-4 dark:border-neutral-800 dark:bg-neutral-900/30">
            <div className="mb-3 flex flex-col gap-1">
                <div>
                    <h4 className="text-sm font-black text-neutral-900 dark:text-neutral-100">Leave taken by cycle</h4>
                    <p className="mt-0.5 text-[11px] font-semibold text-neutral-500 dark:text-neutral-400">
                        Approved leave days taken from the 25th to the following month's 24th.
                    </p>
                </div>
            </div>

            <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-[var(--card-bg)] px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900">
                <button
                    type="button"
                    onClick={() => setCycleOffset((value) => Math.max(-12, value - 1))}
                    disabled={cycleOffset <= -12}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-neutral-100 text-sm font-black text-neutral-600 transition hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-35 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                    aria-label="Previous leave cycle"
                >
                    ←
                </button>
                <div className="min-w-0 text-center">
                    <p className="text-[9px] font-black uppercase tracking-widest text-[var(--accent-primary)]">
                        {cycleOffset === 0 ? "Current leave cycle" : cycleOffset < 0 ? "Previous leave cycle" : "Next leave cycle"}
                    </p>
                    <strong className="mt-0.5 block truncate text-xs text-neutral-900 dark:text-neutral-100">
                        {formatCycleDate(cycleStart)} – {formatCycleDate(cycleEnd)}
                    </strong>
                </div>
                <button
                    type="button"
                    onClick={() => setCycleOffset((value) => Math.min(1, value + 1))}
                    disabled={cycleOffset >= 1}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-neutral-100 text-sm font-black text-neutral-600 transition hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-35 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                    aria-label="Next leave cycle"
                >
                    →
                </button>
            </div>

            <div className="rounded-xl border border-neutral-200 bg-[var(--card-bg)] dark:border-neutral-700 dark:bg-neutral-900">
                <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3 dark:border-neutral-800">
                    <span className="text-xs font-bold text-neutral-500 dark:text-neutral-400">Approved leave taken</span>
                    <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-black text-red-600 dark:bg-red-500/10 dark:text-red-300">
                        {totalLeaveDays} day{totalLeaveDays === 1 ? "" : "s"}
                    </span>
                </div>

                {approvedLeavesInCycle.length ? (
                    <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                        {approvedLeavesInCycle.map(({ leave, dates }) => (
                            <div key={leave?._id || `${leave?.startDate}-${leave?.endDate}`} className="px-4 py-3">
                                <div className="flex items-center justify-between gap-3">
                                    <strong className="text-xs text-neutral-900 dark:text-neutral-100">
                                        {leaveTypeLabels[leave?.leaveType] || "Leave"}
                                    </strong>
                                    <span className="text-[10px] font-black text-red-500">
                                        {dates.length} day{dates.length === 1 ? "" : "s"}
                                    </span>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {dates.map((date) => (
                                        <span
                                            key={date.toISOString()}
                                            className="rounded-lg border border-red-100 bg-red-50 px-2.5 py-1 text-[10px] font-bold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300"
                                        >
                                            {formatLeaveDate(date)}
                                        </span>
                                    ))}
                                </div>
                                {leave?.reason && (
                                    <p className="mt-2 line-clamp-2 text-[10px] font-semibold text-neutral-400">
                                        {leave.reason}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="px-4 py-8 text-center">
                        <CalendarDays className="mx-auto text-neutral-300 dark:text-neutral-600" size={24} />
                        <p className="mt-2 text-xs font-bold text-neutral-500 dark:text-neutral-400">
                            No approved leave taken in this cycle.
                        </p>
                    </div>
                )}
            </div>

            <div className="mt-3 rounded-xl border border-red-200 bg-[var(--card-bg)] dark:border-red-500/30 dark:bg-neutral-900">
                <div className="flex items-center justify-between gap-3 border-b border-red-100 px-4 py-3 dark:border-red-500/20">
                    <div>
                        <span className="block text-xs font-black text-red-600 dark:text-red-300">
                            Absent without leave application
                        </span>
                        <span className="mt-0.5 block text-[10px] font-semibold text-neutral-400">
                            Attendance marked absent with no leave request for that date.
                        </span>
                    </div>
                    <span className="shrink-0 rounded-full bg-red-100 px-3 py-1 text-xs font-black text-red-700 dark:bg-red-500/15 dark:text-red-300">
                        {absenceWithoutApplication.length} day{absenceWithoutApplication.length === 1 ? "" : "s"}
                    </span>
                </div>
                {absenceWithoutApplication.length ? (
                    <div className="flex flex-wrap gap-1.5 px-4 py-3">
                        {absenceWithoutApplication.map((date) => (
                            <span
                                key={date.toISOString()}
                                className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-[10px] font-black text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
                            >
                                {formatLeaveDate(date)}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="px-4 py-4 text-center text-xs font-bold text-neutral-400">
                        No absence without a leave application in this cycle.
                    </p>
                )}
            </div>
        </section>
    );
};

const EmployeeLeaveHistory = ({ leaves = [], loading = false, error = "" }) => {
    const [selectedLeave, setSelectedLeave] = useState(null);
    const formatDate = (value) => value
        ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        : "-";
    const leaveTypeLabels = {
        casual: "Casual Leave",
        sick: "Medical Leave",
        emergency: "Emergency Leave",
        compensatory: "Comp Off",
        lop: "Loss of Pay",
        other: "Other Leave",
    };
    const statusStyles = {
        approved: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
        rejected: "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
        pending: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
    };
    const counts = leaves.reduce((result, leave) => {
        const status = String(leave?.status || "pending").toLowerCase();
        result[status] = (result[status] || 0) + 1;
        return result;
    }, { pending: 0, approved: 0, rejected: 0 });

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {[
                    { label: "Pending", value: counts.pending, tone: "text-amber-600" },
                    { label: "Approved", value: counts.approved, tone: "text-emerald-600" },
                    { label: "Rejected", value: counts.rejected, tone: "text-red-600" },
                ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between rounded-xl border border-neutral-200 bg-[var(--card-bg)] px-4 py-3 shadow-sm dark:border-neutral-800 dark:bg-neutral-900/40">
                        <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{item.label}</span>
                        <strong className={`text-xl ${item.tone}`}>{item.value}</strong>
                    </div>
                ))}
            </div>

            {loading ? (
                <div className="rounded-xl border border-dashed border-neutral-200 px-5 py-12 text-center dark:border-neutral-700">
                    <Loader2 className="mx-auto animate-spin text-[var(--accent-primary)]" size={30} />
                    <p className="mt-3 text-sm font-bold text-neutral-600 dark:text-neutral-300">Loading leave history...</p>
                </div>
            ) : error ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-8 text-center dark:border-red-500/30 dark:bg-red-500/10">
                    <AlertCircle className="mx-auto text-red-500" size={30} />
                    <p className="mt-3 text-sm font-bold text-red-700 dark:text-red-300">{error}</p>
                </div>
            ) : leaves.length ? (
                <div className="w-full overflow-hidden rounded-xl border border-neutral-200 bg-[var(--card-bg)] sm:max-w-md dark:border-neutral-800 dark:bg-neutral-950/20">
                    {leaves.map((leave) => {
                        const status = String(leave?.status || "pending").toLowerCase();
                        return (
                            <article
                                key={leave._id}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedLeave(leave)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") setSelectedLeave(leave);
                                }}
                                className="group cursor-pointer border-b border-neutral-100 p-4 transition last:border-b-0 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--accent-primary)]/30 sm:px-5 dark:border-neutral-800 dark:hover:bg-neutral-900/60"
                            >
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="min-w-0">
                                        <h4 className="text-sm font-black text-neutral-900 dark:text-neutral-100">
                                            {leaveTypeLabels[leave.leaveType] || "Leave"}
                                        </h4>
                                        <p className="mt-1 text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                                            {formatDate(leave.startDate)} – {formatDate(leave.endDate)}
                                        </p>
                                    </div>
                                    <span className={`w-fit rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-wide ${statusStyles[status] || statusStyles.pending}`}>
                                        {status}
                                    </span>
                                </div>
                                <div className="mt-3 flex flex-col gap-1 text-[11px] font-semibold text-neutral-400 sm:flex-row sm:items-center sm:justify-between">
                                    <span>Applied on {formatDate(leave.appliedOn || leave.createdAt)}</span>
                                    <span className="font-black text-[var(--accent-primary)]">View subject →</span>
                                </div>
                            </article>
                        );
                    })}
                </div>
            ) : (
                <div className="rounded-xl border border-dashed border-neutral-200 px-5 py-12 text-center dark:border-neutral-700">
                    <FileCheck2 className="mx-auto text-neutral-300 dark:text-neutral-600" size={30} />
                    <p className="mt-3 text-sm font-bold text-neutral-600 dark:text-neutral-300">No leave applications yet</p>
                    <p className="mt-1 text-xs text-neutral-400">Applications will appear here with their admin approval status.</p>
                </div>
            )}
            {selectedLeave && createPortal(
                <div className="fixed inset-0 z-[300] flex items-end justify-center p-0 sm:items-center sm:p-5">
                    <button type="button" aria-label="Close leave details" onClick={() => setSelectedLeave(null)} className="absolute inset-0 bg-black/65 backdrop-blur-sm" />
                    <div className="relative z-10 max-h-[92dvh] w-full overflow-y-auto rounded-t-3xl border border-neutral-200 bg-[var(--card-bg)] p-5 shadow-2xl sm:max-w-xl sm:rounded-2xl sm:p-6 dark:border-neutral-800 dark:bg-[#121214]">
                        <div className="flex items-start justify-between gap-4 border-b border-neutral-100 pb-4 dark:border-neutral-800">
                            <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-widest text-[var(--accent-primary)]">Leave application subject</p>
                                <h3 className="mt-1 text-xl font-black text-neutral-900 dark:text-neutral-100">{leaveTypeLabels[selectedLeave.leaveType] || "Leave Request"}</h3>
                                <p className="mt-1 text-xs font-semibold text-neutral-400">Applied on {formatDate(selectedLeave.appliedOn || selectedLeave.createdAt)}</p>
                            </div>
                            <button type="button" onClick={() => setSelectedLeave(null)} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-neutral-100 text-neutral-500 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"><X size={17} /></button>
                        </div>
                        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="rounded-xl bg-neutral-50 p-3 dark:bg-neutral-900">
                                <p className="text-[10px] font-black uppercase text-neutral-400">Leave dates</p>
                                <p className="mt-1 text-sm font-bold text-neutral-800 dark:text-neutral-200">{formatDate(selectedLeave.startDate)} – {formatDate(selectedLeave.endDate)}</p>
                            </div>
                            <div className="rounded-xl bg-neutral-50 p-3 dark:bg-neutral-900">
                                <p className="text-[10px] font-black uppercase text-neutral-400">Admin decision</p>
                                <span className={`mt-1 inline-flex rounded-full border px-3 py-1 text-[10px] font-black uppercase ${statusStyles[String(selectedLeave.status || "pending").toLowerCase()] || statusStyles.pending}`}>{selectedLeave.status || "pending"}</span>
                            </div>
                        </div>
                        <div className="mt-4 rounded-xl border border-neutral-100 p-4 dark:border-neutral-800">
                            <p className="text-[10px] font-black uppercase tracking-wide text-neutral-400">Subject / Reason</p>
                            <p className="mt-2 whitespace-pre-wrap text-sm font-semibold leading-6 text-neutral-700 dark:text-neutral-300">{selectedLeave.reason || "No subject or reason was provided."}</p>
                        </div>
                        <div className="mt-4 rounded-xl bg-neutral-50 p-4 text-xs font-semibold text-neutral-500 dark:bg-neutral-900 dark:text-neutral-400">
                            {String(selectedLeave.status).toLowerCase() === "pending"
                                ? "This application is waiting for an admin decision."
                                : `${String(selectedLeave.status).toLowerCase() === "approved" ? "Approved" : "Rejected"} by ${selectedLeave.processedBy?.name || "Admin"}${selectedLeave.processedAt ? ` on ${formatDate(selectedLeave.processedAt)}` : ""}.`}
                        </div>
                        {String(selectedLeave.status).toLowerCase() === "rejected" && selectedLeave.rejectionReason && (
                            <div className="mt-4 rounded-xl border border-red-100 bg-red-50 p-4 dark:border-red-500/20 dark:bg-red-500/10">
                                <p className="text-[10px] font-black uppercase tracking-wide text-red-500">Rejection reason</p>
                                <p className="mt-2 text-sm font-semibold text-red-700 dark:text-red-300">{selectedLeave.rejectionReason}</p>
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

const EMPLOYEE_DETAIL_PAGES = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "leaves", label: "Leave History", icon: FileCheck2 },
    { key: "attendance", label: "Attendance", icon: CalendarDays },
    { key: "timeline", label: "Timeline", icon: History },
    { key: "profile", label: "Profile", icon: UserRound },
];

const buildBackendUrl = (path = "") => {
    if (!path || String(path).startsWith("data:") || String(path).startsWith("blob:")) return path;
    if (/^https?:\/\//i.test(path)) return path;

    const baseURL = String(api.defaults.baseURL || "").replace(/\/+$/, "");
    const normalizedPath = String(path).startsWith("/") ? path : `/${path}`;
    return `${baseURL}${normalizedPath}`;
};

const REQUIRED_EMPLOYEE_UPLOADS = [
    { key: "signedOfferLetter", label: "Signed Offer" },
    { key: "bankPassbook", label: "Bank Passbook" },
    { key: "aadharCard", label: "Aadhaar" },
    { key: "idCardPhoto", label: "ID Photo" },
];

const getDocumentCompliance = (employee = {}) => {
    const documents = Array.isArray(employee.documentCompliance?.documents)
        ? employee.documentCompliance.documents
        : REQUIRED_EMPLOYEE_UPLOADS.map((doc) => ({
            ...doc,
            uploaded: Boolean(employee[doc.key]),
            fileName: employee[`${doc.key}Name`] || "",
        }));
    const uploadedCount = documents.filter((doc) => doc.uploaded).length;
    const missing = documents.filter((doc) => !doc.uploaded);

    return {
        documents,
        uploadedCount,
        total: documents.length,
        missing,
        missingCount: missing.length,
        complete: missing.length === 0,
    };
};

const buildEmployeePageCacheKey = (params = {}) => JSON.stringify({
    page: Number(params.page || 1),
    search: params.search || "",
    team: params.team || "",
    role: params.role || "",
    status: params.status || "",
    documents: params.documents || "",
});

const EMPLOYEE_DIRECTORY_SNAPSHOT_KEY = "admin_employee_directory_snapshot:v1";
const EMPLOYEE_DIRECTORY_SNAPSHOT_TTL = 10 * 60 * 1000;

const readEmployeeDirectorySnapshot = () => {
    try {
        const snapshot = JSON.parse(sessionStorage.getItem(EMPLOYEE_DIRECTORY_SNAPSHOT_KEY) || "null");
        if (!snapshot || Date.now() - snapshot.savedAt > EMPLOYEE_DIRECTORY_SNAPSHOT_TTL) return new Map();
        return new Map(Array.isArray(snapshot.pages) ? snapshot.pages : []);
    } catch {
        return new Map();
    }
};

const writeEmployeeDirectorySnapshot = (pages) => {
    try {
        sessionStorage.setItem(EMPLOYEE_DIRECTORY_SNAPSHOT_KEY, JSON.stringify({
            savedAt: Date.now(),
            pages: Array.from(pages.entries()),
        }));
    } catch {
        // A blocked/full storage area should never prevent the live request.
    }
};

const measureAnchoredPopup = (node, popup, setPopup) => {
    if (!node || !popup || popup.measured) return;
    const viewportPadding = 12;
    const gap = 8;
    const height = Math.min(node.offsetHeight, window.innerHeight - viewportPadding * 2);
    const fitsBelow = popup.anchorBottom + gap + height <= window.innerHeight - viewportPadding;
    const preferredTop = fitsBelow
        ? popup.anchorBottom + gap
        : popup.anchorTop - height - gap;
    const top = Math.max(
        viewportPadding,
        Math.min(preferredTop, window.innerHeight - height - viewportPadding)
    );

    setPopup((current) => current ? { ...current, top, measured: true } : null);
};

const DocumentComplianceBadge = ({ employee, compact = false, summaryOnly = false }) => {
    const compliance = getDocumentCompliance(employee);
    const visibleDocs = compliance.documents;
    const badgeRef = useRef(null);
    const [popup, setPopup] = useState(null);
    const documentSummary = visibleDocs
        .map((doc) => `${doc.label}: ${doc.uploaded ? "Uploaded" : "Missing"}`)
        .join("\n");

    useEffect(() => {
        document.title = "Employees | Savrion Technologies";
        if (!popup) return undefined;
        const close = () => setPopup(null);
        const closeOnEscape = (event) => event.key === "Escape" && close();
        window.addEventListener("resize", close);
        window.addEventListener("scroll", close, true);
        document.addEventListener("click", close);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            window.removeEventListener("resize", close);
            window.removeEventListener("scroll", close, true);
            document.removeEventListener("click", close);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [popup]);

    const togglePopup = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (popup) {
            setPopup(null);
            return;
        }
        const rect = badgeRef.current?.getBoundingClientRect();
        if (!rect) return;
        const popupWidth = 280;
        setPopup({
            anchorTop: rect.top,
            anchorBottom: rect.bottom,
            top: rect.bottom + 8,
            left: Math.max(12, Math.min(rect.left, window.innerWidth - popupWidth - 12)),
            measured: false,
        });
    };

    return (
        <div className={summaryOnly ? "w-full min-w-0" : "w-full min-w-[360px]"}>
            <button
                ref={badgeRef}
                type="button"
                onClick={summaryOnly ? togglePopup : undefined}
                title={summaryOnly ? "View document status" : documentSummary}
                aria-expanded={summaryOnly ? Boolean(popup) : undefined}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-black ${summaryOnly ? "cursor-pointer transition hover:ring-2 hover:ring-[var(--accent-primary)]/20" : "cursor-default"} ${
                compliance.complete
                    ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                    : "bg-amber-500/15 text-amber-600 dark:text-amber-300"
            }`}>
                {compliance.complete ? <FileCheck2 size={13} /> : <AlertCircle size={13} />}
                {compliance.complete ? "Complete" : `${compliance.missingCount} missing`}
            </button>
            {summaryOnly && popup && createPortal(
                <div
                    ref={(node) => measureAnchoredPopup(node, popup, setPopup)}
                    role="dialog"
                    aria-label="Employee document status"
                    onClick={(event) => event.stopPropagation()}
                    className={`fixed z-[300] max-h-[calc(100vh-24px)] w-[280px] overflow-y-auto rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] p-3 shadow-2xl transition-opacity ${popup.measured ? "opacity-100" : "opacity-0"}`}
                    style={{ top: popup.top, left: popup.left }}
                >
                    <div className="mb-2 flex items-center justify-between gap-3 border-b border-[var(--border-color)] pb-2">
                        <strong className="text-xs font-black uppercase tracking-wide">Document status</strong>
                        <span className={`text-[10px] font-bold ${compliance.complete ? "text-emerald-500" : "text-amber-500"}`}>
                            {compliance.uploadedCount}/{compliance.total} uploaded
                        </span>
                    </div>
                    <div className="space-y-1.5">
                        {visibleDocs.map((doc) => (
                            <div key={doc.key} className="flex items-center justify-between gap-3 rounded-lg bg-[var(--bg-primary)] px-2.5 py-2">
                                <span className="text-xs font-semibold">{doc.label}</span>
                                <span className={`inline-flex items-center gap-1 text-[10px] font-black ${doc.uploaded ? "text-emerald-500" : "text-red-500"}`}>
                                    {doc.uploaded ? <FileCheck2 size={12} /> : <AlertCircle size={12} />}
                                    {doc.uploaded ? "Uploaded" : "Missing"}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>,
                document.body
            )}
            {!summaryOnly && visibleDocs.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {visibleDocs.map((doc) => (
                        <span
                            key={doc.key}
                            title={`${doc.label}: ${doc.uploaded ? "Uploaded" : "Missing"}`}
                            className={`whitespace-nowrap rounded-md border px-2.5 py-1 text-[10px] font-black ${
                                doc.uploaded
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300"
                                    : "border-red-200 bg-red-50 text-red-600 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-300"
                            }`}
                        >
                            {doc.uploaded ? "OK" : "Miss"} {doc.label}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const getEscalationCount = (employee = {}) =>
    Number(employee.escalationCount ?? employee.escalationSummary?.count ?? 0) || 0;

const getHistoricalEscalationCount = (employee = {}) =>
    Number(
        employee.escalationSummary?.historicalEscalationCount
        ?? employee.escalationSummary?.mailedEscalationCount
        ?? employee.historicalEscalationCount
        ?? employee.mailedEscalationCount
        ?? 0
    ) || 0;

const getEscalationReasons = (employee = {}) => {
    const reasons = employee.escalationSummary?.reasons || [];
    return Array.isArray(reasons) ? reasons.filter(Boolean) : [];
};

const getEscalationTitle = (employee = {}) => {
    const reasons = getEscalationReasons(employee);
    const historyCount = getHistoricalEscalationCount(employee);
    const activeCount = getEscalationCount(employee);
    const activeText = reasons.length ? reasons.join("\n") : "No active escalations";
    return `${historyCount} historical escalation${historyCount === 1 ? "" : "s"} received\n${activeCount} currently eligible\n${activeText}`;
};

const EscalationBadge = ({ employee, compact = false, summaryOnly = false, align = "left", onSendMail, sending = false, onClear, clearing = false }) => {
    const activeCount = getEscalationCount(employee);
    const historyCount = getHistoricalEscalationCount(employee);
    const reasons = getEscalationReasons(employee);
    const active = activeCount > 0;
    const hasHistory = historyCount > 0;
    const [activePopover, setActivePopover] = useState("");
    const [popoverPopup, setPopoverPopup] = useState(null);
    const summaryBadgeRef = useRef(null);
    const [summaryPopup, setSummaryPopup] = useState(null);
    const summary = employee?.escalationSummary || {};
    const mailStatus = summary.mailStatus || {};
    const sentEscalationMailCount = Number(summary.sentEscalationMailCount || employee?.sentEscalationMailCount || 0);
    const mailedEscalationCount = Number(summary.mailedEscalationCount || employee?.mailedEscalationCount || 0);
    const sentAtLabel = mailStatus.sentAt
        ? new Date(mailStatus.sentAt).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        })
        : "";

    const openPopover = (type) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (type === "history" && !hasHistory) {
            setActivePopover("");
            setPopoverPopup(null);
            return;
        }
        if (activePopover === type) {
            setActivePopover("");
            setPopoverPopup(null);
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const popupWidth = Math.min(304, window.innerWidth - 24);
        setActivePopover(type);
        setPopoverPopup({
            anchorTop: rect.top,
            anchorBottom: rect.bottom,
            top: rect.bottom + 8,
            left: Math.max(12, Math.min(rect.left, window.innerWidth - popupWidth - 12)),
            measured: false,
        });
    };

    useEffect(() => {
        document.title = "Employees | Savrion Technologies";
        if (!activePopover) return undefined;

        const closePopover = () => {
            setActivePopover("");
            setPopoverPopup(null);
        };
        const closeOnEscape = (event) => {
            if (event.key === "Escape") closePopover();
        };

        document.addEventListener("click", closePopover);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("click", closePopover);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [activePopover]);

    useEffect(() => {
        document.title = "Employees | Savrion Technologies";
        if (!summaryPopup) return undefined;
        const close = () => setSummaryPopup(null);
        const closeOnEscape = (event) => event.key === "Escape" && close();
        window.addEventListener("resize", close);
        window.addEventListener("scroll", close, true);
        document.addEventListener("click", close);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            window.removeEventListener("resize", close);
            window.removeEventListener("scroll", close, true);
            document.removeEventListener("click", close);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [summaryPopup]);

    if (summaryOnly) {
        const toggleSummary = (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (summaryPopup) {
                setSummaryPopup(null);
                return;
            }
            const rect = summaryBadgeRef.current?.getBoundingClientRect();
            if (!rect) return;
            const popupWidth = 304;
            setSummaryPopup({
                anchorTop: rect.top,
                anchorBottom: rect.bottom,
                top: rect.bottom + 8,
                left: Math.max(12, Math.min(rect.left, window.innerWidth - popupWidth - 12)),
                measured: false,
            });
        };

        return (
            <span className="inline-flex min-w-0" onClick={(event) => event.stopPropagation()}>
                <button
                    ref={summaryBadgeRef}
                    type="button"
                    onClick={toggleSummary}
                    aria-expanded={Boolean(summaryPopup)}
                    title="View escalation details"
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-black transition hover:ring-2 ${
                        active
                            ? "bg-red-500/10 text-red-500 ring-red-500/20"
                            : "bg-emerald-500/10 text-emerald-600 ring-emerald-500/20 dark:text-emerald-300"
                    }`}
                >
                    <AlertCircle size={12} /> H {historyCount} Â· E {activeCount}
                </button>
                {summaryPopup && createPortal(
                    <div
                        ref={(node) => measureAnchoredPopup(node, summaryPopup, setSummaryPopup)}
                        role="dialog"
                        aria-label="Employee escalation details"
                        onClick={(event) => event.stopPropagation()}
                        className={`fixed z-[300] max-h-[calc(100vh-24px)] w-[304px] overflow-y-auto rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] p-3 text-left shadow-2xl transition-opacity ${summaryPopup.measured ? "opacity-100" : "opacity-0"}`}
                        style={{ top: summaryPopup.top, left: summaryPopup.left }}
                    >
                        <div className="mb-2 flex items-center justify-between gap-3 border-b border-[var(--border-color)] pb-2">
                            <strong className="text-xs font-black uppercase tracking-wide">Escalation details</strong>
                            <span className={`text-[10px] font-black ${active ? "text-red-500" : "text-emerald-500"}`}>
                                {active ? `${activeCount} active` : "No active issues"}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="rounded-lg bg-[var(--bg-primary)] p-2.5">
                                <span className="block text-[9px] font-black uppercase text-[var(--text-secondary)]">History</span>
                                <strong className="text-sm">{historyCount} received</strong>
                            </div>
                            <div className="rounded-lg bg-[var(--bg-primary)] p-2.5">
                                <span className="block text-[9px] font-black uppercase text-[var(--text-secondary)]">Eligible now</span>
                                <strong className={active ? "text-sm text-red-500" : "text-sm text-emerald-500"}>{activeCount} issues</strong>
                            </div>
                        </div>
                        <div className="mt-2 space-y-1.5">
                            {reasons.length > 0 ? reasons.map((reason, index) => (
                                <div key={`${reason}-${index}`} className="flex gap-2 rounded-lg bg-red-500/10 px-2.5 py-2 text-xs font-semibold text-red-600 dark:text-red-300">
                                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                                    <span className="break-words">{reason}</span>
                                </div>
                            )) : (
                                <div className="rounded-lg bg-[var(--bg-primary)] px-2.5 py-2 text-xs font-semibold text-[var(--text-secondary)]">
                                    No current escalation reasons.
                                </div>
                            )}
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2 border-t border-[var(--border-color)] pt-2">
                            <span className="text-[10px] font-bold text-[var(--text-secondary)]">
                                {sentEscalationMailCount} mail{sentEscalationMailCount === 1 ? "" : "s"} sent
                            </span>
                            {active && (
                                <span className="flex gap-1.5">
                                    {onClear && <button type="button" disabled={clearing} onClick={() => onClear(employee)} className="rounded-lg bg-[var(--bg-primary)] px-2.5 py-1.5 text-[10px] font-black disabled:opacity-60">{clearing ? "Clearing" : "Clear"}</button>}
                                    {onSendMail && <button type="button" disabled={sending} onClick={() => onSendMail(employee)} className="rounded-lg bg-red-500 px-2.5 py-1.5 text-[10px] font-black text-white disabled:opacity-60">{sending ? "Sending" : mailStatus.sent ? "Resend" : "Send mail"}</button>}
                                </span>
                            )}
                        </div>
                    </div>,
                    document.body
                )}
            </span>
        );
    }

    return (
        <span
            className="relative inline-flex min-w-[7.5rem] flex-row items-center gap-1.5"
            onClick={(event) => event.stopPropagation()}
        >
            <button
                type="button"
                title={`${historyCount} escalation${historyCount === 1 ? "" : "s"} received in history`}
                aria-expanded={activePopover === "history"}
                disabled={!hasHistory}
                onClick={openPopover("history")}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[9px] font-semibold uppercase leading-none outline-none transition ${
                    hasHistory
                        ? "bg-[var(--accent-subtle)] text-[var(--accent-primary)] ring-1 ring-[var(--accent-primary)]/20 hover:bg-[var(--accent-light)]"
                        : "cursor-default bg-neutral-500/10 text-[var(--text-secondary)] ring-1 ring-neutral-500/10"
                }`}
            >
                <Clock3 size={10} />
                H {historyCount}
            </button>
            <button
                type="button"
                title={getEscalationTitle(employee)}
                aria-expanded={activePopover === "eligible"}
                onClick={openPopover("eligible")}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[9px] font-semibold uppercase leading-none outline-none transition ${
                    active
                        ? "bg-red-500/10 text-red-500 ring-1 ring-red-500/20 hover:bg-red-500/15 focus-visible:ring-2 focus-visible:ring-red-400/50"
                        : "bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/15 hover:bg-emerald-500/15 focus-visible:ring-2 focus-visible:ring-emerald-400/40 dark:text-emerald-300"
                }`}
            >
                <AlertCircle size={10} />
                E {activeCount}
            </button>
            {activePopover && popoverPopup && createPortal(
            <span
                ref={(node) => measureAnchoredPopup(node, popoverPopup, setPopoverPopup)}
                className={`pointer-events-auto fixed z-[300] w-[min(19rem,calc(100vw-2rem))] rounded-xl border border-neutral-200 bg-[var(--card-bg)] p-3 text-left shadow-2xl shadow-black/10 transition-opacity dark:border-neutral-800 dark:bg-neutral-950 ${popoverPopup.measured ? "opacity-100" : "opacity-0"}`}
                style={{ top: popoverPopup.top, left: popoverPopup.left }}
                onClick={(event) => event.stopPropagation()}
            >
                <span className="mb-2 flex items-start justify-between gap-3">
                    <span className="min-w-0">
                        <span className="block text-[10px] font-black uppercase tracking-wide text-neutral-400">
                            {activePopover === "history" ? "Escalation history" : "Eligible escalation"}
                        </span>
                        <span className="mt-0.5 block whitespace-normal text-xs font-bold leading-tight text-neutral-800 dark:text-neutral-100">
                            {activePopover === "history"
                                ? `${historyCount} escalation${historyCount === 1 ? "" : "s"} received`
                                : active ? `${activeCount} issue${activeCount === 1 ? "" : "s"} eligible now` : "No eligible escalations now"}
                        </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1">
                        {activePopover === "eligible" && summary.level && (
                            <span className="rounded-full bg-red-500/10 px-2 py-0.5 text-[9px] font-black uppercase text-red-500">
                                {summary.level}
                            </span>
                        )}
                        <button
                            type="button"
                            title="Cancel"
                            aria-label="Cancel escalation popover"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setActivePopover("");
                                setPopoverPopup(null);
                                event.currentTarget.blur();
                            }}
                            className="inline-grid h-6 w-6 place-items-center rounded-full bg-neutral-100 text-neutral-500 transition hover:bg-neutral-200 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400/50 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700 dark:hover:text-white"
                        >
                            <X size={13} />
                        </button>
                    </span>
                </span>
                {activePopover === "eligible" && (
                    <>
                        {reasons.length > 0 ? (
                            <span className="block space-y-1.5">
                                {reasons.map((reason, index) => (
                                    <span key={`${reason}-${index}`} className="flex gap-2 rounded-lg bg-red-50 px-2.5 py-2 text-xs font-semibold leading-snug text-red-700 dark:bg-red-500/10 dark:text-red-200">
                                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                                        <span className="min-w-0 break-words">{reason}</span>
                                    </span>
                                ))}
                            </span>
                        ) : (
                            <span className="block rounded-lg bg-neutral-50 px-2.5 py-2 text-xs font-semibold text-neutral-500 dark:bg-neutral-900 dark:text-neutral-400">
                                This employee has no current escalation reasons.
                            </span>
                        )}
                    </>
                )}
                <span className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-2.5 py-2 dark:border-neutral-800 dark:bg-neutral-900">
                    <span className="min-w-0">
                        <span className={`block text-[10px] font-black uppercase ${mailStatus.sent ? "text-emerald-600 dark:text-emerald-300" : "text-amber-600 dark:text-amber-300"}`}>
                            {activePopover === "history" ? "History sent" : mailStatus.sent ? "Mail sent" : "Mail not sent"}
                        </span>
                        <span className="mt-0.5 block truncate text-[10px] font-semibold text-neutral-500 dark:text-neutral-400">
                            {sentEscalationMailCount} mail{sentEscalationMailCount === 1 ? "" : "s"} sent for {mailedEscalationCount} escalation{mailedEscalationCount === 1 ? "" : "s"}
                        </span>
                        {activePopover === "eligible" && (
                            <span className="mt-0.5 block truncate text-[10px] font-semibold text-neutral-500 dark:text-neutral-400">
                                {mailStatus.sent ? `${sentAtLabel}${mailStatus.sentTo ? ` to ${mailStatus.sentTo}` : ""}` : "Send when admin wants to notify"}
                            </span>
                        )}
                    </span>
                    {activePopover === "eligible" && (
                        <span className="flex shrink-0 items-center gap-1">
                            {active && (
                                <>
                                    {onClear && (
                                        <button
                                            type="button"
                                            disabled={clearing}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                onClear(employee);
                                            }}
                                            className="inline-flex items-center rounded-lg bg-neutral-200 px-2 py-1.5 text-[10px] font-black uppercase text-neutral-700 transition hover:bg-neutral-300 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
                                        >
                                            {clearing ? "Clearing" : "Clear"}
                                        </button>
                                    )}
                                    {onSendMail && (
                                        <button
                                            type="button"
                                            disabled={sending}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                onSendMail(employee);
                                            }}
                                            className="inline-flex items-center gap-1 rounded-lg bg-red-500 px-2.5 py-1.5 text-[10px] font-black uppercase text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <Mail size={11} />
                                            {sending ? "Sending" : mailStatus.sent ? "Resend" : "Send"}
                                        </button>
                                    )}
                                </>
                            )}
                        </span>
                    )}
                </span>
                {activePopover === "eligible" && summary.window && (
                    <span className="mt-2 block text-[10px] font-bold uppercase tracking-wide text-neutral-400">
                        Window: {summary.window}
                    </span>
                )}
            </span>,
            document.body
            )}
        </span>
    );
};

const Employees = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const initialEmployeePagesRef = useRef(readEmployeeDirectorySnapshot());
    const initialEmployeePageCandidate = initialEmployeePagesRef.current.get(buildEmployeePageCacheKey({ page: 1 }));
    const initialEmployeePage = Array.isArray(initialEmployeePageCandidate?.data) && initialEmployeePageCandidate.data.length > 0
        ? initialEmployeePageCandidate
        : null;
    const [employees, setEmployees] = useState(() => initialEmployeePage?.data || []);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(() => initialEmployeePage?.pages || 1);
    const [loading, setLoading] = useState(() => !initialEmployeePage);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [teamFilter, setTeamFilter] = useState("all");
    const [roleFilter, setRoleFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [documentFilter, setDocumentFilter] = useState("all");
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [modalError, setModalError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [escalationMailSendingId, setEscalationMailSendingId] = useState("");
    const [escalationClearingId, setEscalationClearingId] = useState("");
    const [resendingId, setResendingId] = useState("");
    const [serverStats, setServerStats] = useState(() => initialEmployeePage?.stats || { total: 0, active: 0, inactive: 0, passwordSet: 0, pending: 0 });
    const employeePageCacheRef = initialEmployeePagesRef;

    // Popup Modal States
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedEmployeeLeaves, setSelectedEmployeeLeaves] = useState([]);
    const [selectedEmployeeLeavesState, setSelectedEmployeeLeavesState] = useState({ loading: false, error: "" });
    const [selectedEmployeeAttendance, setSelectedEmployeeAttendance] = useState([]);
    const [selectedEmployeeTimeline, setSelectedEmployeeTimeline] = useState({ items: [], counts: {}, loading: false });
    const [selectedEmployeeTab, setSelectedEmployeeTab] = useState("overview");
    const [publicHolidays, setPublicHolidays] = useState([]);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showAdminSalarySlip, setShowAdminSalarySlip] = useState(false);
    const [signatures, setSignatures] = useState([]);
    const [offerLetterOpen, setOfferLetterOpen] = useState(false);
    const [offerLetterSaving, setOfferLetterSaving] = useState(false);
    const [offerLetterForm, setOfferLetterForm] = useState({ name: "", joiningDate: "", position: "", salary: "", signatureId: "" });
    const [experienceLetterOpen, setExperienceLetterOpen] = useState(false);
    const [experienceLetterSaving, setExperienceLetterSaving] = useState(false);
    const [experienceLetterForm, setExperienceLetterForm] = useState({
        name: "",
        address: "",
        phone: "",
        email: "",
        date: new Date().toISOString().split("T")[0],
        letterType: "internship",
        fromDate: "",
        toDate: "",
        position: "",
        signatureId: "",
    });
    const [relievingLetterOpen, setRelievingLetterOpen] = useState(false);
    const [relievingLetterSaving, setRelievingLetterSaving] = useState(false);
    const [relievingLetterForm, setRelievingLetterForm] = useState({
        name: "",
        employeeId: "",
        location: "",
        position: "",
        date: new Date().toISOString().split("T")[0],
        resignationDate: "",
        relievingDate: "",
        signatureId: "",
    });
    const [internshipLetterOpen, setInternshipLetterOpen] = useState(false);
    const [internshipLetterSaving, setInternshipLetterSaving] = useState(false);
    const [internshipLetterForm, setInternshipLetterForm] = useState({
        internName: "",
        internshipRole: "",
        startDate: "",
        endDate: "",
        amount: "",
        officeMode: "office",
        teamLeadName: "Sanjay Asokan",
        signatureId: "",
    });
    const [noticePeriodOpen, setNoticePeriodOpen] = useState(false);
    const [noticePeriodSaving, setNoticePeriodSaving] = useState(false);
    const [noticePeriodForm, setNoticePeriodForm] = useState({
        durationValue: "30",
        durationUnit: "days",
        effectiveDate: new Date().toISOString().split("T")[0],
        reason: "",
        notes: "",
    });

    const { teams, roles, addTeam, addRole, deleteTeam, deleteRole } = useMeta();

    const CUSTOM_OPTION_VALUE = "__custom__";
    const [customTeamInput, setCustomTeamInput] = useState("");
    const [customRoleInput, setCustomRoleInput] = useState("");

    const [showManageTeams, setShowManageTeams] = useState(false);
    const [showManageRoles, setShowManageRoles] = useState(false);
    const [metaSubmitting, setMetaSubmitting] = useState(false);
    const [newTeamName, setNewTeamName] = useState("");
    const [newRoleName, setNewRoleName] = useState("");

    const normalizeText = (value) => String(value || "").trim().toLowerCase();
    const isMongoId = (value) => /^[a-f\d]{24}$/i.test(String(value || ""));

    const resetCustomInputs = () => {
        setCustomTeamInput("");
        setCustomRoleInput("");
    };

    const resolveTeamValue = async (value) => {
        const trimmed = String(value || "").trim();
        if (!trimmed) return "";

        const existing = teams.find((t) => normalizeText(t?.value) === normalizeText(trimmed));
        if (existing) return existing.value;

        try {
            const created = await addTeam(trimmed);
            return created;
        } catch (err) {
            // Don't block employee creation; still save the typed value.
            alert(err?.response?.data?.message || err.message || "Failed to add team");
            return trimmed;
        }
    };

    const resolveRoleValue = async (value) => {
        const trimmed = String(value || "").trim();
        if (!trimmed) return "";

        const existing = roles.find((r) => normalizeText(r) === normalizeText(trimmed));
        if (existing) return existing;

        try {
            const created = await addRole(trimmed);
            return created;
        } catch (err) {
            alert(err?.response?.data?.message || err.message || "Failed to add role");
            return normalizeText(trimmed);
        }
    };

    useEffect(() => {
        document.title = "Employees | Savrion Technologies";
        if (selectedEmployee?._id) {
            setSelectedEmployeeLeavesState({ loading: true, error: "" });
            api.get(`/api/admin/employees/${selectedEmployee._id}/leave-history`, { params: { _t: Date.now() } })
                .then(res => {
                    setSelectedEmployeeLeaves(Array.isArray(res.data) ? res.data : []);
                    setSelectedEmployeeLeavesState({ loading: false, error: "" });
                })
                .catch(err => {
                    console.error("Failed to fetch employee leaves:", err);
                    setSelectedEmployeeLeaves([]);
                    setSelectedEmployeeLeavesState({
                        loading: false,
                        error: err.response?.data?.message || "Could not load this employee's leave history.",
                    });
                });

            api.get("/api/admin/attendance", {
                cache: false,
                params: { employee: selectedEmployee._id, _t: Date.now() },
            })
                .then(res => setSelectedEmployeeAttendance(res.data))
                .catch(err => console.error("Failed to fetch employee attendance:", err));

            setSelectedEmployeeTimeline((current) => ({ ...current, loading: true }));
            api.get(`/api/admin/employees/${selectedEmployee._id}/timeline`, { params: { limit: 80, _t: Date.now() } })
                .then(res => setSelectedEmployeeTimeline({ items: res.data?.items || [], counts: res.data?.counts || {}, loading: false }))
                .catch(err => {
                    console.error("Failed to fetch employee timeline:", err);
                    setSelectedEmployeeTimeline({ items: [], counts: {}, loading: false });
                });

            if (!selectedEmployee.hasOwnProperty('offerLetter') || !selectedEmployee.hasOwnProperty('experienceLetter') || !selectedEmployee.hasOwnProperty('relievingLetter') || !selectedEmployee.hasOwnProperty('internshipLetter')) {
                api.get(`/api/admin/employees/${selectedEmployee._id}`)
                    .then(res => setSelectedEmployee(res.data))
                    .catch(err => console.error("Failed to fetch full employee details:", err));
            }
        } else {
            setSelectedEmployeeLeaves([]);
            setSelectedEmployeeLeavesState({ loading: false, error: "" });
            setSelectedEmployeeAttendance([]);
            setSelectedEmployeeTimeline({ items: [], counts: {}, loading: false });
        }
    }, [selectedEmployee]);

    useEffect(() => {
        document.title = "Employees | Savrion Technologies";
        setSelectedEmployeeTab("overview");
    }, [selectedEmployee?._id]);

    const [form, setForm] = useState({
        name: "",
        email: "",
        team: "",
        managedTeams: [],
        role: "",
        designation: "",
        employeeId: "",
        salary: "",
        hasStayAllowance: false,
        selfieEnabled: true,
        stayAllowanceCarryForward: false,
        lossOfPay: "",
        casualLeave: "",
        medicalLeave: "",
        compensatoryLeave: "",
        carriedForwardCasualLeave: "",
        carriedForwardMedicalLeave: "",
        workLocation: "Head Office",
        employmentType: "Full-time",
        joiningDate: "",
        shiftTiming: "09:00 AM - 06:00 PM",
        workingDays: [],
        employeeStatus: "Active",
        autoSendEmail: true,
        panNumber: "",
        uanNumber: "",
        bankDetails: {
            accountNumber: "",
            bankName: "",
            ifscCode: "",
            accountHolderName: ""
        }
        ,assignedAssets: []
    });

    const handleTeamFilterChange = async (e) => {
        const value = e.target.value;
        if (value === "__add_team__") {
            setShowManageTeams(true);
            return;
        }
        setTeamFilter(value);
    };

    const handleRoleFilterChange = async (e) => {
        const value = e.target.value;
        if (value === "__add_role__") {
            setShowManageRoles(true);
            return;
        }
        setRoleFilter(value);
    };

    const handleTeamFormChange = (e) => {
        const value = e.target.value;
        setForm((prev) => ({
            ...prev,
            team: value,
            managedTeams: String(prev.role || "").toLowerCase() === "lead" && value !== CUSTOM_OPTION_VALUE
                ? [...new Set([...(prev.managedTeams || []), value].filter(Boolean))]
                : prev.managedTeams,
        }));
        if (value !== CUSTOM_OPTION_VALUE) {
            setCustomTeamInput("");
        }
    };

    const handleRoleFormChange = (e) => {
        const value = e.target.value;
        setForm((prev) => ({
            ...prev,
            role: value,
            managedTeams: String(value || "").toLowerCase() === "lead"
                ? [...new Set([...(prev.managedTeams || []), prev.team].filter(Boolean))]
                : [],
        }));
        if (value !== CUSTOM_OPTION_VALUE) {
            setCustomRoleInput("");
        }
    };

    const handleCustomTeamBlur = async () => {
        if (!customTeamInput.trim()) return;
        const resolved = await resolveTeamValue(customTeamInput);
        setForm((prev) => ({ ...prev, team: resolved }));
        setCustomTeamInput("");
    };

    const handleCustomRoleBlur = async () => {
        if (!customRoleInput.trim()) return;
        const resolved = await resolveRoleValue(customRoleInput);
        setForm((prev) => ({ ...prev, role: resolved }));
        setCustomRoleInput("");
    };

    // ================= CLEAR ALERT =================
    useEffect(() => {
        document.title = "Employees | Savrion Technologies";
        if (successMsg || errorMsg) {
            const timer = setTimeout(() => {
                setSuccessMsg("");
                setErrorMsg("");
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMsg, errorMsg]);

    useEffect(() => {
        document.title = "Employees | Savrion Technologies";
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 250);

        return () => clearTimeout(timer);
    }, [search]);

    // ================= AUTO-GENERATE EMPLOYEE ID =================
    useEffect(() => {
        document.title = "Employees | Savrion Technologies";
        if (!showAddModal || form.employeeId) return;

        let cancelled = false;

        const generateEmployeeId = async () => {
            let sourceEmployees = employees;
            let companyPrefix = "";

            try {
                const [empRes, compRes] = await Promise.allSettled([
                    api.get("/api/admin/employees/all"),
                    api.get("/api/admin/company")
                ]);

                if (empRes.status === "fulfilled" && Array.isArray(empRes.value.data)) {
                    sourceEmployees = empRes.value.data;
                }
                if (compRes.status === "fulfilled" && compRes.value.data?.employeeIdPrefix) {
                    companyPrefix = String(compRes.value.data.employeeIdPrefix).trim().toUpperCase();
                }
            } catch (err) {
                console.error("Failed to fetch employees or company for ID generation:", err);
            }

            if (cancelled) return;

            // Extract base text prefix (e.g. "SAV001" -> "SAV", "AVIA" -> "AVIA")
            let prefix = companyPrefix.replace(/\d+$/, "");
            if (!prefix) {
                prefix = companyPrefix || "AVIA";
            }

            let maxNum = 0;
            sourceEmployees.forEach(emp => {
                if (emp.employeeId) {
                    const uppercaseId = String(emp.employeeId).toUpperCase();
                    if (uppercaseId.startsWith(prefix)) {
                        const numStr = uppercaseId.substring(prefix.length);
                        const num = parseInt(numStr, 10);
                        if (!isNaN(num) && num > maxNum) {
                            maxNum = num;
                        }
                    }
                }
            });

            if (maxNum === 0 && sourceEmployees.length > 0) {
                sourceEmployees.forEach(emp => {
                    if (emp.employeeId) {
                        const match = String(emp.employeeId).match(/^([A-Z]+)(\d+)$/i);
                        if (match) {
                            const num = parseInt(match[2], 10);
                            if (!isNaN(num) && num > maxNum) {
                                maxNum = num;
                            }
                        }
                    }
                });
            }

            const nextNum = maxNum + 1;
            const nextId = `${prefix}${nextNum.toString().padStart(3, '0')}`;
            setForm(prev => prev.employeeId ? prev : { ...prev, employeeId: nextId });
        };

        generateEmployeeId();

        return () => {
            cancelled = true;
        };
    }, [showAddModal, form.employeeId, employees]);

    const buildEmployeeListParams = useCallback((overrides = {}) => ({
        page,
        search: debouncedSearch,
        team: teamFilter === "all" ? "" : teamFilter,
        role: roleFilter === "all" ? "" : roleFilter,
        status: statusFilter === "all" ? "" : statusFilter,
        documents: documentFilter === "all" ? "" : documentFilter,
        ...overrides,
    }), [page, debouncedSearch, teamFilter, roleFilter, statusFilter, documentFilter]);

    const PaginationControls = () => {
        if (totalPages <= 1) return null;

        return (
            <div className="flex items-center justify-center gap-3">
                {[...Array(totalPages)].map((_, index) => (
                    <button
                        key={index}
                        type="button"
                        onClick={() => setPage(index + 1)}
                        className={`h-11 w-11 rounded-2xl font-semibold transition-all ${page === index + 1
                            ? "bg-[var(--accent-primary)] text-white"
                            : "border border-[var(--border-color)] bg-[var(--card-bg)] hover:bg-[var(--accent-primary)]/10"
                            }`}
                    >
                        {index + 1}
                    </button>
                ))}
            </div>
        );
    };

    const cacheEmployeePage = useCallback((params, payload) => {
        const cacheKey = buildEmployeePageCacheKey(params);
        const existing = employeePageCacheRef.current.get(cacheKey);
        employeePageCacheRef.current.set(cacheKey, {
            data: Array.isArray(payload?.data) ? payload.data : [],
            pages: payload?.pages || 1,
            stats: payload?.stats || existing?.stats || null,
        });
        writeEmployeeDirectorySnapshot(employeePageCacheRef.current);
    }, [employeePageCacheRef]);

    const prefetchEmployeePage = useCallback(async (nextPage) => {
        if (!nextPage || nextPage < 1) return;
        const params = buildEmployeeListParams({
            page: nextPage,
            includeEscalations: true,
            includeStats: false,
        });
        const cacheKey = buildEmployeePageCacheKey(params);
        if (employeePageCacheRef.current.has(cacheKey)) return;

        try {
            const res = await api.get("/api/admin/employees", { params });
            cacheEmployeePage(params, res.data || {});
        } catch (err) {
            console.error("Failed to prefetch employees page:", err);
        }
    }, [buildEmployeeListParams, cacheEmployeePage, employeePageCacheRef]);

    // ================= FETCH =================
    const fetchEmployees = useCallback(async (background = false) => {
        try {
            const params = buildEmployeeListParams({
                // The directory itself is cheap; escalation summaries fan out into
                // several collections. Paint the list first, then enrich it in the
                // background so navigation never waits on those reports.
                includeEscalations: background,
                includeStats: background && page === 1,
            });
            const cacheKey = buildEmployeePageCacheKey(params);
            const cached = employeePageCacheRef.current.get(cacheKey);

            if (!background && cached && cached.data.length > 0) {
                setEmployees(cached.data);
                setTotalPages(cached.pages);
                if (cached.stats) {
                    setServerStats(cached.stats);
                }
                setLoading(false);
                if (page < cached.pages) {
                    prefetchEmployeePage(page + 1);
                }
                window.setTimeout(() => fetchEmployees(true), 0);
                return;
            }

            if (!background && employees.length === 0) setLoading(true);
            const res = await api.get("/api/admin/employees", {
                params,
            });
            const list = res.data.data || [];
            cacheEmployeePage(params, res.data || {});
            setEmployees(list);
            setTotalPages(res.data.pages || 1);
            if (res.data.stats) {
                setServerStats(res.data.stats);
            }

            // Keep the salary slip preview stable while it is open.
            if (!showAdminSalarySlip) {
                setSelectedEmployee(prev => {
                    if (prev) {
                        const freshData = list.find(e => e._id === prev._id);
                        return freshData ? { ...prev, ...freshData } : prev;
                    }
                    return null;
                });
            }

            // Automatically open profile if requested by redirect state
            if (!background && location.state?.openEmployeeId) {
                const target = list.find(e => e._id === location.state.openEmployeeId);
                if (target) {
                    setSelectedEmployee(target);
                    // Clear location state to avoid reopening on refresh
                    window.history.replaceState({}, document.title);
                }
            }

            if (!background && page < (res.data.pages || 1)) {
                prefetchEmployeePage(page + 1);
            }

            if (!background) {
                window.setTimeout(() => fetchEmployees(true), 0);
            }
        } catch (err) {
            console.error(err);
            if (err.response?.status === 401 || err.response?.status === 403) {
                navigate("/admin/login");
            } else if (!background) {
                setErrorMsg(err.response?.data?.message || "Unable to load employees. Please retry.");
            }
        } finally {
            if (!background) setLoading(false);
        }
    }, [navigate, page, showAdminSalarySlip, location.state?.openEmployeeId, employees.length, buildEmployeeListParams, cacheEmployeePage, prefetchEmployeePage, employeePageCacheRef]);

    const handleSendEmployeeEscalationMail = async (employee) => {
        if (!employee?._id) return;
        setEscalationMailSendingId(employee._id);
        setSuccessMsg("");
        setErrorMsg("");
        try {
            const res = await api.post(`/api/admin/employees/${employee._id}/escalation-mail`);
            const escalationSummary = res.data?.escalationSummary;
            const escalationCount = res.data?.escalationCount;
            const patch = {
                ...(escalationSummary ? { escalationSummary } : {}),
                ...(escalationCount !== undefined ? { escalationCount } : {}),
            };
            setEmployees((current) => current.map((item) => (
                item._id === employee._id ? { ...item, ...patch } : item
            )));
            setSelectedEmployee((current) => (
                current?._id === employee._id ? { ...current, ...patch } : current
            ));
            setSuccessMsg(res.data?.message || "Escalation mail sent.");
        } catch (err) {
            setErrorMsg(err.response?.data?.message || "Failed to send escalation mail.");
        } finally {
            setEscalationMailSendingId("");
        }
    };

    const handleClearEmployeeEscalation = async (employee) => {
        if (!employee?._id) return;
        setEscalationClearingId(employee._id);
        setSuccessMsg("");
        setErrorMsg("");
        try {
            const res = await api.post(`/api/admin/employees/${employee._id}/escalation-clear`);
            const escalationSummary = res.data?.escalationSummary;
            const escalationCount = res.data?.escalationCount;
            const patch = {
                ...(escalationSummary ? { escalationSummary } : {}),
                ...(escalationCount !== undefined ? { escalationCount } : {}),
            };
            setEmployees((current) => current.map((item) => (
                item._id === employee._id ? { ...item, ...patch } : item
            )));
            setSelectedEmployee((current) => (
                current?._id === employee._id ? { ...current, ...patch } : current
            ));
            setSuccessMsg(res.data?.message || "Escalation cleared.");
        } catch (err) {
            setErrorMsg(err.response?.data?.message || "Failed to clear escalation.");
        } finally {
            setEscalationClearingId("");
        }
    };

    useEffect(() => {
        document.title = "Employees | Savrion Technologies";
        if (showAdminSalarySlip) return;

        fetchEmployees();

        // Background live-sync interval. Keep this light so the Employees page stays responsive.
        const interval = setInterval(() => {
            fetchEmployees(true);
        }, 60000);

        return () => clearInterval(interval);
    }, [fetchEmployees, showAdminSalarySlip]);

    useEffect(() => {
        document.title = "Employees | Savrion Technologies";
        let active = true;
        api.get("/api/holidays/public")
            .then((res) => {
                if (active) setPublicHolidays(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => {
                if (active) setPublicHolidays([]);
            });
        return () => {
            active = false;
        };
    }, []);

    const fetchSignatures = async () => {
        try {
            const res = await api.get("/api/admin/signatures");
            setSignatures(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Failed to fetch signatures:", err);
        }
    };

    useEffect(() => {
        document.title = "Employees | Savrion Technologies";
        if (offerLetterOpen || experienceLetterOpen || relievingLetterOpen || internshipLetterOpen) {
            fetchSignatures();
        }
    }, [offerLetterOpen, experienceLetterOpen, relievingLetterOpen, internshipLetterOpen]);

    // ================= VIEW FILE (BLOB BYPASS) =================
    const handleViewFile = (dataUrl) => {
        try {
            const arr = dataUrl.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            const blob = new Blob([u8arr], { type: mime });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) {
            console.error("Error viewing file:", error);
            window.open(dataUrl, '_blank'); // fallback
        }
    };

    const formatDateInput = (value) => {
        if (!value) return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "";
        return date.toISOString().split("T")[0];
    };

    const applyUpdatedEmployee = (updated) => {
        setSelectedEmployee(updated);
        setEmployees((current) => current.map((item) => item._id === updated._id ? { ...item, ...updated } : item));
        setEditingEmployee((current) => current?._id === updated._id ? { ...current, ...updated } : current);
    };

    const openNoticePeriodEditor = (employee, event) => {
        event?.stopPropagation?.();
        const notice = employee?.noticePeriod || {};
        setSelectedEmployee(employee);
        setNoticePeriodForm({
            durationValue: notice.durationValue ? String(notice.durationValue) : "30",
            durationUnit: notice.durationUnit || "days",
            effectiveDate: formatDateInput(notice.effectiveDate) || new Date().toISOString().split("T")[0],
            reason: notice.reason || "",
            notes: notice.notes || "",
        });
        setNoticePeriodOpen(true);
    };

    const handleNoticePeriodSubmit = async (event) => {
        event.preventDefault();
        if (!selectedEmployee?._id) return;

        const payload = {
            durationValue: Number(noticePeriodForm.durationValue || 0),
            durationUnit: noticePeriodForm.durationUnit,
            effectiveDate: noticePeriodForm.effectiveDate,
            reason: noticePeriodForm.reason.trim(),
            notes: noticePeriodForm.notes.trim(),
        };

        if (!payload.durationValue || payload.durationValue < 1 || !payload.effectiveDate) {
            setErrorMsg("Notice duration and effective date are required.");
            return;
        }

        try {
            setNoticePeriodSaving(true);
            const res = await api.post(`/api/admin/employees/${selectedEmployee._id}/notice-period`, payload);
            applyUpdatedEmployee(res.data?.employee || { ...selectedEmployee, noticePeriod: payload });
            setSuccessMsg(res.data?.message || "Notice period email sent successfully");
            setNoticePeriodOpen(false);
        } catch (err) {
            setErrorMsg(err.response?.data?.message || "Failed to send notice period email");
        } finally {
            setNoticePeriodSaving(false);
        }
    };

    const openOfferLetterEditor = (employee) => {
        const details = employee?.offerLetterDetails || {};
        setOfferLetterForm({
            name: details.name || employee?.name || "",
            joiningDate: formatDateInput(details.joiningDate || employee?.joiningDate),
            position: details.position || employee?.designation || employee?.role || "",
            salary: details.salary || employee?.salary || "",
            signatureId: details.signature?.id || "",
        });
        setOfferLetterOpen(true);
    };

    const handleOfferLetterSubmit = async (event) => {
        event.preventDefault();
        if (!selectedEmployee?._id) return;
        const payload = {
            name: offerLetterForm.name.trim(),
            joiningDate: offerLetterForm.joiningDate,
            position: offerLetterForm.position.trim(),
            salary: String(offerLetterForm.salary || "").trim(),
            signatureId: offerLetterForm.signatureId,
        };
        if (!payload.name || !payload.joiningDate || !payload.position || !payload.salary || !payload.signatureId) {
            setErrorMsg("Name, joining date, designation, salary and signature are required.");
            return;
        }
        try {
            setOfferLetterSaving(true);
            const res = await api.post(`/api/admin/employees/${selectedEmployee._id}/offer-letter`, payload);
            applyUpdatedEmployee(res.data?.employee || { ...selectedEmployee, ...res.data });
            setSuccessMsg(res.data?.message || "Offer letter saved successfully");
            setOfferLetterOpen(false);
        } catch (err) {
            setErrorMsg(err.response?.data?.message || "Failed to generate offer letter");
        } finally {
            setOfferLetterSaving(false);
        }
    };

    const openExperienceLetterEditor = async (employee) => {
        let employeeWithDetails = employee;
        if (employee?._id && !employee?.latestOffboarding) {
            try {
                const res = await api.get(`/api/admin/employees/${employee._id}`);
                employeeWithDetails = res.data;
                setSelectedEmployee(res.data);
            } catch (err) {
                console.error("Failed to fetch employee offboarding details:", err);
            }
        }
        employee = employeeWithDetails;
        const details = employee?.experienceLetterDetails || {};
        const offboardingToDate = employee?.latestOffboarding?.approvedLastWorkingDate || employee?.latestOffboarding?.requestedLastWorkingDate;
        const defaultName = details.name || employee?.name || "";
        const defaultAddress = details.address || (defaultName ? `${stripSalutation(defaultName)}\nAVIATRICKS AERO LABS PRIVATE LIMITED` : "");
        setExperienceLetterForm({
            name: defaultName,
            address: defaultAddress,
            phone: details.phone || employee?.phone || "",
            email: details.email || employee?.email || "",
            date: formatDateInput(details.date) || new Date().toISOString().split("T")[0],
            letterType: details.letterType || "internship",
            fromDate: formatDateInput(details.fromDate || employee?.joiningDate),
            toDate: formatDateInput(offboardingToDate || details.toDate),
            position: details.position || employee?.designation || employee?.role || "",
            signatureId: details.signature?.id || "",
        });
        setExperienceLetterOpen(true);
    };

    const handleExperienceLetterSubmit = async (event) => {
        event.preventDefault();
        if (!selectedEmployee?._id) return;
        const payload = {
            name: experienceLetterForm.name.trim(),
            address: experienceLetterForm.address.trim(),
            phone: experienceLetterForm.phone.trim(),
            email: experienceLetterForm.email.trim(),
            date: experienceLetterForm.date,
            letterType: experienceLetterForm.letterType.trim(),
            fromDate: experienceLetterForm.fromDate,
            toDate: experienceLetterForm.toDate,
            position: experienceLetterForm.position.trim(),
            signatureId: experienceLetterForm.signatureId,
        };
        if (!payload.name || !payload.address || !payload.phone || !payload.email || !payload.date || !payload.letterType || !payload.fromDate || !payload.toDate || !payload.position || !payload.signatureId) {
            setErrorMsg("Address, phone, email, date, letter wording, employee name, from date, to date, position and signature are required.");
            return;
        }
        try {
            setExperienceLetterSaving(true);
            const res = await api.post(`/api/admin/employees/${selectedEmployee._id}/experience-letter`, payload);
            applyUpdatedEmployee(res.data?.employee || { ...selectedEmployee, ...res.data });
            setSuccessMsg(res.data?.message || "Experience letter saved successfully");
            setExperienceLetterOpen(false);
        } catch (err) {
            setErrorMsg(err.response?.data?.message || "Failed to generate experience letter");
        } finally {
            setExperienceLetterSaving(false);
        }
    };

    const openRelievingLetterEditor = (employee) => {
        const details = employee?.relievingLetterDetails || {};
        setRelievingLetterForm({
            name: details.name || employee?.name || "",
            employeeId: details.employeeId || employee?.employeeId || "",
            location: details.location || details.workLocation || employee?.workLocation || "",
            position: details.position || employee?.designation || employee?.role || "",
            date: formatDateInput(details.date) || new Date().toISOString().split("T")[0],
            resignationDate: formatDateInput(details.resignationDate),
            relievingDate: formatDateInput(details.relievingDate),
            signatureId: details.signature?.id || "",
        });
        setRelievingLetterOpen(true);
    };

    const handleRelievingLetterSubmit = async (event) => {
        event.preventDefault();
        if (!selectedEmployee?._id) return;
        const payload = {
            name: relievingLetterForm.name.trim(),
            employeeId: relievingLetterForm.employeeId.trim(),
            location: relievingLetterForm.location.trim(),
            position: relievingLetterForm.position.trim(),
            date: relievingLetterForm.date,
            resignationDate: relievingLetterForm.resignationDate,
            relievingDate: relievingLetterForm.relievingDate,
            signatureId: relievingLetterForm.signatureId,
        };
        if (!payload.name || !payload.employeeId || !payload.location || !payload.position || !payload.date || !payload.resignationDate || !payload.relievingDate || !payload.signatureId) {
            setErrorMsg("Date, employee name, employee ID, location, designation, resignation date, relieving date and signature are required.");
            return;
        }
        try {
            setRelievingLetterSaving(true);
            const res = await api.post(`/api/admin/employees/${selectedEmployee._id}/relieving-letter`, payload);
            applyUpdatedEmployee(res.data?.employee || { ...selectedEmployee, ...res.data });
            setSuccessMsg(res.data?.message || "Relieving letter saved successfully");
            setRelievingLetterOpen(false);
        } catch (err) {
            setErrorMsg(err.response?.data?.message || "Failed to generate relieving letter");
        } finally {
            setRelievingLetterSaving(false);
        }
    };

    const openInternshipLetterEditor = (employee) => {
        const details = employee?.internshipLetterDetails || {};
        setInternshipLetterForm({
            internName: details.internName || details.name || employee?.name || "",
            internshipRole: details.internshipRole || details.role || employee?.designation || employee?.role || "",
            startDate: formatDateInput(details.startDate || employee?.joiningDate),
            endDate: formatDateInput(details.endDate),
            amount: details.amount || "",
            officeMode: details.officeMode || "office",
            teamLeadName: details.teamLeadName || "Sanjay Asokan",
            signatureId: details.signature?.id || "",
        });
        setInternshipLetterOpen(true);
    };

    const handleInternshipLetterSubmit = async (event) => {
        event.preventDefault();
        if (!selectedEmployee?._id) return;
        const payload = {
            internName: internshipLetterForm.internName.trim(),
            internshipRole: internshipLetterForm.internshipRole.trim(),
            startDate: internshipLetterForm.startDate,
            endDate: internshipLetterForm.endDate,
            amount: String(internshipLetterForm.amount || "").trim(),
            officeMode: internshipLetterForm.officeMode.trim(),
            teamLeadName: internshipLetterForm.teamLeadName.trim(),
            signatureId: internshipLetterForm.signatureId,
        };
        if (!payload.internName || !payload.internshipRole || !payload.startDate || !payload.endDate || !payload.amount || !payload.officeMode || !payload.teamLeadName || !payload.signatureId) {
            setErrorMsg("Intern name, internship role, start date, end date, amount, office mode, team lead name and signature are required.");
            return;
        }
        try {
            setInternshipLetterSaving(true);
            const res = await api.post(`/api/admin/employees/${selectedEmployee._id}/internship-letter`, payload);
            applyUpdatedEmployee(res.data?.employee || { ...selectedEmployee, ...res.data });
            setSuccessMsg(res.data?.message || "Internship letter saved successfully");
            setInternshipLetterOpen(false);
        } catch (err) {
            setErrorMsg(err.response?.data?.message || "Failed to generate internship letter");
        } finally {
            setInternshipLetterSaving(false);
        }
    };

    const handleGeneratedLetterDelete = async (employee, letterKey) => {
        const config = {
            offerLetter: { endpoint: "offer-letter", label: "offer letter" },
            experienceLetter: { endpoint: "experience-letter", label: "experience letter" },
            relievingLetter: { endpoint: "relieving-letter", label: "relieving letter" },
            internshipLetter: { endpoint: "internship-letter", label: "internship letter" },
        }[letterKey];
        if (!employee?._id || !config) return;
        if (!window.confirm(`Delete this ${config.label}?`)) return;
        try {
            const res = await api.delete(`/api/admin/employees/${employee._id}/${config.endpoint}`);
            applyUpdatedEmployee(res.data?.employee || {
                ...employee,
                [letterKey]: "",
                [`${letterKey}Name`]: "",
                [`${letterKey}DetailsUrl`]: "",
            });
            setSuccessMsg(res.data?.message || `${config.label} deleted successfully`);
        } catch (err) {
            setErrorMsg(err.response?.data?.message || `Failed to delete ${config.label}`);
        }
    };

    const handleUploadedDocumentDelete = async (employee, doc) => {
        if (!employee?._id || !doc?.key) return;
        if (!window.confirm(`Delete this ${doc.label}? This will remove it from the employee profile too.`)) return;
        try {
            const res = await api.delete(`/api/admin/employees/${employee._id}/uploaded-documents/${doc.key}`);
            applyUpdatedEmployee(res.data?.employee || {
                ...employee,
                [doc.key]: "",
                [`${doc.key}Name`]: "",
                ...(doc.key === "signedOfferLetter" ? { signedOfferLetterUploadedAt: null } : {}),
            });
            setSuccessMsg(res.data?.message || `${doc.label} deleted successfully`);
        } catch (err) {
            setErrorMsg(err.response?.data?.message || `Failed to delete ${doc.label}`);
        }
    };

    // ================= CREATE =================
    const handleCreate = async (e) => {
        e.preventDefault();
        setModalError("");
        try {
            setSubmitting(true);
            const rawTeam = form.team === CUSTOM_OPTION_VALUE ? customTeamInput : form.team;
            const rawRole = form.role === CUSTOM_OPTION_VALUE ? customRoleInput : form.role;
            const team = await resolveTeamValue(rawTeam);
            const role = await resolveRoleValue(rawRole);
            const payload = {
                ...form,
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                employeeId: form.employeeId.trim(),
                team,
                role,
                designation: form.designation.trim(),
            };
            await api.post("/api/admin/employees", payload);
            setSuccessMsg(`Invite sent to ${payload.email}`);
            setShowAddModal(false);
            setModalError("");
            resetCustomInputs();
            setForm({
                name: "",
                email: "",
                team: "",
                role: "",
                designation: "",
                employeeId: "",
                salary: "",
                hasStayAllowance: false,
                stayAllowanceCarryForward: false,
                lossOfPay: "",
                casualLeave: "",
                medicalLeave: "",
                compensatoryLeave: "",
                workLocation: "Head Office",
                employmentType: "Full-time",
                joiningDate: "",
                shiftTiming: "09:00 AM - 06:00 PM",
                workingDays: [],
                employeeStatus: "Active",
                autoSendEmail: true,
                panNumber: "",
                uanNumber: "",
                bankDetails: {
                    accountNumber: "",
                    bankName: "",
                    ifscCode: "",
                    accountHolderName: ""
                }
            });
            fetchEmployees();
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to create employee";
            setModalError(msg);
            setErrorMsg(msg);
        } finally {
            setSubmitting(false);
        }
    };

    // ================= UPDATE =================
    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            const rawTeam = form.team === CUSTOM_OPTION_VALUE ? customTeamInput : form.team;
            const rawRole = form.role === CUSTOM_OPTION_VALUE ? customRoleInput : form.role;
            const team = await resolveTeamValue(rawTeam);
            const role = await resolveRoleValue(rawRole);
            const payload = {
                ...form,
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                employeeId: form.employeeId.trim(),
                team,
                role,
                designation: form.designation.trim(),
            };
            const res = await api.patch(
                `/api/admin/employees/${editingEmployee._id}`,
                payload
            );
            setSuccessMsg("Employee updated successfully");
            setEditingEmployee(null);
            resetCustomInputs();

            // Sync with profile popup in real-time
            if (selectedEmployee && selectedEmployee._id === editingEmployee._id) {
                setSelectedEmployee(res.data);
            }

            setForm({
                name: "",
                email: "",
                team: "",
                role: "",
                employeeId: "",
                salary: "",
                hasStayAllowance: false,
                stayAllowanceCarryForward: false,
                lossOfPay: "",
                casualLeave: "",
                medicalLeave: "",
                compensatoryLeave: "",
                workLocation: "Head Office",
                employmentType: "Full-time",
                joiningDate: "",
                shiftTiming: "09:00 AM - 06:00 PM",
                employeeStatus: "Active",
                autoSendEmail: true,
                panNumber: "",
                uanNumber: "",
                bankDetails: {
                    accountNumber: "",
                    bankName: "",
                    ifscCode: "",
                    accountHolderName: ""
                }
            });
            fetchEmployees();
        } catch (err) {
            setErrorMsg(
                err.response?.data?.message || "Failed to update employee"
            );
        } finally {
            setSubmitting(false);
        }
    };

    // ================= EDIT CLICK =================
    const handleEditClick = (employee, e) => {
        if (e) e.stopPropagation();
        setEditingEmployee(employee);
        const teamExists = teams.some((t) => normalizeText(t?.value) === normalizeText(employee.team));
        const roleExists = roles.some((r) => normalizeText(r) === normalizeText(employee.role));
        setCustomTeamInput(teamExists ? "" : (employee.team || ""));
        setCustomRoleInput(roleExists ? "" : (employee.role || ""));
        setForm({
            name: employee.name || "",
            email: employee.email || "",
            team: teamExists ? (employee.team || "") : (employee.team ? CUSTOM_OPTION_VALUE : ""),
            managedTeams: Array.isArray(employee.managedTeams) && employee.managedTeams.length
                ? employee.managedTeams
                : (String(employee.role || "").toLowerCase() === "lead" && employee.team ? [employee.team] : []),
            role: roleExists ? (employee.role || "") : (employee.role ? CUSTOM_OPTION_VALUE : ""),
            designation: employee.designation || employee.role || "",
            employeeId: employee.employeeId || "",
            salary: employee.salary ?? "",
            hasStayAllowance: employee.hasStayAllowance === true,
            selfieEnabled: employee.selfieEnabled !== false,
            stayAllowanceCarryForward: employee.stayAllowanceCarryForward === true,
            lossOfPay: employee.lossOfPay ?? "",
            casualLeave: employee.monthlyCasualLeave ?? employee.casualLeave ?? "",
            medicalLeave: employee.monthlyMedicalLeave ?? employee.medicalLeave ?? "",
            compensatoryLeave: employee.compensatoryLeave ?? "",
            carriedForwardCasualLeave: employee.carriedForwardCasualLeave ?? "",
            carriedForwardMedicalLeave: employee.carriedForwardMedicalLeave ?? "",
            workLocation: employee.workLocation || "Head Office",
            employmentType: employee.employmentType || "Full-time",
            joiningDate: employee.joiningDate ? new Date(employee.joiningDate).toISOString().split('T')[0] : "",
            shiftTiming: employee.shiftTiming || "09:00 AM - 06:00 PM",
            workingDays: Array.isArray(employee.workingDays) ? employee.workingDays : [],
            employeeStatus: employee.employeeStatus || "Active",
            autoSendEmail: true,
            panNumber: employee.panNumber || "",
            uanNumber: employee.uanNumber || "",
            bankDetails: employee.bankDetails || {
                accountNumber: "",
                bankName: "",
                ifscCode: "",
                accountHolderName: ""
            },
            assignedAssets: Array.isArray(employee.assignedAssets) ? employee.assignedAssets : []
        });
    };

    // ================= RESEND =================
    const handleResend = async (id, e) => {
        if (e) e.stopPropagation();
        if (resendingId === id) return;
        setResendingId(id);
        try {
            await api.post(`/api/admin/employees/${id}/resend-invite`);
            setSuccessMsg("Invite resent successfully");
        } catch (err) {
            setErrorMsg("Failed to resend invite");
        } finally {
            setResendingId("");
        }
    };

    // ================= DELETE =================
    const handleDelete = async (id, e) => {
        if (e) e.stopPropagation();
        const confirmDelete = window.confirm("Delete employee?");
        if (!confirmDelete) return;

        try {
            await api.delete(`/api/admin/employees/${id}`);
            setEmployees((prev) => prev.filter((e) => e._id !== id));
            // Close profile modal if the deleted employee was selected
            if (selectedEmployee && selectedEmployee._id === id) {
                setSelectedEmployee(null);
            }
        } catch (err) {
            setErrorMsg("Failed to delete employee");
        }
    };

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <AdminNavbar />

            <div className="flex-1 min-w-0 w-full px-4 md:px-6 py-4 space-y-4">

                {/* ================= ALERTS ================= */}
                {successMsg && (
                    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-4 text-emerald-400">
                        {successMsg}
                    </div>
                )}
                {errorMsg && (
                    <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-5 py-4 text-red-400">
                        {errorMsg}
                    </div>
                )}

                {/* ================= MANAGE TEAMS MODAL ================= */}
                {showManageTeams && (
                    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 sm:p-6">
                        <div
                            onClick={() => setShowManageTeams(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
                        />
                        <div className="relative w-full max-w-lg bg-[var(--card-bg)] dark:bg-[#121214] border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-2xl p-6 z-10">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-50">Manage Teams</h2>
                                <button
                                    type="button"
                                    onClick={() => setShowManageTeams(false)}
                                    className="h-9 w-9 rounded-lg border border-[var(--border-color)] hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all flex items-center justify-center"
                                    title="Close"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            <div className="flex gap-2 mb-4">
                                <input
                                    type="text"
                                    value={newTeamName}
                                    onChange={(e) => setNewTeamName(e.target.value)}
                                    placeholder="New team name"
                                    className="flex-1 h-10 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 outline-none focus:border-[var(--accent-primary)] text-sm"
                                />
                                <button
                                    type="button"
                                    disabled={metaSubmitting}
                                    onClick={async () => {
                                        if (!newTeamName.trim()) return;
                                        try {
                                            setMetaSubmitting(true);
                                            await addTeam(newTeamName);
                                            setNewTeamName("");
                                            setSuccessMsg("Team added");
                                        } catch (err) {
                                            setErrorMsg(err?.response?.data?.message || err.message || "Failed to add team");
                                        } finally {
                                            setMetaSubmitting(false);
                                        }
                                    }}
                                    className="h-10 px-4 rounded-lg bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white font-semibold transition-all text-sm"
                                >
                                    Add
                                </button>
                            </div>

                            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                                {teams.map((t) => (
                                    <div
                                        key={t._id}
                                        className="flex items-center justify-between gap-3 p-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)]"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-semibold text-sm truncate text-neutral-900 dark:text-neutral-50">{t.name}</div>
                                            <div className="text-xs text-[var(--text-secondary)] truncate">{t.value}</div>
                                        </div>
                                        {isMongoId(t._id) ? (
                                            <button
                                                type="button"
                                                disabled={metaSubmitting}
                                                onClick={async () => {
                                                    const ok = window.confirm(`Delete team "${t.name}"?`);
                                                    if (!ok) return;
                                                    try {
                                                        setMetaSubmitting(true);
                                                        await deleteTeam(t._id);
                                                        setSuccessMsg("Team deleted");
                                                    } catch (err) {
                                                        setErrorMsg(err?.response?.data?.message || err.message || "Failed to delete team");
                                                    } finally {
                                                        setMetaSubmitting(false);
                                                    }
                                                }}
                                                className="h-9 px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-all text-xs font-semibold"
                                            >
                                                Delete
                                            </button>
                                        ) : (
                                            <span className="text-[11px] font-semibold text-neutral-400 whitespace-nowrap">Default</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ================= MANAGE ROLES MODAL ================= */}
                {showManageRoles && (
                    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 sm:p-6">
                        <div
                            onClick={() => setShowManageRoles(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
                        />
                        <div className="relative w-full max-w-lg bg-[var(--card-bg)] dark:bg-[#121214] border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-2xl p-6 z-10">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-50">Manage Roles</h2>
                                <button
                                    type="button"
                                    onClick={() => setShowManageRoles(false)}
                                    className="h-9 w-9 rounded-lg border border-[var(--border-color)] hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all flex items-center justify-center"
                                    title="Close"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            <div className="flex gap-2 mb-4">
                                <input
                                    type="text"
                                    value={newRoleName}
                                    onChange={(e) => setNewRoleName(e.target.value)}
                                    placeholder="New role name"
                                    className="flex-1 h-10 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 outline-none focus:border-[var(--accent-primary)] text-sm"
                                />
                                <button
                                    type="button"
                                    disabled={metaSubmitting}
                                    onClick={async () => {
                                        if (!newRoleName.trim()) return;
                                        try {
                                            setMetaSubmitting(true);
                                            await addRole(newRoleName);
                                            setNewRoleName("");
                                            setSuccessMsg("Role added");
                                        } catch (err) {
                                            setErrorMsg(err?.response?.data?.message || err.message || "Failed to add role");
                                        } finally {
                                            setMetaSubmitting(false);
                                        }
                                    }}
                                    className="h-10 px-4 rounded-lg bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white font-semibold transition-all text-sm"
                                >
                                    Add
                                </button>
                            </div>

                            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                                {roles.map((r) => {
                                    const locked = ["admin", "lead"].includes(normalizeText(r));
                                    return (
                                        <div
                                            key={r}
                                            className="flex items-center justify-between gap-3 p-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)]"
                                        >
                                            <div className="min-w-0">
                                                <div className="font-semibold text-sm truncate text-neutral-900 dark:text-neutral-50">
                                                    {formatRoleLabel(r)}
                                                </div>
                                                <div className="text-xs text-[var(--text-secondary)] truncate">{r}</div>
                                            </div>
                                            {locked ? (
                                                <span className="text-[11px] font-semibold text-neutral-400 whitespace-nowrap">Locked</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    disabled={metaSubmitting}
                                                    onClick={async () => {
                                                        const ok = window.confirm(`Delete role "${r}"?`);
                                                        if (!ok) return;
                                                        try {
                                                            setMetaSubmitting(true);
                                                            await deleteRole(r);
                                                            setSuccessMsg("Role deleted");
                                                        } catch (err) {
                                                            setErrorMsg(err?.response?.data?.message || err.message || "Failed to delete role");
                                                        } finally {
                                                            setMetaSubmitting(false);
                                                        }
                                                    }}
                                                    className="h-9 px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-all text-xs font-semibold"
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* ================= STATS GRID ================= */}
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
                    {[
                        { title: "Total Employees", value: serverStats.total, icon: <Users size={20} /> },
                        { title: "Active", value: serverStats.active, icon: <ShieldCheck size={20} /> },
                        { title: "Password Set", value: serverStats.passwordSet, icon: <BadgeCheck size={20} /> },
                        { title: "Pending Invites", value: serverStats.pending, icon: <Clock3 size={20} /> },
                        { title: "Docs Missing", value: serverStats.documentsMissing || 0, icon: <AlertCircle size={20} /> },
                    ].map((item, index) => (
                        <div key={index} className="glass-card rounded-2xl border border-[var(--border-color)] p-4 hover:border-[var(--accent-primary)]/40 transition-all">
                            <div className="w-10 h-10 rounded-xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center mb-3">
                                {item.icon}
                            </div>
                            <h2 className="text-3xl font-black mb-0.5">{item.value}</h2>
                            <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{item.title}</p>
                        </div>
                    ))}

                    <div
                        onClick={() => {
                            setModalError("");
                            setForm({ name: "", email: "", team: "", role: "", designation: "", employeeId: "", salary: "", lossOfPay: "", casualLeave: "", medicalLeave: "", workLocation: "Head Office", employmentType: "Full-time", joiningDate: new Date().toISOString().split('T')[0], shiftTiming: "09:00 AM - 06:00 PM", workingDays: [], employeeStatus: "Active", autoSendEmail: true });
                            resetCustomInputs();
                            setShowAddModal(true);
                        }}
                        className="group glass-card rounded-2xl border border-[var(--border-color)] hover:border-[var(--accent-primary)] p-4 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 hover:shadow-md text-center"
                    >
                        <div className="w-10 h-10 rounded-xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] group-hover:bg-[var(--accent-primary)] group-hover:text-white flex items-center justify-center transition-all duration-300 mb-2">
                            <UserPlus size={20} className="transition-transform duration-300 group-hover:scale-110" />
                        </div>
                        <h3 className="text-sm font-black group-hover:text-[var(--accent-primary)] transition-colors leading-tight">Add Employee</h3>
                        <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">Invite new member</p>
                    </div>
                </div>

                {/* ================= FILTERS ================= */}
                <div className="glass-card rounded-2xl border border-[var(--border-color)] px-4 py-3 flex flex-col xl:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                        <input
                            type="text"
                            placeholder="Search employees..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full h-9 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] pl-9 pr-3 text-sm outline-none focus:border-[var(--accent-primary)]"
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <select value={teamFilter} onChange={handleTeamFilterChange}
                            className="h-9 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm outline-none">
                            <option value="all">All Teams</option>
                            {teams.map(t => (
                                <option key={t._id} value={t.value}>{t.name}</option>
                            ))}
                            <option value="__add_team__">+ Add team…</option>
                        </select>
                        <button
                            type="button"
                            onClick={() => setShowManageTeams(true)}
                            className="h-9 px-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] hover:bg-[var(--accent-primary)]/10 text-xs font-semibold transition-all"
                            title="Manage teams"
                        >
                            Manage
                        </button>
                    </div>

                    <div className="flex items-center gap-2">
                        <select value={roleFilter} onChange={handleRoleFilterChange}
                            className="h-9 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm outline-none">
                            <option value="all">All Roles</option>
                            {roles.map((r) => (
                                <option key={r} value={r}>
                                    {formatRoleLabel(r)}
                                </option>
                            ))}
                            <option value="__add_role__">+ Add role…</option>
                        </select>
                        <button
                            type="button"
                            onClick={() => setShowManageRoles(true)}
                            className="h-9 px-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] hover:bg-[var(--accent-primary)]/10 text-xs font-semibold transition-all"
                            title="Manage roles"
                        >
                            Manage
                        </button>
                    </div>

                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        className="h-9 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm outline-none">
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>

                    <select value={documentFilter} onChange={(e) => {
                        setDocumentFilter(e.target.value);
                        setPage(1);
                    }}
                        className="h-9 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm outline-none">
                        <option value="all">All Documents</option>
                        <option value="missing">Missing Documents</option>
                        <option value="complete">Documents Complete</option>
                    </select>
                </div>

                {/* ================= PAGINATION ================= */}
                <PaginationControls />

                {/* ================= MOBILE CARDS ================= */}
                <div className="space-y-3 xl:hidden">
                    {employees.map((employee) => (
                            <div
                                key={employee._id}
                                onClick={() => setSelectedEmployee(employee)}
                                className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)]"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="w-11 h-11 rounded-xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center font-bold text-sm shrink-0 overflow-hidden border border-[var(--border-color)]">
                                        {employee?.name?.charAt(0)?.toUpperCase()}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <h3 className="font-bold text-sm leading-tight break-words">{employee.name}</h3>
                                                <p className="mt-0.5 text-xs text-[var(--text-secondary)] break-all">{employee.email}</p>
                                            </div>
                                            <span className={`shrink-0 inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold ${
                                                employee.isActive ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-400"
                                            }`}>
                                                {employee.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </div>

                                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                                            <div className="rounded-xl bg-[var(--bg-primary)] px-3 py-2">
                                                <p className="font-black uppercase text-[9px] tracking-wide text-[var(--text-secondary)]">Team</p>
                                                <p className="mt-1 font-semibold capitalize break-words">{employee.team || "-"}</p>
                                            </div>
                                            <div className="rounded-xl bg-[var(--bg-primary)] px-3 py-2">
                                                <p className="font-black uppercase text-[9px] tracking-wide text-[var(--text-secondary)]">Role</p>
                                                <p className="mt-1 font-semibold break-words">{formatRoleLabel(employee.role) || "-"}</p>
                                            </div>
                                            <div className="rounded-xl bg-[var(--bg-primary)] px-3 py-2">
                                                <p className="font-black uppercase text-[9px] tracking-wide text-[var(--text-secondary)]">Employee ID</p>
                                                <p className="mt-1 font-mono font-semibold break-words">{employee.employeeId || "-"}</p>
                                            </div>
                                            <div className="rounded-xl bg-[var(--bg-primary)] px-3 py-2">
                                                <p className="font-black uppercase text-[9px] tracking-wide text-[var(--text-secondary)]">Escalations</p>
                                                <div className="mt-1">
                                                    <EscalationBadge
                                                        employee={employee}
                                                        compact
                                                        onSendMail={handleSendEmployeeEscalationMail}
                                                        sending={escalationMailSendingId === employee._id}
                                                        onClear={handleClearEmployeeEscalation}
                                                        clearing={escalationClearingId === employee._id}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-3 rounded-xl bg-[var(--bg-primary)] px-3 py-2">
                                            <p className="mb-2 font-black uppercase text-[9px] tracking-wide text-[var(--text-secondary)]">Documents</p>
                                            <DocumentComplianceBadge employee={employee} compact />
                                        </div>

                                        <div className="mt-3 grid grid-cols-2 gap-2">
                                            <button
                                                type="button"
                                                onClick={(e) => handleEditClick(employee, e)}
                                                className="h-9 rounded-xl bg-[var(--bg-primary)] hover:bg-[var(--accent-primary)]/10 border border-[var(--border-color)] transition-all flex items-center justify-center gap-1.5 text-xs font-bold"
                                            >
                                                <Edit2 size={13} /> Edit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => openNoticePeriodEditor(employee, e)}
                                                className="h-9 rounded-xl bg-[var(--bg-primary)] hover:bg-[var(--accent-primary)]/10 border border-[var(--border-color)] transition-all flex items-center justify-center gap-1.5 text-xs font-bold"
                                            >
                                                <Clock3 size={13} /> Notice
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => handleDelete(employee._id, e)}
                                                className="h-9 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-all flex items-center justify-center gap-1.5 text-xs font-bold"
                                            >
                                                <Trash2 size={13} /> Delete
                                            </button>
                                            {!employee.isPasswordSet && (
                                                <button
                                                    type="button"
                                                    disabled={resendingId === employee._id}
                                                    onClick={(e) => handleResend(employee._id, e)}
                                                    className="col-span-2 h-9 rounded-xl bg-[var(--bg-primary)] hover:bg-[var(--accent-primary)]/10 border border-[var(--border-color)] transition-all flex items-center justify-center gap-1.5 text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <Mail size={13} className={resendingId === employee._id ? "animate-spin" : ""} />
                                                    {resendingId === employee._id ? "Sending..." : "Resend invite"}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                    ))}
                    {!loading && employees.length === 0 && (
                        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] py-16 text-center text-[var(--text-secondary)]">
                            No employees found
                        </div>
                    )}
                </div>

                {/* ================= TABLE ================= */}
                <div className="ui-table-shell hidden xl:block">
                    <table className="ui-table employees-table table-fixed">
                        <colgroup>
                            <col className="w-[13%]" />
                            <col className="w-[16%]" />
                            <col className="w-[10%]" />
                            <col className="w-[8%]" />
                            <col className="w-[8%]" />
                            <col className="w-[7%]" />
                            <col className="w-[9%]" />
                            <col className="w-[11%]" />
                            <col className="w-[18%]" />
                        </colgroup>
                        <thead className="bg-[var(--bg-primary)]">
                            <tr>
                                {["Employee", "Email", "Team", "Role", "Employee ID", "Status", "Escalations", "Documents", "Actions"].map((head) => (
                                    <th key={head} className="px-3 py-3">
                                        {head}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && employees.length === 0 && Array.from({ length: 6 }, (_, rowIndex) => (
                                <tr key={`employee-loading-${rowIndex}`} aria-hidden="true">
                                    {Array.from({ length: 9 }, (_, cellIndex) => (
                                        <td key={cellIndex} className="px-3 py-4">
                                            <div
                                                className={`h-4 animate-pulse rounded-full bg-neutral-200/80 dark:bg-neutral-700/70 ${
                                                    cellIndex === 0 ? "w-4/5" : cellIndex === 8 ? "w-2/3" : "w-3/4"
                                                }`}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            {employees.map((employee) => (
                                    <tr
                                        key={employee._id}
                                        onClick={() => setSelectedEmployee(employee)}
                                        className="cursor-pointer transition-all hover:bg-[var(--accent-primary)]/5"
                                    >
                                        <td className="px-3 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-lg bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden border border-[var(--border-color)]">
                                                    {employee?.name?.charAt(0)?.toUpperCase()}
                                                </div>
                                                <div className="min-w-0">
                                                    <h3 className="font-semibold text-sm truncate">{employee.name}</h3>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-4 text-xs text-[var(--text-secondary)]">
                                            <p className="truncate" title={employee.email}>{employee.email || "-"}</p>
                                        </td>
                                        <td className="px-3 py-4 text-xs font-semibold capitalize break-words">{employee.team || "-"}</td>
                                        <td className="px-3 py-4 text-xs font-semibold break-words">{formatRoleLabel(employee.role) || "-"}</td>
                                        <td className="px-3 py-4 text-xs font-mono">{employee.employeeId || "-"}</td>
                                        <td className="px-3 py-4">
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold ${
                                                employee.isActive
                                                    ? "bg-emerald-500/15 text-emerald-500"
                                                    : "bg-red-500/15 text-red-400"
                                            }`}>
                                                {employee.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-4">
                                            <EscalationBadge
                                                employee={employee}
                                                summaryOnly
                                                onSendMail={handleSendEmployeeEscalationMail}
                                                sending={escalationMailSendingId === employee._id}
                                                onClear={handleClearEmployeeEscalation}
                                                clearing={escalationClearingId === employee._id}
                                            />
                                        </td>
                                        <td className="px-3 py-4">
                                            <DocumentComplianceBadge employee={employee} summaryOnly />
                                        </td>
                                        <td className="px-3 py-4">
                                            <div className="ui-action-row justify-center">
                                                <button onClick={(e) => handleEditClick(employee, e)}
                                                    className="ui-button ui-button-secondary h-8 w-8 min-w-0 p-0" title="Edit employee" aria-label={`Edit ${employee.name}`}>
                                                    <Edit2 size={14} />
                                                </button>
                                                <button onClick={(e) => openNoticePeriodEditor(employee, e)}
                                                    className="ui-button ui-button-secondary h-8 w-8 min-w-0 p-0" title="Manage notice period" aria-label={`Manage notice period for ${employee.name}`}>
                                                    <Clock3 size={14} />
                                                </button>
                                                {!employee.isPasswordSet && (
                                                    <button onClick={(e) => handleResend(employee._id, e)}
                                                        disabled={resendingId === employee._id}
                                                        className="ui-button ui-button-secondary h-8 w-8 min-w-0 p-0 disabled:opacity-50 disabled:cursor-not-allowed" title="Resend invite" aria-label={`Resend invite to ${employee.name}`}>
                                                        <Mail size={14} className={resendingId === employee._id ? "animate-spin" : ""} />
                                                    </button>
                                                )}
                                                <button onClick={(e) => handleDelete(employee._id, e)}
                                                    className="ui-button ui-button-danger h-8 w-8 min-w-0 p-0" title="Delete employee" aria-label={`Delete ${employee.name}`}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                            ))}
                        </tbody>
                    </table>
                    {!loading && employees.length === 0 && (
                        <div className="py-20 text-center text-[var(--text-secondary)]">
                            <p>{errorMsg ? "Employees could not be loaded." : "No employees found"}</p>
                            {errorMsg && (
                                <button
                                    type="button"
                                    onClick={() => fetchEmployees()}
                                    className="ui-button ui-button-primary mt-4"
                                >
                                    Retry
                                </button>
                            )}
                        </div>
                    )}
                </div>

            </div>

            {/* ================= SELECTED EMPLOYEE PROFILE DETAILS MODAL (VIEW POPUP) ================= */}
            {selectedEmployee && !editingEmployee && (
                <div className="fixed inset-0 z-[150] flex items-end justify-center p-0 sm:items-center sm:p-4">
                    {/* Backdrop */}
                    <div
                        onClick={() => setSelectedEmployee(null)}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
                    />

                    {/* Modal Box */}
                    <div className="relative z-10 flex max-h-[100dvh] w-full flex-col overflow-y-auto rounded-t-3xl border border-neutral-200 bg-[var(--card-bg)] p-4 shadow-2xl transition-transform duration-300 animate-fade-in sm:max-h-[92vh] sm:max-w-3xl sm:rounded-2xl sm:p-6 lg:p-7 dark:border-neutral-800 dark:bg-[#121214]">

                        {/* Floating Close Button Outside */}
                        <button
                            onClick={() => setSelectedEmployee(null)}
                            className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 bg-[var(--card-bg)] text-neutral-500 shadow-md transition hover:scale-105 hover:bg-neutral-100 sm:right-5 sm:top-5 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800"
                            title="Close"
                        >
                            <X size={22} />
                        </button>

                        {/* Header Details */}
                        <div className="mb-5 flex items-center justify-between gap-3 border-b border-neutral-100 pb-5 pr-11 dark:border-neutral-800 sm:mb-6 sm:pb-6">
                            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--accent-primary)]/10 text-xl font-bold text-[var(--accent-primary)] sm:h-16 sm:w-16 sm:text-2xl">
                                        {selectedEmployee?.name?.charAt(0)?.toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h2 className="min-w-0 text-lg font-bold leading-tight text-neutral-900 dark:text-neutral-50 sm:text-xl">{selectedEmployee.name}</h2>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${selectedEmployee.isActive ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"
                                            }`}>
                                            {selectedEmployee.isActive ? "Active" : "Inactive"}
                                        </span>
                                        <EscalationBadge
                                            employee={selectedEmployee}
                                            align="right"
                                            onSendMail={handleSendEmployeeEscalationMail}
                                            sending={escalationMailSendingId === selectedEmployee._id}
                                            onClear={handleClearEmployeeEscalation}
                                            clearing={escalationClearingId === selectedEmployee._id}
                                        />
                                    </div>
                                    <p className="text-sm text-neutral-400 dark:text-neutral-500 capitalize mt-1">
                                        {formatRoleLabel(selectedEmployee.role)} • {selectedEmployee.team}
                                    </p>
                                </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                <button
                                    type="button"
                                    onClick={(event) => handleEditClick(selectedEmployee, event)}
                                    className="inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-lg bg-[var(--accent-primary)] text-xs font-semibold text-white shadow-sm transition hover:bg-[var(--accent-hover)] sm:w-auto sm:px-3.5"
                                    title="Edit employee"
                                >
                                    <Edit2 size={13} />
                                    <span className="hidden sm:inline">Edit Employee</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={(event) => openNoticePeriodEditor(selectedEmployee, event)}
                                    className="inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-lg bg-neutral-900 text-xs font-semibold text-white shadow-sm transition hover:bg-neutral-800 sm:w-auto sm:px-3.5 dark:bg-neutral-100 dark:text-neutral-950 dark:hover:bg-[var(--card-bg)]"
                                    title="Notice period"
                                >
                                    <Clock3 size={13} />
                                    <span className="hidden sm:inline">Notice Period</span>
                                </button>
                            </div>
                        </div>

                        <nav className="mb-6 grid grid-cols-2 gap-1 rounded-xl border border-neutral-200 bg-neutral-50 p-1 sm:grid-cols-5 dark:border-neutral-800 dark:bg-neutral-900/50" aria-label="Employee detail pages">
                            {EMPLOYEE_DETAIL_PAGES.map((tab, index) => {
                                const active = selectedEmployeeTab === tab.key;
                                return (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        onClick={() => setSelectedEmployeeTab(tab.key)}
                                        className={`flex min-w-0 items-center justify-center gap-2 rounded-lg px-2 py-2.5 text-xs font-semibold transition ${
                                            active
                                                ? "bg-[var(--card-bg)] text-neutral-900 shadow-sm ring-1 ring-neutral-200 dark:bg-neutral-800 dark:text-white dark:ring-neutral-700"
                                                : "text-neutral-500 hover:bg-[var(--card-bg)]/70 hover:text-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-800/70 dark:hover:text-neutral-100"
                                        }`}
                                        aria-current={active ? "page" : undefined}
                                    >
                                        <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-bold ${active ? "bg-[var(--accent-primary)] text-white" : "bg-neutral-200 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-300"}`}>
                                            {index + 1}
                                        </span>
                                        <span className="truncate">{tab.label}</span>
                                    </button>
                                );
                            })}
                        </nav>

                        {selectedEmployeeTab === "overview" && (
                        <>
                        {/* Leave & Salary Dashboard Cards */}
                        <div className="mb-6">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase">Workspace Dashboard</h3>
                                <button
                                    onClick={() => setShowAdminSalarySlip(true)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white text-xs font-bold rounded-lg transition-all"
                                >
                                    <FileText size={12} />
                                    Generate Salary Slip
                                </button>
                            </div>
                            <div className="grid gap-3 md:grid-cols-[150px_minmax(0,1fr)]">
                                <LeaveDashboardCard
                                    label="Salary"
                                    value={`₹${selectedEmployee.salary || 0}`}
                                    tone="text-[var(--accent-primary)]"
                                />
                                <LeaveUsageDetail employee={selectedEmployee} />
                            </div>
                        </div>

                        {selectedEmployee.noticePeriod?.status === "active" && (
                            <div className="mb-6 rounded-xl border border-amber-500/20 bg-amber-500/10 p-4">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-wider text-amber-600 dark:text-amber-300">Notice Period Active</p>
                                        <h3 className="mt-1 text-sm font-black text-neutral-900 dark:text-neutral-50">
                                            {selectedEmployee.noticePeriod.durationValue} {selectedEmployee.noticePeriod.durationUnit}
                                        </h3>
                                        <p className="mt-1 text-xs font-semibold text-neutral-600 dark:text-neutral-300">
                                            From {new Date(selectedEmployee.noticePeriod.effectiveDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                            {" "}to {new Date(selectedEmployee.noticePeriod.endDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => openNoticePeriodEditor(selectedEmployee, e)}
                                        className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-[var(--card-bg)]/80 px-3 text-xs font-bold text-neutral-800 shadow-sm transition hover:bg-[var(--card-bg)] dark:bg-neutral-900/70 dark:text-neutral-100"
                                    >
                                        <Clock3 size={13} /> Edit notice
                                    </button>
                                </div>
                            </div>
                        )}
                        </>
                        )}

                        {selectedEmployeeTab === "leaves" && (
                            <div className="mb-6">
                                <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                                    <div>
                                        <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-100">Leave application history</h3>
                                        <p className="mt-1 text-xs font-medium text-neutral-500 dark:text-neutral-400">Review submitted requests and administrative decisions.</p>
                                    </div>
                                    <span className="w-fit rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-neutral-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400">
                                        {selectedEmployeeLeaves.length} record{selectedEmployeeLeaves.length === 1 ? "" : "s"}
                                    </span>
                                </div>
                                <EmployeeLeaveBalanceSummary
                                    employee={selectedEmployee}
                                    leaves={selectedEmployeeLeaves}
                                    attendance={selectedEmployeeAttendance}
                                    holidays={publicHolidays}
                                />
                                <EmployeeLeaveHistory
                                    leaves={selectedEmployeeLeaves}
                                    loading={selectedEmployeeLeavesState.loading}
                                    error={selectedEmployeeLeavesState.error}
                                />
                            </div>
                        )}

                        {/* Leave & Attendance Calendar Tracker */}
                        {selectedEmployeeTab === "attendance" && (
                        <div className="mb-6">
                            <h3 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase mb-3">Corporate Leave & Calendar Tracker</h3>
                            <EmployeeLeaveCalendar 
                                employee={selectedEmployee} 
                                leaves={selectedEmployeeLeaves} 
                                attendance={selectedEmployeeAttendance} 
                                holidays={publicHolidays}
                                onAttendanceSaved={(record, updatedEmployee) => {
                                    if (updatedEmployee?._id) {
                                        setSelectedEmployee((current) => current?._id === updatedEmployee._id
                                            ? { ...current, ...updatedEmployee }
                                            : current);
                                    }
                                    setSelectedEmployeeAttendance((current) => {
                                        const recordEmployeeId = String(record?.employee?._id || record?.employee || selectedEmployee?._id || "");
                                        return [
                                            record,
                                            ...current.filter((item) => {
                                                const itemEmployeeId = String(item?.employee?._id || item?.employee || selectedEmployee?._id || "");
                                                const sameRecord = item?._id && record?._id && item._id === record._id;
                                                const sameEmployeeDate = itemEmployeeId === recordEmployeeId && item?.date === record?.date;
                                                return !sameRecord && !sameEmployeeDate;
                                            }),
                                        ];
                                    });
                                }}
                            />
                        </div>
                        )}

                        {selectedEmployeeTab === "timeline" && (
                        <div className="mb-6">
                            <div className="mb-3 flex items-center justify-between gap-3">
                                <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">Employee Timeline</h3>
                                <span className="rounded-full bg-neutral-100 px-3 py-1 text-[10px] font-black uppercase text-neutral-500 dark:bg-neutral-800 dark:text-neutral-300">
                                    {selectedEmployeeTimeline.items.length} recent item(s)
                                </span>
                            </div>
                            <div className="max-h-80 overflow-y-auto rounded-xl border border-neutral-100 bg-neutral-50/30 p-3 dark:border-neutral-800 dark:bg-neutral-900/20">
                                {selectedEmployeeTimeline.loading ? (
                                    <p className="px-2 py-6 text-center text-sm font-semibold text-neutral-500">Loading employee timeline...</p>
                                ) : selectedEmployeeTimeline.items.length ? (
                                    <div className="space-y-2">
                                        {selectedEmployeeTimeline.items.slice(0, 18).map((item) => (
                                            <div key={`${item.type}-${item.refId}`} className="rounded-lg border border-neutral-100 bg-[var(--card-bg)] px-3 py-2 dark:border-neutral-800 dark:bg-neutral-950/50">
                                                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-black text-neutral-900 dark:text-neutral-100">{item.title}</p>
                                                        <p className="mt-0.5 truncate text-xs font-semibold text-neutral-500 dark:text-neutral-400">{item.detail || item.periodKey || item.type}</p>
                                                    </div>
                                                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                                                        <span className="rounded-full bg-[var(--accent-primary)]/10 px-2 py-0.5 text-[10px] font-black uppercase text-[var(--accent-primary)]">{item.type}</span>
                                                        {item.status && <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-black uppercase text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{item.status}</span>}
                                                        <span className="text-[10px] font-bold text-neutral-400">
                                                            {item.date ? new Date(item.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="px-2 py-6 text-center text-sm font-semibold text-neutral-500">No timeline activity found for this employee.</p>
                                )}
                            </div>
                        </div>
                        )}

                        {/* Personal & Personal Details Info Panels */}
                        {selectedEmployeeTab === "profile" && (
                        <div className="space-y-6">
                            {/* Contact Details Panel */}
                            <div>
                                <h3 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase mb-3">Contact Information</h3>
                                <div className="p-4 rounded-xl border border-neutral-100 dark:border-neutral-800 bg-neutral-50/20 dark:bg-neutral-900/20 space-y-3">
                                    <div className="flex items-center gap-3">
                                        <Mail size={15} className="text-[var(--accent-primary)] shrink-0" />
                                        <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">{selectedEmployee.email}</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Phone size={15} className="text-[var(--accent-primary)] shrink-0" />
                                        <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">{selectedEmployee.phone || "No phone provided"}</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <MapPin size={15} className="text-[var(--accent-primary)] shrink-0 mt-0.5" />
                                        <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 leading-normal">{selectedEmployee.address || "No address provided"}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Information Panel */}
                            <div>
                                <h3 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase mb-3">Regulatory & Personal Info</h3>
                                <div className="p-4 rounded-xl border border-neutral-100 dark:border-neutral-800 bg-neutral-50/20 dark:bg-neutral-900/20 grid grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">Employee ID</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.employeeId}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">Gender</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.gender || "Not specified"}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">PAN Number</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.panNumber || "Not configured"}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">Aadhar Number</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.aadharNumber || "Not configured"}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">UAN Number</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.uanNumber || "Not configured"}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">ESI Number</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.esiNumber || "Not configured"}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Employment & Logistics Panel */}
                            <div>
                                <h3 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase mb-3">Employment & Logistics</h3>
                                <div className="p-4 rounded-xl border border-neutral-100 dark:border-neutral-800 bg-neutral-50/20 dark:bg-neutral-900/20 grid grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">Work Location</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.workLocation || "Not configured"}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">Employment Type</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.employmentType || "Full-time"}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">Joining Date</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">
                                            {selectedEmployee.joiningDate 
                                                ? new Date(selectedEmployee.joiningDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                                                : "Not specified"}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">Shift Timing</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.shiftTiming || "Not configured"}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">Working Days</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{formatWorkingDays(selectedEmployee.workingDays)}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">Employee Status</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.employeeStatus || "Active"}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Bank & Payment Information */}
                            <div>
                                <h3 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase mb-3">Bank & Payment Details</h3>
                                <div className="p-4 rounded-xl border border-neutral-100 dark:border-neutral-800 bg-neutral-50/20 dark:bg-neutral-900/20 grid grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">Account Holder Name</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.bankDetails?.accountHolderName || "Not configured"}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">Bank Name</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.bankDetails?.bankName || "Not configured"}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">Account Number</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.bankDetails?.accountNumber || "Not configured"}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase font-bold">IFSC Code</h4>
                                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-0.5">{selectedEmployee.bankDetails?.ifscCode || "Not configured"}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Documents Panel */}
                            <div>
                                <h3 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase mb-3">Uploaded Documents</h3>
                                <div className="space-y-3">
                                    {[
                                        { key: "offerLetter", label: "Offer Letter", value: selectedEmployee.offerLetter, name: selectedEmployee.offerLetterName || "Offer_Letter.pdf", editor: openOfferLetterEditor, tone: "amber" },
                                        { key: "internshipLetter", label: "Internship Letter", value: selectedEmployee.internshipLetter, name: selectedEmployee.internshipLetterName || "Internship_Letter.pdf", editor: openInternshipLetterEditor, tone: "emerald" },
                                        { key: "experienceLetter", label: "Experience Letter", value: selectedEmployee.experienceLetter, name: selectedEmployee.experienceLetterName || "Experience_Letter.pdf", editor: openExperienceLetterEditor, tone: "sky" },
                                        { key: "relievingLetter", label: "Relieving Letter", value: selectedEmployee.relievingLetter, name: selectedEmployee.relievingLetterName || "Relieving_Letter.pdf", editor: openRelievingLetterEditor, tone: "violet" },
                                    ].map((doc) => (
                                        <div key={doc.key} className="p-4 rounded-xl border border-neutral-100 dark:border-neutral-800 bg-neutral-50/20 dark:bg-neutral-900/20 flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <FileText size={18} className="text-[var(--accent-primary)] shrink-0" />
                                                <div className="min-w-0">
                                                    <h4 className="text-xs font-bold text-neutral-700 dark:text-neutral-300">{doc.label}</h4>
                                                    <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-0.5 truncate">
                                                        {doc.value ? doc.name : "Not generated yet"}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => doc.editor(selectedEmployee)}
                                                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg border text-xs font-semibold transition-all cursor-pointer ${
                                                        doc.tone === "amber"
                                                            ? "border-amber-300/70 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
                                                            : doc.tone === "emerald"
                                                                ? "border-emerald-300/70 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
                                                                : doc.tone === "sky"
                                                                ? "border-sky-300/70 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300"
                                                                : "border-violet-300/70 bg-[var(--accent-light)] text-[var(--heading-secondary)] hover:bg-[var(--accent-soft)] dark:border-violet-500/30 dark:bg-[var(--accent-subtle)] dark:text-[var(--heading-secondary)]"
                                                    }`}
                                                >
                                                    <Edit2 size={13} />
                                                    {doc.value ? "Edit Letter" : "Generate"}
                                                </button>
                                                {doc.value && (
                                                    <>
                                                        <button
                                                            onClick={() => handleViewFile(doc.value)}
                                                            className="px-3.5 py-1.5 rounded-lg border border-neutral-200 dark:border-neutral-700 text-xs font-semibold hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300 transition-all cursor-pointer"
                                                        >
                                                            View File
                                                        </button>
                                                        <a
                                                            href={doc.value}
                                                            download={doc.name}
                                                            className="px-3.5 py-1.5 rounded-lg bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white text-xs font-semibold transition-all cursor-pointer shadow-sm"
                                                        >
                                                            Download
                                                        </a>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleGeneratedLetterDelete(selectedEmployee, doc.key)}
                                                            className="px-3.5 py-1.5 rounded-lg border border-red-200 bg-red-50 text-xs font-semibold text-red-600 hover:bg-red-100 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300 transition-all cursor-pointer"
                                                        >
                                                            Delete
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Employee Uploaded Documents Panel */}
                            <div>
                                <h3 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase mb-3 mt-4">Employee Uploaded Documents</h3>
                                <div className="space-y-3">
                                    {[
                                        { key: "signedOfferLetter", label: "Signed Offer Letter", value: selectedEmployee.signedOfferLetter, name: selectedEmployee.signedOfferLetterName || "Signed_Offer_Letter.pdf" },
                                        { key: "bankPassbook", label: "Bank Passbook Photo", value: selectedEmployee.bankPassbook, name: selectedEmployee.bankPassbookName || "Bank_Passbook.pdf" },
                                        { key: "aadharCard", label: "Aadhar Card Upload", value: selectedEmployee.aadharCard, name: selectedEmployee.aadharCardName || "Aadhar_Card.pdf" },
                                        { key: "idCardPhoto", label: "ID Card Photo", value: selectedEmployee.idCardPhoto, name: selectedEmployee.idCardPhotoName || "ID_Card_Photo.png" },
                                    ].map((doc) => (
                                        <div key={doc.key} className="p-4 rounded-xl border border-neutral-100 dark:border-neutral-800 bg-neutral-50/20 dark:bg-neutral-900/20 flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <FileText size={18} className="text-emerald-500 shrink-0" />
                                                <div className="min-w-0">
                                                    <h4 className="text-xs font-bold text-neutral-700 dark:text-neutral-300">{doc.label}</h4>
                                                    <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-0.5 truncate">
                                                        {doc.value ? doc.name : "Not uploaded yet"}
                                                    </p>
                                                </div>
                                            </div>
                                            {doc.value && (
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleViewFile(doc.value)}
                                                        className="px-3.5 py-1.5 rounded-lg border border-neutral-200 dark:border-neutral-700 text-xs font-semibold hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300 transition-all cursor-pointer"
                                                    >
                                                        View File
                                                    </button>
                                                    <a
                                                        href={doc.value}
                                                        download={doc.name}
                                                        className="px-3.5 py-1.5 rounded-lg bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white text-xs font-semibold transition-all cursor-pointer shadow-sm"
                                                    >
                                                        Download
                                                    </a>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleUploadedDocumentDelete(selectedEmployee, doc)}
                                                        className="px-3.5 py-1.5 rounded-lg border border-red-200 bg-red-50 text-xs font-semibold text-red-600 hover:bg-red-100 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300 transition-all cursor-pointer"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        )}

                        <div className="mt-4 flex flex-col gap-3 border-t border-neutral-200 pt-4 sm:flex-row sm:items-center sm:justify-between dark:border-neutral-800">
                            {(() => {
                                const currentPageIndex = EMPLOYEE_DETAIL_PAGES.findIndex((pageItem) => pageItem.key === selectedEmployeeTab);
                                const currentPage = EMPLOYEE_DETAIL_PAGES[currentPageIndex];
                                return (
                                    <>
                                        <button
                                            type="button"
                                            disabled={currentPageIndex === 0}
                                            onClick={() => setSelectedEmployeeTab(EMPLOYEE_DETAIL_PAGES[currentPageIndex - 1].key)}
                                            className="rounded-lg border border-neutral-200 bg-[var(--card-bg)] px-4 py-2 text-xs font-semibold text-neutral-600 transition hover:border-neutral-300 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800"
                                        >
                                            ← Previous page
                                        </button>
                                        <div className="text-center">
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">
                                                Page {currentPageIndex + 1} of {EMPLOYEE_DETAIL_PAGES.length}
                                            </p>
                                            <p className="text-sm font-bold text-neutral-800 dark:text-neutral-100">{currentPage.label}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedEmployeeTab(EMPLOYEE_DETAIL_PAGES[currentPageIndex + 1].key)}
                                            disabled={currentPageIndex === EMPLOYEE_DETAIL_PAGES.length - 1}
                                            className="rounded-lg bg-neutral-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-[var(--card-bg)]"
                                        >
                                            Next page →
                                        </button>
                                    </>
                                );
                            })()}
                        </div>

                    </div>
                </div>
            )}

            {noticePeriodOpen && selectedEmployee && (
                <div className="fixed inset-0 z-[230] flex items-center justify-center p-4 sm:p-6">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !noticePeriodSaving && setNoticePeriodOpen(false)} />
                    <form onSubmit={handleNoticePeriodSubmit} className="relative w-full max-w-xl rounded-2xl border border-neutral-200 bg-[var(--card-bg)] p-5 shadow-2xl dark:border-neutral-800 dark:bg-[#121214]">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-[var(--accent-primary)]">Employee email</p>
                                <h3 className="text-xl font-black text-neutral-900 dark:text-neutral-50">Issue notice period</h3>
                                <p className="mt-1 text-xs font-semibold text-neutral-500">To: {selectedEmployee.email}</p>
                            </div>
                            <button type="button" disabled={noticePeriodSaving} onClick={() => setNoticePeriodOpen(false)} className="h-9 w-9 rounded-xl bg-neutral-100 text-neutral-500 transition hover:bg-neutral-200 disabled:opacity-60 dark:bg-neutral-800 dark:hover:bg-neutral-700" title="Close">
                                <X size={17} className="mx-auto" />
                            </button>
                        </div>
                        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Duration</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={noticePeriodForm.durationValue}
                                    onChange={(e) => setNoticePeriodForm((current) => ({ ...current, durationValue: e.target.value }))}
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Unit</label>
                                <select
                                    value={noticePeriodForm.durationUnit}
                                    onChange={(e) => setNoticePeriodForm((current) => ({ ...current, durationUnit: e.target.value }))}
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-neutral-800 dark:text-neutral-200"
                                >
                                    <option value="days">Days</option>
                                    <option value="months">Months</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Effective date</label>
                                <input
                                    type="date"
                                    value={noticePeriodForm.effectiveDate}
                                    onChange={(e) => setNoticePeriodForm((current) => ({ ...current, effectiveDate: e.target.value }))}
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Reason</label>
                                <input
                                    value={noticePeriodForm.reason}
                                    onChange={(e) => setNoticePeriodForm((current) => ({ ...current, reason: e.target.value }))}
                                    placeholder="Optional"
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]"
                                />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Additional notes</label>
                                <textarea
                                    rows={4}
                                    value={noticePeriodForm.notes}
                                    onChange={(e) => setNoticePeriodForm((current) => ({ ...current, notes: e.target.value }))}
                                    placeholder="Optional handover or HR instructions"
                                    className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]"
                                />
                            </div>
                        </div>
                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <button type="button" disabled={noticePeriodSaving} onClick={() => setNoticePeriodOpen(false)} className="rounded-lg bg-neutral-100 px-5 py-2.5 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-200 disabled:opacity-60 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700">Cancel</button>
                            <button type="submit" disabled={noticePeriodSaving} className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--accent-primary)] px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-[var(--accent-hover)] disabled:opacity-60">
                                {noticePeriodSaving && <Loader2 size={16} className="animate-spin" />}
                                {noticePeriodSaving ? "Sending..." : "Send Notice Email"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* ================= SALARY SLIP POPUP MODAL ================= */}
            <SalarySlipModal
                open={showAdminSalarySlip}
                onClose={() => setShowAdminSalarySlip(false)}
                employee={selectedEmployee}
            />

            {offerLetterOpen && selectedEmployee && (
                <div className="fixed inset-0 z-[220] flex items-center justify-center p-4 sm:p-6">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !offerLetterSaving && setOfferLetterOpen(false)} />
                    <form onSubmit={handleOfferLetterSubmit} className="relative w-full max-w-xl rounded-2xl border border-neutral-200 bg-[var(--card-bg)] p-5 shadow-2xl dark:border-neutral-800 dark:bg-[#121214]">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-[var(--accent-primary)]">Employee documents</p>
                                <h3 className="text-xl font-black text-neutral-900 dark:text-neutral-50">{selectedEmployee.offerLetter ? "Edit offer letter" : "Generate offer letter"}</h3>
                            </div>
                            <button type="button" disabled={offerLetterSaving} onClick={() => setOfferLetterOpen(false)} className="h-9 w-9 rounded-xl bg-neutral-100 text-neutral-500 transition hover:bg-neutral-200 disabled:opacity-60 dark:bg-neutral-800 dark:hover:bg-neutral-700" title="Close">
                                <X size={17} className="mx-auto" />
                            </button>
                        </div>
                        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Employee name</label>
                                <input value={offerLetterForm.name} onChange={(e) => setOfferLetterForm((current) => ({ ...current, name: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Joining date</label>
                                <input type="date" value={offerLetterForm.joiningDate} onChange={(e) => setOfferLetterForm((current) => ({ ...current, joiningDate: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Monthly salary</label>
                                <input value={offerLetterForm.salary} onChange={(e) => setOfferLetterForm((current) => ({ ...current, salary: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Designation</label>
                                <input value={offerLetterForm.position} onChange={(e) => setOfferLetterForm((current) => ({ ...current, position: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Authorized Signature *</label>
                                <select 
                                    value={offerLetterForm.signatureId} 
                                    onChange={(e) => setOfferLetterForm((current) => ({ ...current, signatureId: e.target.value }))} 
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-neutral-800 dark:text-neutral-200 cursor-pointer"
                                    required
                                >
                                    <option value="">Select Signature</option>
                                    {signatures.map((sig) => (
                                        <option key={sig.id} value={sig.id}>
                                            {sig.name} ({sig.position})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <button type="button" disabled={offerLetterSaving} onClick={() => setOfferLetterOpen(false)} className="rounded-lg bg-neutral-100 px-5 py-2.5 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-200 disabled:opacity-60 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700">Cancel</button>
                            <button type="submit" disabled={offerLetterSaving} className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--accent-primary)] px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-[var(--accent-hover)] disabled:opacity-60">
                                {offerLetterSaving && <Loader2 size={16} className="animate-spin" />}
                                {offerLetterSaving ? "Saving..." : selectedEmployee.offerLetter ? "Update Letter" : "Generate Letter"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {experienceLetterOpen && selectedEmployee && (
                <div className="fixed inset-0 z-[220] flex items-center justify-center p-4 sm:p-6">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !experienceLetterSaving && setExperienceLetterOpen(false)} />
                    <form onSubmit={handleExperienceLetterSubmit} className="relative w-full max-w-xl rounded-2xl border border-neutral-200 bg-[var(--card-bg)] p-5 shadow-2xl dark:border-neutral-800 dark:bg-[#121214]">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-[var(--accent-primary)]">Employee documents</p>
                                <h3 className="text-xl font-black text-neutral-900 dark:text-neutral-50">{selectedEmployee.experienceLetter ? "Edit experience letter" : "Generate experience letter"}</h3>
                            </div>
                            <button type="button" disabled={experienceLetterSaving} onClick={() => setExperienceLetterOpen(false)} className="h-9 w-9 rounded-xl bg-neutral-100 text-neutral-500 transition hover:bg-neutral-200 disabled:opacity-60 dark:bg-neutral-800 dark:hover:bg-neutral-700" title="Close">
                                <X size={17} className="mx-auto" />
                            </button>
                        </div>
                        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Employee name</label>
                                <input 
                                    value={experienceLetterForm.name} 
                                    onChange={(e) => {
                                        const newName = e.target.value;
                                        setExperienceLetterForm((current) => ({ 
                                            ...current, 
                                            name: newName,
                                            address: `${stripSalutation(newName)}\nAVIATRICKS AERO LABS PRIVATE LIMITED`
                                        }));
                                    }} 
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" 
                                />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">To Address</label>
                                <textarea 
                                    rows={3} 
                                    value={experienceLetterForm.address} 
                                    onChange={(e) => setExperienceLetterForm((current) => ({ ...current, address: e.target.value }))} 
                                    className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" 
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Phone</label>
                                <input value={experienceLetterForm.phone} onChange={(e) => setExperienceLetterForm((current) => ({ ...current, phone: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Email</label>
                                <input value={experienceLetterForm.email} onChange={(e) => setExperienceLetterForm((current) => ({ ...current, email: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Letter date</label>
                                <input type="date" value={experienceLetterForm.date} onChange={(e) => setExperienceLetterForm((current) => ({ ...current, date: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Letter wording</label>
                                <input
                                    list="experience-letter-wording-options"
                                    value={experienceLetterForm.letterType}
                                    onChange={(e) => setExperienceLetterForm((current) => ({ ...current, letterType: e.target.value }))}
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]"
                                    required
                                />
                                <datalist id="experience-letter-wording-options">
                                    <option value="internship" />
                                    <option value="work experience" />
                                </datalist>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Designation</label>
                                <input value={experienceLetterForm.position} onChange={(e) => setExperienceLetterForm((current) => ({ ...current, position: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">From date</label>
                                <input type="date" value={experienceLetterForm.fromDate} onChange={(e) => setExperienceLetterForm((current) => ({ ...current, fromDate: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">To date</label>
                                <input type="date" value={experienceLetterForm.toDate} onChange={(e) => setExperienceLetterForm((current) => ({ ...current, toDate: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Authorized Signature *</label>
                                <select 
                                    value={experienceLetterForm.signatureId} 
                                    onChange={(e) => setExperienceLetterForm((current) => ({ ...current, signatureId: e.target.value }))} 
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-neutral-800 dark:text-neutral-200 cursor-pointer"
                                    required
                                >
                                    <option value="">Select Signature</option>
                                    {signatures.map((sig) => (
                                        <option key={sig.id} value={sig.id}>
                                            {sig.name} ({sig.position})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <button type="button" disabled={experienceLetterSaving} onClick={() => setExperienceLetterOpen(false)} className="rounded-lg bg-neutral-100 px-5 py-2.5 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-200 disabled:opacity-60 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700">Cancel</button>
                            <button type="submit" disabled={experienceLetterSaving} className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--accent-primary)] px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-[var(--accent-hover)] disabled:opacity-60">
                                {experienceLetterSaving && <Loader2 size={16} className="animate-spin" />}
                                {experienceLetterSaving ? "Saving..." : selectedEmployee.experienceLetter ? "Update Letter" : "Generate Letter"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {relievingLetterOpen && selectedEmployee && (
                <div className="fixed inset-0 z-[220] flex items-center justify-center p-4 sm:p-6">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !relievingLetterSaving && setRelievingLetterOpen(false)} />
                    <form onSubmit={handleRelievingLetterSubmit} className="relative w-full max-w-xl rounded-2xl border border-neutral-200 bg-[var(--card-bg)] p-5 shadow-2xl dark:border-neutral-800 dark:bg-[#121214]">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-[var(--accent-primary)]">Employee documents</p>
                                <h3 className="text-xl font-black text-neutral-900 dark:text-neutral-50">{selectedEmployee.relievingLetter ? "Edit relieving letter" : "Generate relieving letter"}</h3>
                            </div>
                            <button type="button" disabled={relievingLetterSaving} onClick={() => setRelievingLetterOpen(false)} className="h-9 w-9 rounded-xl bg-neutral-100 text-neutral-500 transition hover:bg-neutral-200 disabled:opacity-60 dark:bg-neutral-800 dark:hover:bg-neutral-700" title="Close">
                                <X size={17} className="mx-auto" />
                            </button>
                        </div>
                        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Employee name</label>
                                <input value={relievingLetterForm.name} onChange={(e) => setRelievingLetterForm((current) => ({ ...current, name: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Employee ID</label>
                                <input value={relievingLetterForm.employeeId} onChange={(e) => setRelievingLetterForm((current) => ({ ...current, employeeId: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Location</label>
                                <input value={relievingLetterForm.location} onChange={(e) => setRelievingLetterForm((current) => ({ ...current, location: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Designation</label>
                                <input value={relievingLetterForm.position} onChange={(e) => setRelievingLetterForm((current) => ({ ...current, position: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Letter date</label>
                                <input type="date" value={relievingLetterForm.date} onChange={(e) => setRelievingLetterForm((current) => ({ ...current, date: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Resignation date</label>
                                <input type="date" value={relievingLetterForm.resignationDate} onChange={(e) => setRelievingLetterForm((current) => ({ ...current, resignationDate: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Relieving date</label>
                                <input type="date" value={relievingLetterForm.relievingDate} onChange={(e) => setRelievingLetterForm((current) => ({ ...current, relievingDate: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Authorized Signature *</label>
                                <select 
                                    value={relievingLetterForm.signatureId} 
                                    onChange={(e) => setRelievingLetterForm((current) => ({ ...current, signatureId: e.target.value }))} 
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-neutral-800 dark:text-neutral-200 cursor-pointer"
                                    required
                                >
                                    <option value="">Select Signature</option>
                                    {signatures.map((sig) => (
                                        <option key={sig.id} value={sig.id}>
                                            {sig.name} ({sig.position})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <button type="button" disabled={relievingLetterSaving} onClick={() => setRelievingLetterOpen(false)} className="rounded-lg bg-neutral-100 px-5 py-2.5 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-200 disabled:opacity-60 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700">Cancel</button>
                            <button type="submit" disabled={relievingLetterSaving} className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--accent-primary)] px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-[var(--accent-hover)] disabled:opacity-60">
                                {relievingLetterSaving && <Loader2 size={16} className="animate-spin" />}
                                {relievingLetterSaving ? "Saving..." : selectedEmployee.relievingLetter ? "Update Letter" : "Generate Letter"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {internshipLetterOpen && selectedEmployee && (
                <div className="fixed inset-0 z-[220] flex items-center justify-center p-4 sm:p-6">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !internshipLetterSaving && setInternshipLetterOpen(false)} />
                    <form onSubmit={handleInternshipLetterSubmit} className="relative w-full max-w-xl rounded-2xl border border-neutral-200 bg-[var(--card-bg)] p-5 shadow-2xl dark:border-neutral-800 dark:bg-[#121214]">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-[var(--accent-primary)]">Employee documents</p>
                                <h3 className="text-xl font-black text-neutral-900 dark:text-neutral-50">{selectedEmployee.internshipLetter ? "Edit internship letter" : "Generate internship letter"}</h3>
                            </div>
                            <button type="button" disabled={internshipLetterSaving} onClick={() => setInternshipLetterOpen(false)} className="h-9 w-9 rounded-xl bg-neutral-100 text-neutral-500 transition hover:bg-neutral-200 disabled:opacity-60 dark:bg-neutral-800 dark:hover:bg-neutral-700" title="Close">
                                <X size={17} className="mx-auto" />
                            </button>
                        </div>
                        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Intern name</label>
                                <input value={internshipLetterForm.internName} onChange={(e) => setInternshipLetterForm((current) => ({ ...current, internName: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Internship role</label>
                                <input value={internshipLetterForm.internshipRole} onChange={(e) => setInternshipLetterForm((current) => ({ ...current, internshipRole: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Team lead name</label>
                                <input value={internshipLetterForm.teamLeadName} onChange={(e) => setInternshipLetterForm((current) => ({ ...current, teamLeadName: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Start date</label>
                                <input type="date" value={internshipLetterForm.startDate} onChange={(e) => setInternshipLetterForm((current) => ({ ...current, startDate: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">End date</label>
                                <input type="date" value={internshipLetterForm.endDate} onChange={(e) => setInternshipLetterForm((current) => ({ ...current, endDate: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Amount</label>
                                <input value={internshipLetterForm.amount} onChange={(e) => setInternshipLetterForm((current) => ({ ...current, amount: e.target.value }))} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Office mode</label>
                                <input
                                    list="internship-office-mode-options"
                                    value={internshipLetterForm.officeMode}
                                    onChange={(e) => setInternshipLetterForm((current) => ({ ...current, officeMode: e.target.value }))}
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]"
                                />
                                <datalist id="internship-office-mode-options">
                                    <option value="office" />
                                    <option value="remote" />
                                    <option value="hybrid" />
                                </datalist>
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-[11px] font-black uppercase tracking-wide text-neutral-500">Authorized Signature *</label>
                                <select
                                    value={internshipLetterForm.signatureId}
                                    onChange={(e) => setInternshipLetterForm((current) => ({ ...current, signatureId: e.target.value }))}
                                    className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-neutral-800 dark:text-neutral-200 cursor-pointer"
                                    required
                                >
                                    <option value="">Select Signature</option>
                                    {signatures.map((sig) => (
                                        <option key={sig.id} value={sig.id}>
                                            {sig.name} ({sig.position})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <button type="button" disabled={internshipLetterSaving} onClick={() => setInternshipLetterOpen(false)} className="rounded-lg bg-neutral-100 px-5 py-2.5 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-200 disabled:opacity-60 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700">Cancel</button>
                            <button type="submit" disabled={internshipLetterSaving} className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--accent-primary)] px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-[var(--accent-hover)] disabled:opacity-60">
                                {internshipLetterSaving && <Loader2 size={16} className="animate-spin" />}
                                {internshipLetterSaving ? "Saving..." : selectedEmployee.internshipLetter ? "Update Letter" : "Generate Letter"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* ================= EDIT EMPLOYEE DETAILS MODAL (EDIT POPUP) ================= */}
            {editingEmployee && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
                    {/* Backdrop */}
                    <div
                        onClick={() => {
                            setEditingEmployee(null);
                            setForm({ name: "", email: "", team: "", role: "", designation: "", employeeId: "", salary: "", hasStayAllowance: false, stayAllowanceCarryForward: false, lossOfPay: "", casualLeave: "", medicalLeave: "", compensatoryLeave: "", workLocation: "Head Office", employmentType: "Full-time", joiningDate: "", shiftTiming: "09:00 AM - 06:00 PM", workingDays: [], employeeStatus: "Active", autoSendEmail: true });
                            resetCustomInputs();
                        }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
                    />

                    {/* Modal Box */}
                    <div className="relative w-full max-w-2xl bg-[var(--card-bg)] dark:bg-[#121214] border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-2xl p-6 md:p-8 z-10 animate-fade-in max-h-[90vh] overflow-y-auto">

                        {/* Floating Close Button Outside */}
                        <button
                            type="button"
                            onClick={() => {
                                setEditingEmployee(null);
                                setForm({ name: "", email: "", team: "", role: "", designation: "", employeeId: "", salary: "", hasStayAllowance: false, stayAllowanceCarryForward: false, selfieEnabled: true, lossOfPay: "", casualLeave: "", medicalLeave: "", compensatoryLeave: "", workLocation: "Head Office", employmentType: "Full-time", joiningDate: "", shiftTiming: "09:00 AM - 06:00 PM", workingDays: [], employeeStatus: "Active", autoSendEmail: true });
                                resetCustomInputs();
                            }}
                            className="absolute -top-14 right-0 md:-right-14 w-11 h-11 rounded-full bg-[var(--card-bg)]/10 dark:bg-black/20 hover:bg-[var(--card-bg)]/20 dark:hover:bg-black/35 text-white dark:text-neutral-300 backdrop-blur-md flex items-center justify-center transition-all duration-200 border border-white/20 dark:border-neutral-800 hover:scale-110 shadow-xl cursor-pointer"
                            title="Close"
                        >
                            <X size={22} />
                        </button>

                        {/* Title Section */}
                        <div className="flex items-start justify-between pb-4 mb-6 border-b border-neutral-100 dark:border-neutral-800">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-lg bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center">
                                    <Edit2 size={20} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50 leading-tight">Edit Employee Details</h2>
                                    <p className="text-xs text-neutral-400 dark:text-neutral-500">Update workspace info, leaf allotments, and account settings.</p>
                                </div>
                            </div>
                        </div>

                        {/* Edit Form */}
                        <form onSubmit={handleUpdate} className="space-y-6">

                            {/* Account Details */}
                            <div className="space-y-4">
                                <h3 className="text-xs font-bold text-black dark:text-white uppercase tracking-widest text-opacity-80">Account Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Full Name</label>
                                        <input
                                            type="text"
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            required
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Email Address</label>
                                        <input
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            required
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Team / Department</label>
                                        <select
                                            value={form.team}
                                            onChange={handleTeamFormChange}
                                            required
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all text-neutral-700 dark:text-neutral-300"
                                        >
                                            <option value="">Select Team</option>
                                            {teams.map(t => (
                                                <option key={t._id} value={t.value}>{t.name}</option>
                                            ))}
                                            <option value={CUSTOM_OPTION_VALUE}>Other (type manually)</option>
                                        </select>
                                        {form.team === CUSTOM_OPTION_VALUE && (
                                            <input
                                                type="text"
                                                value={customTeamInput}
                                                onChange={(e) => setCustomTeamInput(e.target.value)}
                                                onBlur={handleCustomTeamBlur}
                                                placeholder="Enter team / department"
                                                required
                                                className="h-11 mt-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                            />
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Role</label>
                                        <select
                                            value={form.role}
                                            onChange={handleRoleFormChange}
                                            required
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all text-neutral-700 dark:text-neutral-300"
                                        >
                                            <option value="">Select Role</option>
                                            {roles.map((r) => (
                                                <option key={r} value={r}>
                                                    {formatRoleLabel(r)}
                                                </option>
                                            ))}
                                            <option value={CUSTOM_OPTION_VALUE}>Other (type manually)</option>
                                        </select>
                                        {form.role === CUSTOM_OPTION_VALUE && (
                                            <input
                                                type="text"
                                                value={customRoleInput}
                                                onChange={(e) => setCustomRoleInput(e.target.value)}
                                                onBlur={handleCustomRoleBlur}
                                                placeholder="Enter role"
                                                required
                                                className="h-11 mt-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                            />
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Designation</label>
                                        <input
                                            type="text"
                                            value={form.designation}
                                            onChange={(e) => setForm({ ...form, designation: e.target.value })}
                                            placeholder="e.g. Software Developer"
                                            required
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label htmlFor="edit-employee-id" className="text-xs font-bold text-neutral-500 uppercase">Employee ID</label>
                                        <input
                                            id="edit-employee-id"
                                            type="text"
                                            value={form.employeeId}
                                            onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                                            required
                                            aria-describedby="edit-employee-id-help"
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                        <p id="edit-employee-id-help" className="text-[11px] text-neutral-500">Auto-generated by default. Edit it if you need a different ID, then save your changes.</p>
                                    </div>
                                </div>
                                {String(form.role || "").toLowerCase() === "lead" && (
                                    <div className="rounded-xl border border-[var(--accent-soft)] bg-[var(--accent-light)] p-4">
                                        <label className="text-xs font-black uppercase tracking-wide text-violet-800">Managed teams</label>
                                        <p className="mt-1 text-xs text-[var(--heading-secondary)]">This lead can assign tasks and monitor employees only in these teams.</p>
                                        <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-3">
                                            {teams.map((teamOption) => {
                                                const selected = (form.managedTeams || []).includes(teamOption.value);
                                                return (
                                                    <label key={teamOption.value} className="flex cursor-pointer items-center gap-2 rounded-lg bg-[var(--card-bg)] px-3 py-2 text-sm font-semibold text-slate-700">
                                                        <input
                                                            type="checkbox"
                                                            checked={selected}
                                                            onChange={() => setForm((current) => ({
                                                                ...current,
                                                                managedTeams: selected
                                                                    ? (current.managedTeams || []).filter((team) => team !== teamOption.value)
                                                                    : [...(current.managedTeams || []), teamOption.value],
                                                            }))}
                                                        />
                                                        {teamOption.name}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Salary & Leaves */}
                            <div className="space-y-4 pt-6 border-t border-neutral-100 dark:border-neutral-800">
                                <h3 className="text-xs font-bold text-black dark:text-white uppercase tracking-widest text-opacity-80">Salary & Leaves Allocation</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Monthly Salary</label>
                                        <input
                                            type="number"
                                            value={form.salary}
                                            onChange={(e) => setForm({ ...form, salary: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">LOP Days</label>
                                        <input
                                            type="number"
                                            value={form.lossOfPay}
                                            onChange={(e) => setForm({ ...form, lossOfPay: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Casual Leaves</label>
                                        <input
                                            type="number"
                                            value={form.casualLeave}
                                            onChange={(e) => setForm({ ...form, casualLeave: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Medical Leaves</label>
                                        <input
                                            type="number"
                                            value={form.medicalLeave}
                                            onChange={(e) => setForm({ ...form, medicalLeave: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Comp Leaves</label>
                                        <input
                                            type="number"
                                            value={form.compensatoryLeave}
                                            onChange={(e) => setForm({ ...form, compensatoryLeave: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Casual Carry Forward</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.5"
                                            value={form.carriedForwardCasualLeave}
                                            onChange={(e) => setForm({ ...form, carriedForwardCasualLeave: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Medical Carry Forward</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.5"
                                            value={form.carriedForwardMedicalLeave}
                                            onChange={(e) => setForm({ ...form, carriedForwardMedicalLeave: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <label className="flex items-center gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                                        <input
                                            type="checkbox"
                                            checked={form.hasStayAllowance === true}
                                            onChange={(e) => setForm({
                                                ...form,
                                                hasStayAllowance: e.target.checked,
                                            })}
                                            className="h-4 w-4 accent-[var(--accent-primary)]"
                                        />
                                        <span>Stay allowance deduction (₹4,000)</span>
                                    </label>
                                    <label className="flex items-center gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                                        <input
                                            type="checkbox"
                                            checked={form.stayAllowanceCarryForward === true}
                                            onChange={(e) => setForm({ ...form, stayAllowanceCarryForward: e.target.checked })}
                                            className="h-4 w-4 accent-[var(--accent-primary)]"
                                        />
                                        <span>Carry forward stay allowance if no leave taken</span>
                                    </label>
                                    <label className="flex items-center gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                                        <input
                                            type="checkbox"
                                            checked={form.selfieEnabled === true}
                                            onChange={(e) => setForm({ ...form, selfieEnabled: e.target.checked })}
                                            className="h-4 w-4 accent-[var(--accent-primary)]"
                                        />
                                        <span>Enable Selfie Attendance</span>
                                    </label>
                                </div>
                            </div>

                            {/* Payroll & Bank Details */}
                            <div className="space-y-4 pt-6 border-t border-neutral-100 dark:border-neutral-800">
                                <h3 className="text-xs font-bold text-black dark:text-white uppercase tracking-widest text-opacity-80">Payroll & Bank Details</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">PAN Number</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. ABCPS1234D"
                                            value={form.panNumber}
                                            onChange={(e) => setForm({ ...form, panNumber: e.target.value.toUpperCase() })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">UAN Number</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. 101234567890"
                                            value={form.uanNumber}
                                            onChange={(e) => setForm({ ...form, uanNumber: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Bank Name</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. HDFC Bank"
                                            value={form.bankDetails?.bankName || ""}
                                            onChange={(e) => setForm({ 
                                                ...form, 
                                                bankDetails: { ...form.bankDetails, bankName: e.target.value } 
                                            })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Account Number</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. 501002345678"
                                            value={form.bankDetails?.accountNumber || ""}
                                            onChange={(e) => setForm({ 
                                                ...form, 
                                                bankDetails: { ...form.bankDetails, accountNumber: e.target.value } 
                                            })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">IFSC Code</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. HDFC0001234"
                                            value={form.bankDetails?.ifscCode || ""}
                                            onChange={(e) => setForm({ 
                                                ...form, 
                                                bankDetails: { ...form.bankDetails, ifscCode: e.target.value.toUpperCase() } 
                                            })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Account Holder Name</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Rahul Sharma"
                                            value={form.bankDetails?.accountHolderName || ""}
                                            onChange={(e) => setForm({ 
                                                ...form, 
                                                bankDetails: { ...form.bankDetails, accountHolderName: e.target.value } 
                                            })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Employment & Logistics */}
                            <div className="space-y-4 pt-6 border-t border-neutral-100 dark:border-neutral-800">
                                <h3 className="text-xs font-bold text-black dark:text-white uppercase tracking-widest text-opacity-80">Employment & Logistics</h3>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Work Location</label>
                                        <input type="text" value={form.workLocation} onChange={(e) => setForm({ ...form, workLocation: e.target.value })} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all" placeholder="e.g. Head Office" />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Employment Type</label>
                                        <select value={form.employmentType} onChange={(e) => setForm({ ...form, employmentType: e.target.value })} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all">
                                            <option value="Full-time">Full-time</option>
                                            <option value="Intern">Intern</option>
                                            <option value="Contract">Contract</option>
                                            <option value="Remote">Remote</option>
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Joining Date</label>
                                        <input type="date" value={form.joiningDate} onChange={(e) => setForm({ ...form, joiningDate: e.target.value })} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Shift Timing</label>
                                        <input type="text" value={form.shiftTiming} onChange={(e) => setForm({ ...form, shiftTiming: e.target.value })} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all" placeholder="e.g. 09:00 AM - 06:00 PM" />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Working Days</label>
                                        <input type="text" value={form.workingDays} onChange={(e) => setForm({ ...form, workingDays: e.target.value })} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all" placeholder="e.g. Monday - Friday" />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Employee Status</label>
                                        <select value={form.employeeStatus} onChange={(e) => setForm({ ...form, employeeStatus: e.target.value })} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all">
                                            <option value="Active">Active</option>
                                            <option value="Probation">Probation</option>
                                            <option value="Notice Period">Notice Period</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <div className="flex items-center justify-between">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Assigned Assets</label>
                                        <button type="button" onClick={() => setForm({ ...form, assignedAssets: [...(form.assignedAssets || []), { assetName: "", description: "", status: "Assigned" }] })} className="rounded-lg bg-[var(--accent-light)] px-3 py-1.5 text-xs font-bold text-[var(--accent-primary)] hover:bg-[var(--accent-soft)] dark:bg-[var(--accent-subtle)] dark:text-[var(--heading-secondary)]">+ Add Asset</button>
                                    </div>
                                    {(form.assignedAssets || []).length === 0 ? <p className="rounded-lg border border-dashed border-neutral-200 px-4 py-3 text-sm text-neutral-400 dark:border-neutral-800">No assets assigned yet.</p> : (form.assignedAssets || []).map((asset, index) => (
                                        <div key={index} className="grid grid-cols-1 gap-2 rounded-xl border border-neutral-200 p-3 md:grid-cols-[1fr_2fr_auto] dark:border-neutral-800">
                                            {[['assetName', 'Asset name'], ['description', 'Description']].map(([field, placeholder]) => <input key={field} value={asset[field] || ""} onChange={(e) => setForm({ ...form, assignedAssets: form.assignedAssets.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: e.target.value } : item) })} className="h-10 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm outline-none focus:border-[var(--accent-primary)]" placeholder={placeholder} />)}
                                            <button type="button" onClick={() => setForm({ ...form, assignedAssets: form.assignedAssets.filter((_, itemIndex) => itemIndex !== index) })} className="flex h-10 w-10 items-center justify-center rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30" title="Remove asset"><Trash2 size={16} /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="sticky bottom-0 -mx-6 -mb-6 flex items-center justify-end gap-3 border-t border-neutral-100 bg-[var(--card-bg)] px-6 py-4 dark:border-neutral-800 dark:bg-[#121214] md:-mx-8 md:-mb-8 md:px-8">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setEditingEmployee(null);
                                        setForm({ name: "", email: "", team: "", role: "", designation: "", employeeId: "", salary: "", hasStayAllowance: false, stayAllowanceCarryForward: false, selfieEnabled: true, lossOfPay: "", casualLeave: "", medicalLeave: "", compensatoryLeave: "", workLocation: "Head Office", employmentType: "Full-time", joiningDate: "", shiftTiming: "09:00 AM - 06:00 PM", workingDays: [], employeeStatus: "Active", autoSendEmail: true });
                                            resetCustomInputs();
                                    }}
                                    className="px-6 py-2.5 rounded-lg bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold transition-colors text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-8 py-2.5 rounded-lg bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white font-semibold transition-all shadow-lg shadow-[var(--accent-primary)]/20 text-sm"
                                >
                                    {submitting ? "Saving..." : "Save Changes"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ================= ADD NEW EMPLOYEE MODAL (ADD POPUP) ================= */}
            {showAddModal && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
                    {/* Backdrop */}
                    <div
                        onClick={() => {
                            setShowAddModal(false);
                            setForm({ name: "", email: "", team: "", role: "", designation: "", employeeId: "", salary: "", hasStayAllowance: false, stayAllowanceCarryForward: false, selfieEnabled: true, lossOfPay: "", casualLeave: "", medicalLeave: "", compensatoryLeave: "", workLocation: "Head Office", employmentType: "Full-time", joiningDate: "", shiftTiming: "09:00 AM - 06:00 PM", workingDays: [], employeeStatus: "Active", autoSendEmail: true });
                            resetCustomInputs();
                        }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
                    />

                    {/* Modal Box */}
                    <div className="relative w-full max-w-2xl bg-[var(--card-bg)] dark:bg-[#121214] border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-2xl p-6 md:p-8 z-10 animate-fade-in max-h-[90vh] overflow-y-auto">

                        {/* Floating Close Button Outside */}
                        <button
                            type="button"
                            onClick={() => {
                                setShowAddModal(false);
                                setForm({ name: "", email: "", team: "", role: "", designation: "", employeeId: "", salary: "", hasStayAllowance: false, stayAllowanceCarryForward: false, selfieEnabled: true, lossOfPay: "", casualLeave: "", medicalLeave: "", compensatoryLeave: "", workLocation: "Head Office", employmentType: "Full-time", joiningDate: "", shiftTiming: "09:00 AM - 06:00 PM", workingDays: [], employeeStatus: "Active", autoSendEmail: true });
                                resetCustomInputs();
                            }}
                            className="absolute -top-14 right-0 md:-right-14 w-11 h-11 rounded-full bg-[var(--card-bg)]/10 dark:bg-black/20 hover:bg-[var(--card-bg)]/20 dark:hover:bg-black/35 text-white dark:text-neutral-300 backdrop-blur-md flex items-center justify-center transition-all duration-200 border border-white/20 dark:border-neutral-800 hover:scale-110 shadow-xl cursor-pointer"
                            title="Close"
                        >
                            <X size={22} />
                        </button>

                        {/* Title Section */}
                        <div className="flex items-start justify-between pb-4 mb-6 border-b border-neutral-100 dark:border-neutral-800">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-lg bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center">
                                    <UserPlus size={22} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50 leading-tight">Invite New Employee</h2>
                                    <p className="text-xs text-neutral-400 dark:text-neutral-500">Configure account details and custom leaves allocation for the new member.</p>
                                </div>
                            </div>
                        </div>

                        {/* Create Form */}
                        <form onSubmit={handleCreate} className="space-y-6">

                            {modalError && (
                                <div className="p-3.5 bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 text-xs font-bold rounded-xl flex items-center gap-2 mb-4">
                                    <AlertCircle size={18} className="shrink-0 text-red-500" />
                                    <span>{modalError}</span>
                                </div>
                            )}

                            {/* Account Details */}
                            <div className="space-y-4">
                                <h3 className="text-xs font-bold text-black dark:text-white uppercase tracking-widest text-opacity-80">Account Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Full Name</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. John Doe"
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            required
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-gray-500"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Email Address</label>
                                        <input
                                            type="email"
                                            placeholder="e.g. john@aviatricks.in"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            required
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-gray-500"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Team / Department</label>
                                        <select
                                            value={form.team}
                                            onChange={handleTeamFormChange}
                                            required
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all text-neutral-700 dark:text-neutral-300"
                                        >
                                            <option value="">Select Team</option>
                                            {teams.map(t => (
                                                <option key={t._id} value={t.value}>{t.name}</option>
                                            ))}
                                            <option value={CUSTOM_OPTION_VALUE}>Other (type manually)</option>
                                        </select>
                                        {form.team === CUSTOM_OPTION_VALUE && (
                                            <input
                                                type="text"
                                                value={customTeamInput}
                                                onChange={(e) => setCustomTeamInput(e.target.value)}
                                                onBlur={handleCustomTeamBlur}
                                                placeholder="Enter team / department"
                                                required
                                                className="h-11 mt-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                            />
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Role</label>
                                        <select
                                            value={form.role}
                                            onChange={handleRoleFormChange}
                                            required
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all text-neutral-700 dark:text-neutral-300"
                                        >
                                            <option value="">Select Role</option>
                                            {roles.map((r) => (
                                                <option key={r} value={r}>
                                                    {formatRoleLabel(r)}
                                                </option>
                                            ))}
                                            <option value={CUSTOM_OPTION_VALUE}>Other (type manually)</option>
                                        </select>
                                        {form.role === CUSTOM_OPTION_VALUE && (
                                            <input
                                                type="text"
                                                value={customRoleInput}
                                                onChange={(e) => setCustomRoleInput(e.target.value)}
                                                onBlur={handleCustomRoleBlur}
                                                placeholder="Enter role"
                                                required
                                                className="h-11 mt-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                            />
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Designation</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Software Developer"
                                            value={form.designation}
                                            onChange={(e) => setForm({ ...form, designation: e.target.value })}
                                            required
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-gray-500"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label htmlFor="new-employee-id" className="text-xs font-bold text-neutral-500 uppercase">Employee ID</label>
                                        <input
                                            id="new-employee-id"
                                            type="text"
                                            placeholder="e.g. AV001"
                                            value={form.employeeId}
                                            onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                                            required
                                            aria-describedby="new-employee-id-help"
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-gray-500"
                                        />
                                        <p id="new-employee-id-help" className="text-[11px] text-neutral-500">Auto-generated by default. You can replace it before sending the invite.</p>
                                    </div>
                                </div>
                                {String(form.role || "").toLowerCase() === "lead" && (
                                    <div className="rounded-xl border border-[var(--accent-soft)] bg-[var(--accent-light)] p-4">
                                        <label className="text-xs font-black uppercase tracking-wide text-violet-800">Managed teams</label>
                                        <p className="mt-1 text-xs text-[var(--heading-secondary)]">This lead can assign tasks and monitor employees only in these teams.</p>
                                        <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-3">
                                            {teams.map((teamOption) => {
                                                const selected = (form.managedTeams || []).includes(teamOption.value);
                                                return (
                                                    <label key={teamOption.value} className="flex cursor-pointer items-center gap-2 rounded-lg bg-[var(--card-bg)] px-3 py-2 text-sm font-semibold text-slate-700">
                                                        <input
                                                            type="checkbox"
                                                            checked={selected}
                                                            onChange={() => setForm((current) => ({
                                                                ...current,
                                                                managedTeams: selected
                                                                    ? (current.managedTeams || []).filter((team) => team !== teamOption.value)
                                                                    : [...(current.managedTeams || []), teamOption.value],
                                                            }))}
                                                        />
                                                        {teamOption.name}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Salary & Leaves */}
                            <div className="space-y-4 pt-6 border-t border-neutral-100 dark:border-neutral-800">
                                <h3 className="text-xs font-bold text-black dark:text-white uppercase tracking-widest text-opacity-80">Salary & Leaves Allocation</h3>
                                <div className="rounded-xl border border-[var(--accent-soft)] bg-[var(--accent-light)] dark:bg-[var(--accent-subtle)] dark:border-[var(--border-color)] p-3.5 flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-[var(--accent-primary)] dark:text-[var(--accent-primary)] mt-0.5 shrink-0" />
                                    <p className="text-sm text-blue-800 dark:text-[var(--heading-secondary)] font-medium leading-relaxed">
                                        Note: Please do salary slip and payroll related changes in the separate <span className="font-bold">Payroll</span> page, which is under the Requests heading.
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Monthly Salary</label>
                                        <input
                                            type="number"
                                            placeholder="Amount"
                                            value={form.salary}
                                            onChange={(e) => setForm({ ...form, salary: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-gray-500"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">LOP Days</label>
                                        <input
                                            type="number"
                                            placeholder="Days"
                                            value={form.lossOfPay}
                                            onChange={(e) => setForm({ ...form, lossOfPay: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-gray-500"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Casual Leaves</label>
                                        <input
                                            type="number"
                                            placeholder="Quota"
                                            value={form.casualLeave}
                                            onChange={(e) => setForm({ ...form, casualLeave: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-gray-500"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Medical Leaves</label>
                                        <input
                                            type="number"
                                            placeholder="Quota"
                                            value={form.medicalLeave}
                                            onChange={(e) => setForm({ ...form, medicalLeave: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-gray-500"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Comp Leaves</label>
                                        <input
                                            type="number"
                                            placeholder="Quota"
                                            value={form.compensatoryLeave}
                                            onChange={(e) => setForm({ ...form, compensatoryLeave: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-gray-500"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <label className="flex items-center gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                                        <input
                                            type="checkbox"
                                            checked={form.hasStayAllowance === true}
                                            onChange={(e) => setForm({
                                                ...form,
                                                hasStayAllowance: e.target.checked,
                                            })}
                                            className="h-4 w-4 accent-[var(--accent-primary)]"
                                        />
                                        <span>Stay allowance deduction (₹4,000)</span>
                                    </label>
                                    <label className="flex items-center gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                                        <input
                                            type="checkbox"
                                            checked={form.stayAllowanceCarryForward === true}
                                            onChange={(e) => setForm({ ...form, stayAllowanceCarryForward: e.target.checked })}
                                            className="h-4 w-4 accent-[var(--accent-primary)]"
                                        />
                                        <span>Carry forward if no leave taken</span>
                                    </label>
                                    <label className="flex items-center gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                                        <input
                                            type="checkbox"
                                            checked={form.selfieEnabled === true}
                                            onChange={(e) => setForm({ ...form, selfieEnabled: e.target.checked })}
                                            className="h-4 w-4 accent-[var(--accent-primary)]"
                                        />
                                        <span>Enable Selfie Attendance</span>
                                    </label>
                                </div>
                            </div>

                            {/* Payroll & Bank Details */}
                            <div className="space-y-4 pt-6 border-t border-neutral-100 dark:border-neutral-800">
                                <h3 className="text-xs font-bold text-black dark:text-white uppercase tracking-widest text-opacity-80">Payroll & Bank Details</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">PAN Number</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. ABCPS1234D"
                                            value={form.panNumber}
                                            onChange={(e) => setForm({ ...form, panNumber: e.target.value.toUpperCase() })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">UAN Number</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. 101234567890"
                                            value={form.uanNumber}
                                            onChange={(e) => setForm({ ...form, uanNumber: e.target.value })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Bank Name</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. HDFC Bank"
                                            value={form.bankDetails?.bankName || ""}
                                            onChange={(e) => setForm({ 
                                                ...form, 
                                                bankDetails: { ...form.bankDetails, bankName: e.target.value } 
                                            })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Account Number</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. 501002345678"
                                            value={form.bankDetails?.accountNumber || ""}
                                            onChange={(e) => setForm({ 
                                                ...form, 
                                                bankDetails: { ...form.bankDetails, accountNumber: e.target.value } 
                                            })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">IFSC Code</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. HDFC0001234"
                                            value={form.bankDetails?.ifscCode || ""}
                                            onChange={(e) => setForm({ 
                                                ...form, 
                                                bankDetails: { ...form.bankDetails, ifscCode: e.target.value.toUpperCase() } 
                                            })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Account Holder Name</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Rahul Sharma"
                                            value={form.bankDetails?.accountHolderName || ""}
                                            onChange={(e) => setForm({ 
                                                ...form, 
                                                bankDetails: { ...form.bankDetails, accountHolderName: e.target.value } 
                                            })}
                                            className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Employment & Logistics */}
                            <div className="space-y-4 pt-6 border-t border-neutral-100 dark:border-neutral-800">
                                <h3 className="text-xs font-bold text-black dark:text-white uppercase tracking-widest text-opacity-80">Employment & Logistics</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Work Location</label>
                                        <input type="text" value={form.workLocation} onChange={(e) => setForm({ ...form, workLocation: e.target.value })} placeholder="e.g. Head Office" className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all placeholder:font-normal placeholder:text-gray-400" />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Employment Type</label>
                                        <select value={form.employmentType} onChange={(e) => setForm({ ...form, employmentType: e.target.value })} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all">
                                            <option value="Full-time">Full-time</option>
                                            <option value="Intern">Intern</option>
                                            <option value="Contract">Contract</option>
                                            <option value="Remote">Remote</option>
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Joining Date</label>
                                        <input type="date" value={form.joiningDate} onChange={(e) => setForm({ ...form, joiningDate: e.target.value })} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Shift Timing</label>
                                        <input type="text" value={form.shiftTiming} onChange={(e) => setForm({ ...form, shiftTiming: e.target.value })} placeholder="e.g. 09:00 AM - 06:00 PM" className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all placeholder:font-normal placeholder:text-gray-400" />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Working Days</label>
                                        <input type="text" value={form.workingDays} onChange={(e) => setForm({ ...form, workingDays: e.target.value })} placeholder="e.g. Monday - Friday" className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all placeholder:font-normal placeholder:text-gray-400" />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-neutral-500 uppercase">Employee Status</label>
                                        <select value={form.employeeStatus} onChange={(e) => setForm({ ...form, employeeStatus: e.target.value })} className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all">
                                            <option value="Active">Active</option>
                                            <option value="Probation">Probation</option>
                                            <option value="Notice Period">Notice Period</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Auto-send welcome email toggle */}
                                <div className="flex items-center gap-3 pt-2">
                                    <input
                                        type="checkbox"
                                        id="autoSendEmail"
                                        checked={form.autoSendEmail !== false}
                                        onChange={(e) => setForm({ ...form, autoSendEmail: e.target.checked })}
                                        className="w-4 h-4 accent-[var(--accent-primary)] cursor-pointer"
                                    />
                                    <label htmlFor="autoSendEmail" className="text-sm font-medium text-neutral-600 dark:text-neutral-400 cursor-pointer select-none">
                                        Auto-send welcome email with login credentials
                                    </label>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-end gap-3 pt-6 border-t border-neutral-100 dark:border-neutral-800">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddModal(false);
                                        setForm({ name: "", email: "", team: "", role: "", designation: "", employeeId: "", salary: "", hasStayAllowance: false, stayAllowanceCarryForward: false, selfieEnabled: true, lossOfPay: "", casualLeave: "", medicalLeave: "", compensatoryLeave: "", workLocation: "Head Office", employmentType: "Full-time", joiningDate: "", shiftTiming: "09:00 AM - 06:00 PM", workingDays: [], employeeStatus: "Active", autoSendEmail: true });
                                        resetCustomInputs();
                                    }}
                                    className="px-6 py-2.5 rounded-lg bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold transition-colors text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-8 py-2.5 rounded-lg bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white font-semibold transition-all shadow-lg shadow-[var(--accent-primary)]/20 text-sm active:scale-95"
                                >
                                    {submitting ? "Inviting..." : "Send Invite"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const formatWorkingDays = (workingDaysInput) => {
    if (!workingDaysInput) return "";

    let daysArray = [];
    if (Array.isArray(workingDaysInput)) {
        daysArray = workingDaysInput.map(d => String(d).trim()).filter(Boolean);
    } else if (typeof workingDaysInput === "string") {
        const str = workingDaysInput.trim();
        if (!str) return "";
        if (str.includes("-")) return str;
        daysArray = str.split(",").map(d => d.trim()).filter(Boolean);
    } else {
        daysArray = [String(workingDaysInput).trim()].filter(Boolean);
    }

    if (daysArray.length === 0) return "";
    if (daysArray.length === 1) {
        const d = daysArray[0];
        return d.charAt(0).toUpperCase() + d.slice(1).toLowerCase();
    }

    const weekdayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const weekdayShortMap = {
        mon: "Monday", monday: "Monday",
        tue: "Tuesday", tues: "Tuesday", tuesday: "Tuesday",
        wed: "Wednesday", wednesday: "Wednesday",
        thu: "Thursday", thur: "Thursday", thurs: "Thursday", thursday: "Thursday",
        fri: "Friday", friday: "Friday",
        sat: "Saturday", saturday: "Saturday",
        sun: "Sunday", sunday: "Sunday"
    };

    const normalizedDays = daysArray.map(d => {
        const lower = d.toLowerCase();
        if (weekdayShortMap[lower]) return weekdayShortMap[lower];
        return d.charAt(0).toUpperCase() + d.slice(1).toLowerCase();
    });

    const indices = normalizedDays.map(d => weekdayOrder.indexOf(d)).filter(idx => idx !== -1);
    if (indices.length <= 1) return normalizedDays.join(", ");

    const sortedIndices = [...indices].sort((a, b) => a - b);
    let isConsecutive = true;
    for (let i = 1; i < sortedIndices.length; i++) {
        if (sortedIndices[i] !== sortedIndices[i - 1] + 1) {
            isConsecutive = false;
            break;
        }
    }

    if (isConsecutive && sortedIndices.length > 1) {
        const startDay = weekdayOrder[sortedIndices[0]];
        const endDay = weekdayOrder[sortedIndices[sortedIndices.length - 1]];
        return `${startDay} - ${endDay}`;
    }

    return normalizedDays.join(", ");
};

export default Employees;


